/**
 * @fileoverview Docs Settings — Precedent Templates
 *
 * Purpose: Upload and manage precedent document templates for auto-population.
 * Gallery layout with categorical grouping. Preview includes branding header/footer.
 * URL: /dashboard/settings/docs
 */

import DOMPurify from "dompurify";
import { createFileRoute } from "@tanstack/react-router";
import { useUploadFiles } from "@xixixao/uploadstuff/react";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { useConvex } from "convex/react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { api } from "~/convex/_generated/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useActiveOrg } from "@/utils/org-context";
import {
  Spinner,
  Badge,
  Button,
  Checkbox,
  Input,
  Switch,
  buttonVariants,
} from "@/ui/primitives";
import type { BadgeColor } from "@/ui/primitives/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/ui/composed/overlays/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import {
  Upload,
  FileText,
  Loader2,
  Eye,
  Trash2,
  FileSpreadsheet,
  Pencil,
  Plus,
  Check,
  GripVertical,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Lock,
  Paperclip,
  Search,
  X as XIcon,
  Filter,
  CheckSquare,
  Tags,
  FolderInput,
  LayoutGrid,
  List,
  Info,
  Columns3,
  StickyNote,
  Copy,
  Settings as SettingsIcon,
  Download,
  Library,
  type LucideIcon,
} from "lucide-react";
import { usePostHog } from "@/utils/posthog";
import { cn } from "@/utils/misc";
import type { FunctionReturnType } from "convex/server";
import type { Id } from "~/convex/_generated/dataModel";
import type { ResolvedLayer } from "@/utils/branding/resolveVariants";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
  GALLAGHER_INSURANCE_FIELDS,
  type PrecedentField,
  ALL_TAG_OPTIONS,
  APPOINTMENT_TYPE_TAGS,
  TAG_ALL,
  TAG_GROUP_PREFIX,
  JOB_STATUS_TAGS,
  jobStatusTag,
} from "@/lib/precedent-templates";
import {
  ROLE_META,
  ROLE_ORDER,
  contactRoleTag,
  parseContactRoleTag,
  FIRM_CATEGORY_META,
  FIRM_CATEGORY_ORDER,
  firmContactTag,
  parseFirmContactTag,
  firmContactCustomCategoryTag,
  firmContactStateTag,
  jobContactCustomRoleTag,
  parseFirmContactCustomCategoryTag,
  parseFirmContactStateTag,
  parseJobContactCustomRoleTag,
  isJobContactExternalAdminTag,
  isFirmContactJobStateTag,
  FIRM_CONTACT_JOB_STATE_TAG,
  JOB_CONTACT_EXTERNAL_ADMIN_TAG,
  type ContactRole,
  type FirmContactCategory,
} from "@/lib/contact-roles";
import { roleIconComponent } from "@/components/settings/tasks/role-icons";
import { AU_STATES } from "@/utils/format/auStates";
import {
  Tag as TagIcon,
  MapPin,
  MapPinned,
  Globe,
  Landmark,
} from "lucide-react";
import type { WorkflowRoleIcon } from "@cvx/tasks/workflowRoles/schema";
import type { JobStatus } from "@cvx/jobs/schema";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Label } from "@/ui/primitives/label";
import { TemplateEditor } from "@/ui/composed/editor/template-editor";
import {
  prepareHtmlForEditor,
  buildFieldsLabelMap,
  resolveVariantBlocks,
  fillVariantSnippetPlaceholders,
  decorateFieldChipModifiers,
} from "@/ui/composed/editor/html-utils";
import { VariantPicker } from "@/components/precedents/VariantPicker";
import { extractVariantGroupSlugs } from "@/components/precedents/VariantPicker.helpers";
import { toast } from "sonner";
import {
  resolveDocumentPadding,
  resolveVariantFragments,
  type PageRule,
  type PaddingSource,
  type VariantFragments,
  resolvePagePadding,
  type PageOverrideSource,
} from "@/utils/branding/resolve-letterhead";
import {
  buildBundleFragments,
  buildVariantFragments,
  type LayeredVariantLike,
  type LetterheadAssetLike,
} from "@/utils/branding/letterheadFragments";
import {
  resolvePageNumbering,
  type PageNumberingConfig,
} from "@/utils/branding/page-numbering";
import {
  type LetterParts,
  variantPaddingSources,
  pagePaddingInputs,
  getVariantsForParts,
} from "@/utils/export/exportHtmlDocument";
import { zoneLocksForPage } from "@/utils/branding/pageFit";
import {
  buildSampleValueResolver,
  substituteSampleValuesInHtml,
} from "@/utils/precedents/sampleValues";
import { PageMarginsPopover } from "@/components/branding/PageMarginsPopover";
import { PaginatedLetterPreview } from "@/components/templates/PaginatedLetterPreview";
import { LetterheadOverridePicker } from "@/components/branding/LetterheadOverridePicker";
import {
  PdfFieldEditor,
  type FieldPosition,
} from "@/components/settings/precedents/PdfFieldEditor";
import type {
  FieldCondition,
  FieldConditionMap,
} from "~/convex/jobs/precedents/fieldConditions";
import type { DateCalc } from "~/convex/settings/precedents/dateCalc";
import { SourceDocPreview } from "@/components/settings/precedents/SourceDocPreview";
import { downloadSourceDoc } from "@/components/settings/precedents/SourceDocPreview.helpers";
import { BulkUploadDialog } from "@/components/settings/precedents/BulkUploadDialog";
import { TemplateNotesFeed } from "@/components/settings/precedents/TemplateNotesFeed";
import { AritaLibraryTab } from "./-ui.arita-library";
import type {
  CustomFieldDef,
  FirmContactCustomFieldOption,
} from "@/components/settings/precedents/CanonicalKeyPicker";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { type FieldType } from "~/convex/settings/precedents/canonicalFields";
import { EXTERNAL_ADMIN_ROLE_LABEL } from "~/convex/jobs/precedents/contactFields";
import {
  DEFAULT_GALLAGHER_FIELD_CONFIG,
  GALLAGHER_SOURCE_FIELD_OPTIONS,
  upgradeLegacyGallagherSource,
  type GallagherFieldConfig,
  type GallagherFieldSourceType,
} from "~/convex/jobs/precedents/gallagherFieldConfig";
import { extractErrorMessage } from "@/utils/errors";
import {
  annotateFieldSources,
  classifyFieldSource,
  type FieldSource,
} from "@/utils/precedents/classifyFieldSource";
import { SourceLegend } from "@/components/precedents/SourceLegend";
import type { DocumentCategory } from "@cvx/jobs/gatherInformation/documents/schema";
import type {
  AttachmentRequirement,
  PrecedentAttachmentKey,
  PrecedentAttachmentSourceKind,
} from "@/lib/precedent-attachments";
import {
  normaliseAttachmentRequirement,
  prepareAttachmentRequirementsForSave,
  slugifyAttachmentName,
} from "@/lib/precedent-attachments";
import { useAutosave, AutosaveStatus } from "@/utils/forms";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/docs",
)({
  component: DocsSettings,
  beforeLoad: () => ({
    title: "Docs",
    headerTitle: "Settings",
  }),
});

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

// ---------------------------------------------------------------------------
// Local row types for the Convex query results used across this page,
// derived from the queries' inferred return types so they track the backend
// without manual sync.
// ---------------------------------------------------------------------------

/** `settings.precedents.queries.listByCategory` row: the stored template doc
 *  plus resolved storage URLs and the master-library link status. */
type PrecedentTemplateRow = FunctionReturnType<
  typeof api.settings.precedents.queries.listByCategory
>[number];

/** `settings.precedents.categories.listCategories` row. */
type TemplateCategoryRow = FunctionReturnType<
  typeof api.settings.precedents.categories.listCategories
>[number];

/** `settings.branding.assetQueries.listAssetsForOrg` row: asset doc with
 *  resolved image / source-document URLs. */
type LetterheadAssetRow = FunctionReturnType<
  typeof api.settings.branding.assetQueries.listAssetsForOrg
>[number];

/** Nullable per-edge crop bag as resolved by the branding queries. */
type PreviewSideCrop = {
  top?: number | null;
  bottom?: number | null;
  left?: number | null;
  right?: number | null;
} | null;

/** `settings.branding.queries.getBrandingProfile` result (non-null half):
 *  the brandingProfiles doc plus server-resolved image/document URLs. */
type BrandingProfileRow = NonNullable<
  FunctionReturnType<typeof api.settings.branding.queries.getBrandingProfile>
>;

/**
 * Branding profile shape consumed by the preview surfaces on this page:
 * the `getBrandingProfile` result (brandingProfiles doc + resolved URLs),
 * optionally overlaid with the firm-letterhead zone fields patched on by
 * `previewBrandingProfile` in {@link DocsSettingsInner}.
 *
 * The query-sourced part is derived from the backend; the intersection
 * below adds only the fields the client patch introduces. The page rules
 * and image scales exist on the doc too, but the patch overwrites them with
 * values resolved from letterhead assets/variants (page rules whose union
 * includes "none", scales that may be null), so they're re-declared here
 * with the wider client-side types.
 */
type PreviewBrandingProfile = Omit<
  BrandingProfileRow,
  | "headerPageRule"
  | "footerPageRule"
  | "leftSidePageRule"
  | "rightSidePageRule"
  | "headerImageScale"
  | "footerImageScale"
  | "leftSideImageScale"
  | "rightSideImageScale"
> & {
  firmLetterheadFragments?: VariantFragments[] | null;
  headerImageScale?: number | null;
  footerImageScale?: number | null;
  leftSideImageScale?: number | null;
  rightSideImageScale?: number | null;
  headerImageCropTop?: number | null;
  headerImageCropBottom?: number | null;
  headerImageCropLeft?: number | null;
  headerImageCropRight?: number | null;
  footerImageCropTop?: number | null;
  footerImageCropBottom?: number | null;
  footerImageCropLeft?: number | null;
  footerImageCropRight?: number | null;
  headerPageRule?: PageRule | null;
  footerPageRule?: PageRule | null;
  leftSidePageRule?: PageRule | null;
  rightSidePageRule?: PageRule | null;
  leftSideFit?: "fullHeight" | "topBlock" | null;
  rightSideFit?: "fullHeight" | "topBlock" | null;
  leftSideTopBlockHeightPct?: number | null;
  rightSideTopBlockHeightPct?: number | null;
  leftSideCrop?: PreviewSideCrop;
  rightSideCrop?: PreviewSideCrop;
};

/** Card descriptor synthesised from a template row (see CategorySection). */
type TemplateTypeMeta = {
  key: string;
  name: string;
  shortName: string;
  description: string;
  category: string;
  icon: LucideIcon;
  thirdPartyForm?: boolean;
  hardcodedFields?: PrecedentField[];
};

/**
 * Inline banner shown when an org `member` visits the docs settings page.
 * Communicates the read-only state without removing read access (browse,
 * preview, fields list all stay visible).
 */
function ReadOnlyBanner() {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
      <Lock className="mt-0.5 size-4 shrink-0" />
      <div className="text-sm">
        <p className="font-medium">Read-only view</p>
        <p className="mt-0.5 text-xs opacity-80">
          You need owner or admin access to edit the precedent library. Ask an
          admin in your organisation to make changes.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

/**
 * Firm-facing wrapper: resolves the active org + role, then renders the shared
 * editor. The admin master-library page renders {@link DocsSettingsInner}
 * directly with the master org, so the heavy precedent tooling is shared and
 * future improvements here flow through to both surfaces.
 */
export default function DocsSettings() {
  const { orgId, activeOrg } = useActiveOrg();
  const canEdit = activeOrg?.role === "owner" || activeOrg?.role === "admin";
  return <DocsSettingsInner orgId={orgId} canEdit={canEdit} mode="firm" />;
}

/**
 * Shared precedent-library editor. Parameterised by org so it can drive both
 * the firm settings page (`mode="firm"`) and the admin ARITA master library
 * (`mode="master"`). In master mode the firm-only chrome (ARITA Library tab,
 * letterhead-driven previews) is hidden.
 */
export function DocsSettingsInner({
  orgId,
  canEdit,
  mode = "firm",
}: {
  orgId: Id<"organizations"> | null;
  canEdit: boolean;
  mode?: "firm" | "master";
}) {
  const typedOrgId = orgId as Id<"organizations">;

  const { mutateAsync: ensureDefaults } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.categories.ensureDefaults,
    ),
  });

  const [seeded, setSeeded] = useState(false);
  const seedRef = useRef(false);

  // Idempotent seed on first load. Skipped for the ARITA master library — it
  // shouldn't carry the firm default categories/Gallagher template.
  const doSeed = useCallback(async () => {
    if (seedRef.current || !orgId) return;
    seedRef.current = true;
    if (mode === "master") {
      setSeeded(true);
      return;
    }
    try {
      await ensureDefaults({ orgId: typedOrgId });
    } catch {
      seedRef.current = false;
    }
    setSeeded(true);
  }, [orgId, typedOrgId, ensureDefaults, mode]);

  useState(() => {
    doSeed();
  });

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    ...convexQuery(api.settings.precedents.categories.listCategories, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId && seeded,
  });

  const { data: templates, isLoading: templatesLoading } = useQuery({
    ...convexQuery(api.settings.precedents.queries.listByCategory, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  const { data: brandingProfile } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  // The template preview needs to show "what would this look like with the
  // firm letterhead applied." The legacy `brandingProfile.*ImageUrl`
  // fields used to carry the single org letterhead, but now they're
  // stale (the user might have multiple bundles, with a firm default
  // pointing at one of them). Resolve from the asset library instead.
  const { data: letterheadAssetsRaw } = useQuery({
    ...convexQuery(api.settings.branding.assetQueries.listAssetsForOrg, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  // Needed by the preview when the firm default is a letterhead VARIANT
  // (MOR-953) rather than a bundle.
  const { data: letterheadVariantsForPreview } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  // Top-level surface toggle: the firm's own library vs the ARITA master
  // library browser. Only firms granted access (and never the admin master
  // editor itself) see the second tab.
  const { data: hasMasterAccess } = useQuery({
    ...convexQuery(api.settings.precedents.master.firmHasMasterAccess, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId && mode === "firm",
  });
  const showAritaTab = mode === "firm" && hasMasterAccess === true;
  const [activeTopTab, setActiveTopTab] = useState<"library" | "arita">(
    "library",
  );

  // Build a synthesised "firm letterhead" for the template previews
  // below. Picks the firm-default bundle (or the only bundle when there
  // is one) and surfaces its 4 zone images + page rules in the same
  // shape the legacy preview consumed.
  //
  // We then patch the brandingProfile object so existing preview code
  // (which reads `brandingProfile.headerImageUrl` etc. directly) picks
  // up the bundle values without any further plumbing. This avoids
  // editing 30+ call sites and keeps the previews in step with what the
  // actual export pipeline does for jobs.
  const firmLetterhead = useMemo(() => {
    // Firm default VARIANT (MOR-953): synthesise the legacy single-image
    // preview shape from the variant's page-1 layers. Previews render page
    // 1, so the first layer that shows on page 1 wins per zone.
    const variantDefaultId =
      brandingProfile?.firmDefaultLetterheadVariantId ?? null;
    const variantDefault = variantDefaultId
      ? (letterheadVariantsForPreview ?? []).find(
          (v) => v._id === variantDefaultId,
        )
      : null;
    if (variantDefault) {
      const showsOnPage1 = (rule: string | null | undefined) =>
        !rule || rule === "all" || rule === "firstOnly" || rule === "oddOnly";
      type PreviewLayer = NonNullable<
        (typeof variantDefault)["resolvedHeaderLayers"]
      >[number];
      const layerFor = (layers: PreviewLayer[] | null | undefined) =>
        (layers ?? []).find((l) => l.imageUrl && showsOnPage1(l.pageRule)) ??
        null;
      const h = layerFor(variantDefault.resolvedHeaderLayers);
      const f = layerFor(variantDefault.resolvedFooterLayers);
      const l = layerFor(variantDefault.resolvedLeftSideLayers);
      const r = layerFor(variantDefault.resolvedRightSideLayers);
      return {
        bundleName: variantDefault.name,
        headerImageUrl:
          h?.imageUrl ?? variantDefault.headerImageUrl ?? undefined,
        footerImageUrl:
          f?.imageUrl ?? variantDefault.footerImageUrl ?? undefined,
        leftSideImageUrl:
          l?.imageUrl ?? variantDefault.leftSideImageUrl ?? undefined,
        rightSideImageUrl:
          r?.imageUrl ?? variantDefault.rightSideImageUrl ?? undefined,
        headerImageScale: h?.imageScale,
        footerImageScale: f?.imageScale,
        leftSideImageScale: l?.imageScale,
        rightSideImageScale: r?.imageScale,
        headerImageCropTop: h?.imageCropTop,
        headerImageCropBottom: h?.imageCropBottom,
        headerImageCropLeft: h?.imageCropLeft,
        headerImageCropRight: h?.imageCropRight,
        footerImageCropTop: f?.imageCropTop,
        footerImageCropBottom: f?.imageCropBottom,
        footerImageCropLeft: f?.imageCropLeft,
        footerImageCropRight: f?.imageCropRight,
        headerPageRule: h?.pageRule ?? "all",
        footerPageRule: f?.pageRule ?? "all",
        leftSidePageRule: l?.pageRule ?? "all",
        rightSidePageRule: r?.pageRule ?? "all",
        leftSideFit: l?.sideFit ?? variantDefault.leftSideFit ?? null,
        rightSideFit: r?.sideFit ?? variantDefault.rightSideFit ?? null,
        leftSideTopBlockHeightPct:
          l?.topBlockHeightPct ??
          variantDefault.leftSideTopBlockHeightPct ??
          null,
        rightSideTopBlockHeightPct:
          r?.topBlockHeightPct ??
          variantDefault.rightSideTopBlockHeightPct ??
          null,
        leftSideCrop: l
          ? {
              top: l.imageCropTop ?? null,
              bottom: l.imageCropBottom ?? null,
              left: l.imageCropLeft ?? null,
              right: l.imageCropRight ?? null,
            }
          : (variantDefault.leftSideCrop ?? null),
        rightSideCrop: r
          ? {
              top: r.imageCropTop ?? null,
              bottom: r.imageCropBottom ?? null,
              left: r.imageCropLeft ?? null,
              right: r.imageCropRight ?? null,
            }
          : (variantDefault.rightSideCrop ?? null),
      };
    }

    if (!letterheadAssetsRaw) return null;
    const assets: LetterheadAssetRow[] = letterheadAssetsRaw;
    const bundleNames = Array.from(
      new Set(
        assets
          .map((a) => a.bundleName ?? a.sourceDocumentName ?? null)
          .filter((b): b is string => !!b),
      ),
    );
    const firmDefault =
      brandingProfile?.firmDefaultLetterheadBundleName ?? null;
    const chosenBundle =
      (firmDefault && bundleNames.includes(firmDefault) && firmDefault) ||
      (bundleNames.length === 1 ? bundleNames[0] : null);
    if (!chosenBundle) return null;
    const bundleAssets = assets.filter(
      (a) => (a.bundleName ?? a.sourceDocumentName ?? null) === chosenBundle,
    );
    // A bundle can hold more than one asset per zone (e.g. a "first page
    // only" header paired with an "all except first page" one, MOR-953).
    // This legacy preview shape carries a single image per zone, so prefer
    // the asset that actually renders on page 1 — previews show page 1 —
    // rather than whichever sorts first alphabetically.
    const showsOnFirstPage = (rule: string | null | undefined) =>
      !rule || rule === "all" || rule === "firstOnly" || rule === "oddOnly";
    const findZone = (zone: string) =>
      bundleAssets.find(
        (a) => a.zoneKind === zone && showsOnFirstPage(a.defaultPageRule),
      ) ?? bundleAssets.find((a) => a.zoneKind === zone);
    const header = findZone("header");
    const footer = findZone("footer");
    const left = findZone("leftSide");
    const right = findZone("rightSide");
    return {
      bundleName: chosenBundle,
      headerImageUrl: header?.imageUrl ?? undefined,
      footerImageUrl: footer?.imageUrl ?? undefined,
      leftSideImageUrl: left?.imageUrl ?? undefined,
      rightSideImageUrl: right?.imageUrl ?? undefined,
      headerImageScale: header?.imageScale,
      footerImageScale: footer?.imageScale,
      leftSideImageScale: left?.imageScale,
      rightSideImageScale: right?.imageScale,
      headerImageCropTop: header?.imageCropTop,
      headerImageCropBottom: header?.imageCropBottom,
      headerImageCropLeft: header?.imageCropLeft,
      headerImageCropRight: header?.imageCropRight,
      footerImageCropTop: footer?.imageCropTop,
      footerImageCropBottom: footer?.imageCropBottom,
      footerImageCropLeft: footer?.imageCropLeft,
      footerImageCropRight: footer?.imageCropRight,
      headerPageRule: header?.defaultPageRule ?? "all",
      footerPageRule: footer?.defaultPageRule ?? "all",
      leftSidePageRule: left?.defaultPageRule ?? "all",
      rightSidePageRule: right?.defaultPageRule ?? "all",
      leftSideFit: left?.sideFit ?? null,
      rightSideFit: right?.sideFit ?? null,
      leftSideTopBlockHeightPct: left?.topBlockHeightPct ?? null,
      rightSideTopBlockHeightPct: right?.topBlockHeightPct ?? null,
      leftSideCrop: left
        ? {
            top: left.imageCropTop ?? null,
            bottom: left.imageCropBottom ?? null,
            left: left.imageCropLeft ?? null,
            right: left.imageCropRight ?? null,
          }
        : null,
      rightSideCrop: right
        ? {
            top: right.imageCropTop ?? null,
            bottom: right.imageCropBottom ?? null,
            left: right.imageCropLeft ?? null,
            right: right.imageCropRight ?? null,
          }
        : null,
    };
  }, [letterheadAssetsRaw, letterheadVariantsForPreview, brandingProfile]);

  // Overlay the firm letterhead onto the brandingProfile so downstream
  // preview components (which read brandingProfile.headerImageUrl etc.)
  // pick up the bundle-resolved values instead of the stale legacy
  // single-letterhead fields.
  // MOR-953: the flat `firmLetterhead` shape above carries ONE image per
  // zone, which cannot express a first-page + subsequent-pages pair. Build
  // the full ordered fragment list alongside it so the template preview
  // (and its sample PDF download) render every layer, exactly as the
  // day-one document surface does.
  const firmLetterheadFragments = useMemo<VariantFragments[]>(() => {
    const assets = (letterheadAssetsRaw ?? []) as LetterheadAssetLike[];
    const variants = (letterheadVariantsForPreview ??
      []) as LayeredVariantLike[];

    // Same precedence as `firmLetterhead` above: the firm default VARIANT
    // first (only it can express a first-page + subsequent-pages pair),
    // then the firm default bundle, then a lone bundle.
    const variantId = brandingProfile?.firmDefaultLetterheadVariantId ?? null;
    const variant = variantId
      ? variants.find((v) => v._id === variantId)
      : null;
    if (variant) return buildVariantFragments(variant);

    const bundles = Array.from(
      new Set(
        assets
          .map((a) => a.bundleName ?? a.sourceDocumentName ?? null)
          .filter((b): b is string => !!b),
      ),
    );
    const firmDefault =
      brandingProfile?.firmDefaultLetterheadBundleName ?? null;
    const chosenBundle =
      (firmDefault && bundles.includes(firmDefault) && firmDefault) ||
      (bundles.length === 1 ? bundles[0] : null);
    return chosenBundle ? buildBundleFragments(chosenBundle, assets) : [];
  }, [letterheadAssetsRaw, letterheadVariantsForPreview, brandingProfile]);

  const previewBrandingProfile = useMemo(() => {
    if (!brandingProfile) return brandingProfile;
    if (!firmLetterhead) {
      // No firm default + no single-bundle fallback → blank out the
      // legacy letterhead fields so the preview doesn't show stale data.
      return {
        ...brandingProfile,
        firmLetterheadFragments,
        headerImageUrl: null,
        footerImageUrl: null,
        leftSideImageUrl: null,
        rightSideImageUrl: null,
        headerHtml: null,
        footerHtml: null,
        leftSideFit: null,
        rightSideFit: null,
        leftSideTopBlockHeightPct: null,
        rightSideTopBlockHeightPct: null,
        leftSideCrop: null,
        rightSideCrop: null,
      };
    }
    return {
      ...brandingProfile,
      firmLetterheadFragments,
      headerImageUrl: firmLetterhead.headerImageUrl ?? null,
      footerImageUrl: firmLetterhead.footerImageUrl ?? null,
      leftSideImageUrl: firmLetterhead.leftSideImageUrl ?? null,
      rightSideImageUrl: firmLetterhead.rightSideImageUrl ?? null,
      headerImageScale: firmLetterhead.headerImageScale,
      footerImageScale: firmLetterhead.footerImageScale,
      leftSideImageScale: firmLetterhead.leftSideImageScale,
      rightSideImageScale: firmLetterhead.rightSideImageScale,
      headerImageCropTop: firmLetterhead.headerImageCropTop,
      headerImageCropBottom: firmLetterhead.headerImageCropBottom,
      headerImageCropLeft: firmLetterhead.headerImageCropLeft,
      headerImageCropRight: firmLetterhead.headerImageCropRight,
      footerImageCropTop: firmLetterhead.footerImageCropTop,
      footerImageCropBottom: firmLetterhead.footerImageCropBottom,
      footerImageCropLeft: firmLetterhead.footerImageCropLeft,
      footerImageCropRight: firmLetterhead.footerImageCropRight,
      headerPageRule: firmLetterhead.headerPageRule,
      footerPageRule: firmLetterhead.footerPageRule,
      leftSidePageRule: firmLetterhead.leftSidePageRule,
      rightSidePageRule: firmLetterhead.rightSidePageRule,
      leftSideFit: firmLetterhead.leftSideFit,
      rightSideFit: firmLetterhead.rightSideFit,
      leftSideTopBlockHeightPct: firmLetterhead.leftSideTopBlockHeightPct,
      rightSideTopBlockHeightPct: firmLetterhead.rightSideTopBlockHeightPct,
      leftSideCrop: firmLetterhead.leftSideCrop,
      rightSideCrop: firmLetterhead.rightSideCrop,
      // Wipe legacy HTML — the asset library renders images only.
      headerHtml: null,
      footerHtml: null,
    };
  }, [brandingProfile, firmLetterhead, firmLetterheadFragments]);

  // -------------------------------------------------------------------------
  // View mode (grid vs table)
  // -------------------------------------------------------------------------
  const [viewMode, setViewMode] = useState<"grid" | "table">(() => {
    if (typeof window !== "undefined") {
      return (
        (localStorage.getItem("precedent-view-mode") as "grid" | "table") ??
        "grid"
      );
    }
    return "grid";
  });

  const toggleViewMode = useCallback((mode: "grid" | "table") => {
    setViewMode(mode);
    localStorage.setItem("precedent-view-mode", mode);
  }, []);

  // -------------------------------------------------------------------------
  // Search + filters across the precedent library
  // -------------------------------------------------------------------------
  const [searchText, setSearchText] = useState("");
  const [appointmentFilters, setAppointmentFilters] = useState<Set<string>>(
    () => new Set(),
  );
  const [statusFilters, setStatusFilters] = useState<Set<JobStatus>>(
    () => new Set(),
  );
  // Map of customTagGroupSlug -> set of selected option values
  const [customTagFilters, setCustomTagFilters] = useState<
    Map<string, Set<string>>
  >(() => new Map());
  const [contactRoleFilters, setContactRoleFilters] = useState<
    Set<ContactRole>
  >(() => new Set());
  // Letterhead filter values:
  //   "firm-default"          → templates with no override
  //   "none"                  → templates with letterheadOverride = none
  //   "bundle:<bundleName>"   → templates with that letterhead bundle override
  //   "variant:<variantId>"   → templates with that variant override
  const [letterheadFilters, setLetterheadFilters] = useState<Set<string>>(
    () => new Set(),
  );
  // Variant filter — set of variant group slugs. A template matches if its
  // templateHtml references AT LEAST ONE of the selected groups.
  const [variantFilters, setVariantFilters] = useState<Set<string>>(
    () => new Set(),
  );
  // Source filter — "arita" | "firm". Only shown when at least one ARITA
  // template exists in the library.
  const [sourceFilters, setSourceFilters] = useState<Set<string>>(
    () => new Set(),
  );
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const { data: tagGroupsForFilter } = useQuery({
    ...convexQuery(api.settings.precedents.tagGroups.listTagGroups, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  // Variant groups available org-wide — surfaces the Variants filter
  // dropdown's options.
  const { data: variantGroupsForFilter } = useQuery({
    ...convexQuery(api.settings.precedents.variantGroups.listVariantGroups, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  // Letterhead filter options — derived from the asset library + variant
  // store so the dropdown shows the same options the template-level
  // picker offers, plus a "Firm default" and "No letterhead" choice.
  const letterheadFilterOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [
      { value: "firm-default", label: "Firm default" },
      { value: "none", label: "No letterhead" },
    ];
    const seenBundles = new Set<string>();
    for (const a of letterheadAssetsRaw ?? []) {
      const name = a.bundleName ?? a.sourceDocumentName ?? null;
      if (!name || seenBundles.has(name)) continue;
      seenBundles.add(name);
      opts.push({ value: `bundle:${name}`, label: name });
    }
    return opts;
  }, [letterheadAssetsRaw]);

  const { data: variantsForFilter } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });
  const letterheadFilterOptionsWithVariants = useMemo(() => {
    const opts = [...letterheadFilterOptions];
    for (const v of variantsForFilter ?? []) {
      opts.push({ value: `variant:${v._id}`, label: v.name });
    }
    return opts;
  }, [letterheadFilterOptions, variantsForFilter]);

  const toggleAppointmentFilter = useCallback((value: string) => {
    setAppointmentFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const toggleStatusFilter = useCallback((value: JobStatus) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const toggleCustomTagFilter = useCallback(
    (groupSlug: string, optionValue: string) => {
      setCustomTagFilters((prev) => {
        const next = new Map(prev);
        const current = new Set(next.get(groupSlug) ?? []);
        if (current.has(optionValue)) current.delete(optionValue);
        else current.add(optionValue);
        if (current.size === 0) next.delete(groupSlug);
        else next.set(groupSlug, current);
        return next;
      });
    },
    [],
  );

  const toggleContactRoleFilter = useCallback((value: ContactRole) => {
    setContactRoleFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const toggleLetterheadFilter = useCallback((value: string) => {
    setLetterheadFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const toggleVariantFilter = useCallback((value: string) => {
    setVariantFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const toggleSourceFilter = useCallback((value: string) => {
    setSourceFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchText("");
    setAppointmentFilters(new Set());
    setStatusFilters(new Set());
    setCustomTagFilters(new Map());
    setContactRoleFilters(new Set());
    setLetterheadFilters(new Set());
    setVariantFilters(new Set());
    setSourceFilters(new Set());
  }, []);

  const activeFilterCount =
    appointmentFilters.size +
    statusFilters.size +
    Array.from(customTagFilters.values()).reduce((sum, s) => sum + s.size, 0) +
    contactRoleFilters.size +
    letterheadFilters.size +
    variantFilters.size +
    sourceFilters.size;
  const hasAnyFilter = !!searchText.trim() || activeFilterCount > 0;

  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    const needle = searchText.trim().toLowerCase();
    return templates.filter((t) => {
      // Text search across the user-visible labels
      if (needle) {
        const haystack = [
          t.displayName ?? "",
          t.name ?? "",
          t.description ?? "",
          t.sourceDocumentName ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }

      const tags: string[] = t.tags ?? [];

      if (appointmentFilters.size > 0) {
        const hasAll = tags.includes(TAG_ALL);
        const matches =
          hasAll ||
          Array.from(appointmentFilters).some((slug) => tags.includes(slug));
        if (!matches) return false;
      }

      if (statusFilters.size > 0) {
        const matches = Array.from(statusFilters).some((s) =>
          tags.includes(jobStatusTag(s)),
        );
        if (!matches) return false;
      }

      if (customTagFilters.size > 0) {
        for (const [groupSlug, optionValues] of customTagFilters) {
          const matches = Array.from(optionValues).some((value) =>
            tags.includes(`${TAG_GROUP_PREFIX}${groupSlug}:${value}`),
          );
          if (!matches) return false;
        }
      }

      if (contactRoleFilters.size > 0) {
        const matches = Array.from(contactRoleFilters).some((kind) =>
          tags.includes(contactRoleTag(kind)),
        );
        if (!matches) return false;
      }

      if (letterheadFilters.size > 0) {
        const ov = t.letterheadOverride;
        // Build the matchable key for this template's override.
        const key: string = !ov
          ? "firm-default"
          : ov.kind === "none"
            ? "none"
            : ov.kind === "letterhead"
              ? `bundle:${ov.bundleName}`
              : `variant:${ov.variantId}`;
        if (!letterheadFilters.has(key)) return false;
      }

      if (variantFilters.size > 0) {
        const html = t.templateHtml ?? "";
        const used = new Set<string>();
        const re = /data-group-slug="([^"]+)"/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(html)) !== null) used.add(m[1]);
        const matches = Array.from(variantFilters).some((slug) =>
          used.has(slug),
        );
        if (!matches) return false;
      }

      if (sourceFilters.size > 0) {
        const isArita = !!t.masterLink?.isArita;
        const key = isArita ? "arita" : "firm";
        if (!sourceFilters.has(key)) return false;
      }

      return true;
    });
  }, [
    templates,
    searchText,
    appointmentFilters,
    statusFilters,
    customTagFilters,
    contactRoleFilters,
    letterheadFilters,
    variantFilters,
    sourceFilters,
  ]);

  const templatesByCategory = useMemo(() => {
    const map = new Map<string, PrecedentTemplateRow[]>();
    for (const t of filteredTemplates) {
      const key = t.categoryId ?? "__uncategorized";
      const list = map.get(key) ?? [];
      list.push(t);
      map.set(key, list);
    }
    return map;
  }, [filteredTemplates]);

  const hasAritaTemplates = useMemo(
    () => (templates ?? []).some((t) => t.masterLink?.isArita),
    [templates],
  );

  // -------------------------------------------------------------------------
  // Bulk-edit selection mode. Lifted to this top-level component so the
  // floating action bar (rendered alongside the gallery) and individual cards
  // can share state. We deliberately drop selections when leaving select-mode
  // so re-entering starts clean.
  // -------------------------------------------------------------------------
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<
    Set<Id<"precedentTemplates">>
  >(() => new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleTemplateSelected = useCallback(
    (templateId: Id<"precedentTemplates">) => {
      setSelectionMode(true);
      setSelectedTemplateIds((prev) => {
        const next = new Set(prev);
        if (next.has(templateId)) next.delete(templateId);
        else next.add(templateId);
        return next;
      });
    },
    [],
  );

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedTemplateIds(new Set());
  }, []);

  const { mutateAsync: bulkDeleteTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.deleteTemplate,
    ),
  });

  const duplicateTemplatesAction = useConvexAction(
    api.settings.precedents.duplicate.duplicateTemplates,
  );
  const { mutateAsync: doDuplicate, isPending: bulkDuplicating } = useMutation({
    mutationFn: duplicateTemplatesAction,
  });

  const handleBulkDuplicate = useCallback(async () => {
    const ids = Array.from(selectedTemplateIds);
    if (ids.length === 0) return;
    try {
      const result = await doDuplicate({ templateIds: ids });
      const n = result?.newTemplateIds?.length ?? ids.length;
      toast.success(`Duplicated ${n} template${n !== 1 ? "s" : ""}`);
      exitSelectionMode();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to duplicate templates"));
    }
  }, [selectedTemplateIds, doDuplicate, exitSelectionMode]);

  const handleBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedTemplateIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        ids.map((templateId) => bulkDeleteTemplate({ templateId })),
      );
      toast.success(
        `Deleted ${ids.length} template${ids.length !== 1 ? "s" : ""}`,
      );
      setBulkDeleteOpen(false);
      exitSelectionMode();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to delete templates"));
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedTemplateIds, bulkDeleteTemplate, exitSelectionMode]);

  const enterSelectionMode = useCallback(() => {
    setSelectionMode(true);
    setSelectedTemplateIds(() => {
      const ids = new Set<Id<"precedentTemplates">>();
      for (const t of filteredTemplates) {
        if (t._id) ids.add(t._id as Id<"precedentTemplates">);
      }
      return ids;
    });
  }, [filteredTemplates]);

  // DnD state for template recategorisation
  const [activeDragTemplate, setActiveDragTemplate] = useState<{
    name: string;
    templateId: Id<"precedentTemplates">;
    categoryId?: string;
  } | null>(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const { mutateAsync: moveTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.updateTemplateCategory,
    ),
  });

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as
      | {
          templateName?: string;
          templateId?: Id<"precedentTemplates">;
          categoryId?: string;
        }
      | undefined;
    if (data?.templateId) {
      setActiveDragTemplate({
        name: data.templateName ?? "Template",
        templateId: data.templateId,
        categoryId: data.categoryId,
      });
    }
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveDragTemplate(null);

      const { active, over } = event;
      if (!over) return;

      const targetCategoryId = over.data.current?.categoryId as
        string | undefined;
      if (!targetCategoryId || targetCategoryId === "__uncategorized") return;

      const templateId = active.data.current?.templateId as
        Id<"precedentTemplates"> | undefined;
      const sourceCategoryId = active.data.current?.categoryId as
        string | undefined;
      if (!templateId || sourceCategoryId === targetCategoryId) return;

      try {
        await moveTemplate({
          templateId,
          categoryId: targetCategoryId as Id<"templateCategories">,
        });
        toast.success("Template moved");
      } catch {
        toast.error("Failed to move template");
      }
    },
    [moveTemplate],
  );

  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  // Filter tab — "all" shows every category, otherwise show one specific category id
  const [activeCategoryTab, setActiveCategoryTab] = useState<string>("all");

  // Track which categories are collapsed (by id). Default = expanded.
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleCategoryCollapse = useCallback((id: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const { mutateAsync: createCategory } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.categories.createCategory,
    ),
  });

  const handleCreateCategory = useCallback(async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    setCreatingCategory(true);
    try {
      await createCategory({ orgId: typedOrgId, name: trimmed });
      toast.success(`Category "${trimmed}" created`);
      setAddCategoryOpen(false);
      setNewCategoryName("");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to create category"));
    } finally {
      setCreatingCategory(false);
    }
  }, [newCategoryName, typedOrgId, createCategory]);

  if (!orgId) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <p className="text-sm text-primary/60">
          Select an organisation to manage precedent templates.
        </p>
      </div>
    );
  }

  if (categoriesLoading || templatesLoading) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <Spinner className="size-6" />
      </div>
    );
  }

  const totalTemplates = templates?.length ?? 0;
  const filteredCount = filteredTemplates.length;

  const topTabs = showAritaTab ? (
    <div className="flex w-fit items-center gap-1 rounded-lg bg-secondary/40 p-1">
      <button
        type="button"
        onClick={() => setActiveTopTab("library")}
        className={cn(
          "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
          activeTopTab === "library"
            ? "bg-background text-primary shadow-sm"
            : "text-primary/50 hover:text-primary/70",
        )}
      >
        My Library
      </button>
      <button
        type="button"
        onClick={() => setActiveTopTab("arita")}
        className={cn(
          "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
          activeTopTab === "arita"
            ? "bg-background text-primary shadow-sm"
            : "text-primary/50 hover:text-primary/70",
        )}
      >
        <Library className="size-3.5" />
        ARITA Library
      </button>
    </div>
  ) : null;

  if (showAritaTab && activeTopTab === "arita") {
    return (
      <div className="flex w-full flex-col gap-6">
        {topTabs}
        <AritaLibraryTab orgId={typedOrgId} canEdit={canEdit} />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {topTabs}
      {!canEdit && <ReadOnlyBanner />}

      {/* Page header + search/filter */}
      <div className={GROUP_CARD_CLASS}>
        <div className="flex items-start justify-between px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-primary">
              Precedent Library
            </h3>
            <p className="mt-1 text-sm text-primary/60">
              Manage your precedent templates in the library. Apply and filter
              by categories and tags.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant="secondary">
              {totalTemplates} template{totalTemplates !== 1 ? "s" : ""}
            </Badge>
            <div className="flex items-center rounded-md border border-border/60">
              <button
                type="button"
                onClick={() => toggleViewMode("grid")}
                className={cn(
                  "flex items-center justify-center rounded-l-md px-2 py-1.5 transition-colors",
                  viewMode === "grid"
                    ? "bg-primary/10 text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => toggleViewMode("table")}
                className={cn(
                  "flex items-center justify-center rounded-r-md px-2 py-1.5 transition-colors",
                  viewMode === "table"
                    ? "bg-primary/10 text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                aria-label="Table view"
              >
                <List className="size-3.5" />
              </button>
            </div>
          </div>
        </div>

        <div className="px-5 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-primary/40" />
              <Input
                placeholder="Search templates by name or description…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="h-9 pl-9 pr-9 text-sm"
              />
              {searchText && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchText("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-primary/40 hover:bg-secondary/60 hover:text-primary/70"
                >
                  <XIcon className="size-3" />
                </button>
              )}
            </div>
            <Button
              type="button"
              variant={
                filtersExpanded || activeFilterCount > 0 ? "default" : "outline"
              }
              size="sm"
              className="h-9 gap-1.5 text-xs"
              onClick={() => setFiltersExpanded((v) => !v)}
            >
              <Filter className="size-3.5" />
              Filters
              {activeFilterCount > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-1 h-4 px-1.5 text-[10px]"
                >
                  {activeFilterCount}
                </Badge>
              )}
              <ChevronDown
                className={cn(
                  "size-3.5 transition-transform",
                  filtersExpanded && "rotate-180",
                )}
              />
            </Button>
            {canEdit && (
              <Button
                type="button"
                variant={selectionMode ? "default" : "outline"}
                size="sm"
                className="h-9 gap-1.5 text-xs"
                onClick={selectionMode ? exitSelectionMode : enterSelectionMode}
              >
                <CheckSquare className="size-3.5" />
                {selectionMode ? "Deselect All" : "Select All"}
              </Button>
            )}
            {canEdit && (
              <Dialog open={addCategoryOpen} onOpenChange={setAddCategoryOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1.5 text-xs"
                  >
                    <Plus className="size-3.5" />
                    Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>New Category</DialogTitle>
                    <DialogDescription>
                      Create a category to organise your templates (e.g. "State
                      Revenue Letters", "Vehicle Search Forms").
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-2">
                    <Input
                      placeholder="Category name"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCreateCategory();
                      }}
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={handleCreateCategory}
                      disabled={!newCategoryName.trim() || creatingCategory}
                      className="gap-1"
                    >
                      {creatingCategory && (
                        <Loader2 className="size-3.5 animate-spin" />
                      )}
                      Create Category
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {canEdit && (categories?.length ?? 0) > 0 && (
              <Button
                size="sm"
                className="h-9 gap-1.5 text-xs"
                onClick={() => setBulkUploadOpen(true)}
              >
                <Upload className="size-3.5" />
                Upload
              </Button>
            )}
            {hasAnyFilter && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 text-xs text-primary/60"
                onClick={clearAllFilters}
              >
                Clear all
              </Button>
            )}
            {hasAnyFilter && !selectionMode && (
              <span className="ml-auto text-xs text-primary/50">
                {filteredCount} of {totalTemplates} template
                {totalTemplates === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {filtersExpanded && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <FilterDropdown
                label="Appointment Type"
                options={APPOINTMENT_TYPE_TAGS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                selected={appointmentFilters}
                onToggle={toggleAppointmentFilter}
              />
              <FilterDropdown
                label="Job Status"
                options={JOB_STATUS_TAGS.map((o) => ({
                  value: o.status,
                  label: o.label,
                }))}
                selected={statusFilters}
                onToggle={(value) => toggleStatusFilter(value as JobStatus)}
              />
              <FilterDropdown
                label="Contact Role"
                options={ROLE_ORDER.map((kind) => ({
                  value: kind,
                  label: ROLE_META[kind].label,
                }))}
                selected={contactRoleFilters}
                onToggle={(value) =>
                  toggleContactRoleFilter(value as ContactRole)
                }
              />
              {hasAritaTemplates && (
                <FilterDropdown
                  label="Source"
                  options={[
                    { value: "arita", label: "ARITA" },
                    { value: "firm", label: "Firm" },
                  ]}
                  selected={sourceFilters}
                  onToggle={toggleSourceFilter}
                />
              )}
              <FilterDropdown
                label="Letterhead"
                options={letterheadFilterOptionsWithVariants}
                selected={letterheadFilters}
                onToggle={toggleLetterheadFilter}
              />
              {variantGroupsForFilter && variantGroupsForFilter.length > 0 && (
                <FilterDropdown
                  label="Variants"
                  options={variantGroupsForFilter.map(
                    (g: { slug: string; name: string }) => ({
                      value: g.slug,
                      label: g.name,
                    }),
                  )}
                  selected={variantFilters}
                  onToggle={toggleVariantFilter}
                />
              )}
              {tagGroupsForFilter?.map(
                (group: NonNullable<typeof tagGroupsForFilter>[number]) => (
                  <FilterDropdown
                    key={group._id}
                    label={group.name}
                    options={group.options.map(
                      (o: { value: string; label: string }) => o,
                    )}
                    selected={customTagFilters.get(group.slug) ?? new Set()}
                    onToggle={(val) => toggleCustomTagFilter(group.slug, val)}
                  />
                ),
              )}
            </div>
          )}
        </div>

        {/* Category filter tabs — only shown when there are 2+ categories to filter between */}
        {(() => {
          const tabCategories = categories ?? [];
          const hasUncategorised =
            (templatesByCategory.get("__uncategorized")?.length ?? 0) > 0;
          const tabs: Array<{ id: string; label: string; count: number }> = [
            { id: "all", label: "All", count: filteredCount },
            ...tabCategories.map((c) => ({
              id: c._id as string,
              label: c.name,
              count: templatesByCategory.get(c._id)?.length ?? 0,
            })),
            ...(hasUncategorised
              ? [
                  {
                    id: "__uncategorized",
                    label: "Uncategorised",
                    count:
                      templatesByCategory.get("__uncategorized")?.length ?? 0,
                  },
                ]
              : []),
          ];

          if (tabs.length <= 2) return null;

          return (
            <div className="border-t border-border/60 px-5 py-2">
              <div className="flex gap-1 overflow-x-auto rounded-lg bg-secondary/40 p-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveCategoryTab(tab.id)}
                    className={cn(
                      "flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition",
                      activeCategoryTab === tab.id
                        ? "bg-background text-primary shadow-sm"
                        : "text-primary/50 hover:text-primary/70",
                    )}
                  >
                    {tab.label}
                    <span
                      className={cn(
                        "rounded px-1.5 py-px text-[10px] tabular-nums transition",
                        activeCategoryTab === tab.id
                          ? "bg-primary/10 text-primary/60"
                          : "bg-primary/5 text-primary/40",
                      )}
                    >
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        <div className="flex flex-col gap-3 px-5 pb-4 pt-3">
          {viewMode === "grid" ? (
            <>
              <DndContext
                sensors={dndSensors}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                {/* DB-driven category sections */}
                {(categories ?? [])
                  .filter(
                    (cat) =>
                      activeCategoryTab === "all" ||
                      activeCategoryTab === cat._id,
                  )
                  .map((cat) => (
                    <CategorySection
                      key={cat._id}
                      category={cat}
                      templates={templatesByCategory.get(cat._id) ?? []}
                      orgId={typedOrgId}
                      brandingProfile={previewBrandingProfile}
                      collapsed={collapsedCategories.has(cat._id)}
                      onToggleCollapsed={() => toggleCategoryCollapse(cat._id)}
                      canEdit={canEdit}
                      selectionMode={selectionMode}
                      selectedTemplateIds={selectedTemplateIds}
                      onToggleSelect={toggleTemplateSelected}
                    />
                  ))}

                {/* Uncategorized templates (legacy fallback) */}
                {(templatesByCategory.get("__uncategorized")?.length ?? 0) >
                  0 &&
                  (activeCategoryTab === "all" ||
                    activeCategoryTab === "__uncategorized") && (
                    <CategorySection
                      category={{
                        _id: "__uncategorized",
                        name: "Uncategorised",
                        isSystem: false,
                      }}
                      templates={
                        templatesByCategory.get("__uncategorized") ?? []
                      }
                      orgId={typedOrgId}
                      brandingProfile={previewBrandingProfile}
                      collapsed={collapsedCategories.has("__uncategorized")}
                      onToggleCollapsed={() =>
                        toggleCategoryCollapse("__uncategorized")
                      }
                      canEdit={canEdit}
                      selectionMode={selectionMode}
                      selectedTemplateIds={selectedTemplateIds}
                      onToggleSelect={toggleTemplateSelected}
                    />
                  )}

                <DragOverlay dropAnimation={null}>
                  {activeDragTemplate ? (
                    <div className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2 shadow-lg">
                      <FileText className="size-4 shrink-0 text-primary/40" />
                      <span className="max-w-[200px] truncate text-sm font-medium text-primary/80">
                        {activeDragTemplate.name}
                      </span>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>

              {/* Empty state */}
              {(categories ?? []).length === 0 && totalTemplates === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="size-10 text-primary/15" />
                  <p className="mt-3 text-sm text-primary/50">
                    No categories or templates yet
                  </p>
                  <p className="mt-1 text-xs text-primary/30">
                    Use bulk upload to add templates, or create a category
                    first.
                  </p>
                </div>
              )}
            </>
          ) : (
            <TemplateTable
              templates={filteredTemplates.filter(
                (t) =>
                  activeCategoryTab === "all" ||
                  (activeCategoryTab === "__uncategorized"
                    ? !t.categoryId
                    : t.categoryId === activeCategoryTab),
              )}
              categories={categories ?? []}
              tagGroups={(tagGroupsForFilter ?? []) as Array<TagGroup>}
              selectionMode={selectionMode}
              selectedTemplateIds={selectedTemplateIds}
              onToggleSelect={toggleTemplateSelected}
              canEdit={canEdit}
              orgId={typedOrgId}
              brandingProfile={previewBrandingProfile}
            />
          )}
        </div>
      </div>

      {canEdit && (
        <BulkUploadDialog
          open={bulkUploadOpen}
          onOpenChange={setBulkUploadOpen}
          orgId={typedOrgId}
          categories={categories ?? []}
          defaultCategoryId={
            activeCategoryTab !== "all" &&
            activeCategoryTab !== "__uncategorized"
              ? activeCategoryTab
              : undefined
          }
        />
      )}

      {/* Floating action bar — fixed to the bottom of the viewport when the
          user is in selection mode and at least one template is selected. */}
      {(selectionMode || selectedTemplateIds.size > 0) && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
          <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-primary-foreground/20 bg-primary px-4 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.18),0_2px_6px_rgba(0,0,0,0.12)]">
            <span className="text-sm font-medium text-primary-foreground">
              {selectedTemplateIds.size} selected
            </span>
            <div className="h-4 w-px bg-primary-foreground/20" />
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1.5 bg-white text-xs text-primary hover:bg-white/90"
              onClick={() => setBulkEditOpen(true)}
            >
              <Pencil className="size-3.5" />
              Edit
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1.5 bg-white text-xs text-primary hover:bg-white/90"
              onClick={() => void handleBulkDuplicate()}
              disabled={bulkDuplicating}
            >
              {bulkDuplicating ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Copy className="size-3.5" />
              )}
              Duplicate
            </Button>
            <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1.5 bg-white text-xs text-red-500 hover:bg-white/90 hover:text-red-600"
                >
                  <Trash2 className="size-3.5" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Delete {selectedTemplateIds.size} template
                    {selectedTemplateIds.size !== 1 ? "s" : ""}?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete the selected templates and
                    their source documents. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={bulkDeleting}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {bulkDeleting && (
                      <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                    )}
                    Delete {selectedTemplateIds.size} template
                    {selectedTemplateIds.size !== 1 ? "s" : ""}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              onClick={exitSelectionMode}
            >
              Done
            </Button>
          </div>
        </div>
      )}

      {canEdit && (
        <BulkEditDialog
          open={bulkEditOpen}
          onOpenChange={setBulkEditOpen}
          orgId={typedOrgId}
          templateIds={Array.from(selectedTemplateIds)}
          categories={categories ?? []}
          tagGroups={(tagGroupsForFilter ?? []) as Array<TagGroup>}
          onApplied={() => {
            setBulkEditOpen(false);
            exitSelectionMode();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter Dropdown — multi-select popover used in the filter bar
// ---------------------------------------------------------------------------

function FilterDropdown({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  selected: ReadonlySet<string>;
  onToggle: (value: string) => void;
}) {
  const count = selected.size;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition",
            count > 0
              ? "border-primary/40 bg-primary/10 font-medium text-primary"
              : "border-border/60 text-primary/50 hover:border-primary/30 hover:text-primary/70",
          )}
        >
          {label}
          {count > 0 && (
            <span className="flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-semibold text-primary-foreground">
              {count}
            </span>
          )}
          <ChevronDown className="size-2.5 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-2">
        <div className="max-h-56 space-y-0.5 overflow-y-auto">
          {options.map((opt) => {
            const active = selected.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onToggle(opt.value)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs transition",
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-primary/70 hover:bg-secondary/60",
                )}
              >
                <Checkbox
                  checked={active}
                  className="pointer-events-none size-3.5"
                  tabIndex={-1}
                />
                {opt.label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Tag Bucket Cell — renders one of the four tag-dimension columns
// (Job Type / Job Status / Tags / Contacts) in the library table.
//
// Each item declares its own `color` so the Contacts column can mirror the
// per-kind palette used by the Job CRM contacts table (Creditors=red,
// ATO/SRO=blue, Loan Counterparties=violet, Employees=teal, etc.) while
// the Job Type / Job Status / Tags columns can pin a single colour.
//
// Default state shows up to two chips with a `+N` overflow pill; clicking
// the cell flips the row into expanded mode (managed by the parent table
// via `expandedRows`) and reveals every chip wrapped onto multiple lines.
// ---------------------------------------------------------------------------

type TagBucketItem = {
  key: string;
  label: string;
  color: BadgeColor;
  /** Optional leading icon — used by the Contacts column to mark the
   *  contact's role/category and distinguish job vs firm contacts. */
  icon?: LucideIcon;
};

function TagBucketCell({
  items,
  expanded,
  onToggle,
}: {
  items: ReadonlyArray<TagBucketItem>;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (items.length === 0) {
    return <span className="text-xs text-primary/30">—</span>;
  }
  const COLLAPSED_LIMIT = 2;
  const visible = expanded ? items : items.slice(0, COLLAPSED_LIMIT);
  const hiddenCount = items.length - visible.length;
  return (
    <div
      className="flex cursor-pointer flex-wrap gap-1"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      {visible.map((item) => {
        const Icon = item.icon;
        return (
          <Badge
            key={item.key}
            color={item.color}
            variant="soft"
            size="sm"
            className="inline-flex max-w-full items-center gap-1 whitespace-nowrap"
            title={item.label}
          >
            {Icon && <Icon className="size-3 shrink-0" />}
            <span className="truncate">{item.label}</span>
          </Badge>
        );
      })}
      {hiddenCount > 0 && (
        <Badge
          color="gray"
          variant="outline"
          size="sm"
          className="whitespace-nowrap"
          title={`Show ${hiddenCount} more`}
        >
          +{hiddenCount}
        </Badge>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template Table — flat table view of all templates
// ---------------------------------------------------------------------------

/**
 * Compact label for the Letterhead column. Shows what each template
 * actually resolves to at render time:
 *   - no override         → "Firm default — Beacon" (muted)
 *   - kind: "none"        → "No letterhead" (slate badge)
 *   - kind: "letterhead"  → bundle name
 *   - kind: "variant"     → variant name (looked up by caller)
 *   - third-party form    → "—" (forms preserve their own branding)
 */
function LetterheadCellLabel({
  override,
  firmDefaultBundleName,
  variantName,
  isThirdPartyForm,
}: {
  override:
    | { kind: "none" }
    | { kind: "letterhead"; bundleName: string }
    | { kind: "variant"; variantId: Id<"letterheadVariants"> }
    | null;
  firmDefaultBundleName: string | null;
  variantName: string | null;
  isThirdPartyForm?: boolean;
}) {
  if (isThirdPartyForm) {
    return <span className="text-xs text-primary/30">—</span>;
  }

  const renderTruncated = (fullText: string, node: React.ReactNode) => {
    if (fullText.length <= 16) return node;
    return (
      <Tooltip>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-[400px]">
          {fullText}
        </TooltipContent>
      </Tooltip>
    );
  };

  if (!override) {
    const fullText = firmDefaultBundleName
      ? `Firm default — ${firmDefaultBundleName}`
      : "Firm default";
    const node = (
      <span className="block max-w-[105px] truncate text-xs text-primary/50">
        {firmDefaultBundleName ? (
          <>
            Firm default{" "}
            <span className="text-primary/40">— {firmDefaultBundleName}</span>
          </>
        ) : (
          "Firm default"
        )}
      </span>
    );
    return renderTruncated(fullText, node);
  }
  if (override.kind === "none") {
    return (
      <Badge
        variant="secondary"
        className="bg-slate-200 text-[10px] font-normal text-slate-700 dark:bg-slate-800 dark:text-slate-200"
      >
        No letterhead
      </Badge>
    );
  }
  if (override.kind === "letterhead") {
    const node = (
      <span className="block max-w-[105px] truncate text-xs font-medium text-primary/80">
        {override.bundleName}
      </span>
    );
    return renderTruncated(override.bundleName, node);
  }
  const label = variantName ?? "Variant";
  const node = (
    <span className="block max-w-[105px] truncate text-xs font-medium text-primary/80">
      {label}
    </span>
  );
  return renderTruncated(label, node);
}

function AritaSyncButton({
  templateId,
  templateName,
  modifiedLocally,
  orgId,
}: {
  templateId: Id<"precedentTemplates">;
  templateName: string;
  modifiedLocally: boolean;
  orgId: Id<"organizations">;
}) {
  const { mutateAsync: syncFromMaster, isPending: syncing } = useMutation({
    mutationFn: useConvexAction(
      api.settings.precedents.masterActions.syncFromMaster,
    ),
  });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleSync = useCallback(async () => {
    try {
      await syncFromMaster({ orgId, templateIds: [templateId] });
      toast.success("Synced latest from ARITA");
      setConfirmOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to sync from ARITA"));
    }
  }, [templateId, syncFromMaster, orgId]);

  return (
    <>
      <button
        type="button"
        disabled={syncing}
        onClick={(e) => {
          e.stopPropagation();
          if (modifiedLocally) setConfirmOpen(true);
          else handleSync();
        }}
        className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-700 transition hover:bg-amber-200 disabled:opacity-50 dark:bg-amber-950/40 dark:text-amber-300"
        title="A newer version is available in the ARITA master library"
      >
        {syncing ? (
          <Loader2 className="size-2.5 animate-spin" />
        ) : (
          <RefreshCw className="size-2.5" />
        )}
        Update
      </button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Sync &ldquo;{templateName}&rdquo; from ARITA?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This template was edited after it was added from the ARITA
              library. Syncing will overwrite your changes with the latest
              master version. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSync}
              className={buttonVariants({ variant: "destructive" })}
            >
              Overwrite &amp; Sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function TemplateTable({
  templates,
  categories,
  tagGroups,
  selectionMode,
  selectedTemplateIds,
  onToggleSelect,
  canEdit,
  orgId,
  brandingProfile,
}: {
  templates: PrecedentTemplateRow[];
  categories: TemplateCategoryRow[];
  tagGroups: ReadonlyArray<TagGroup>;
  selectionMode: boolean;
  selectedTemplateIds: Set<Id<"precedentTemplates">>;
  onToggleSelect: (id: Id<"precedentTemplates">) => void;
  canEdit: boolean;
  orgId: Id<"organizations">;
  brandingProfile: PreviewBrandingProfile | null | undefined;
}) {
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const cat of categories) {
      map.set(cat._id, cat.name);
    }
    return map;
  }, [categories]);

  // Resolve variant names for the Letterhead column. The variants list is
  // already cached by other surfaces (assets manager, day-one page); this
  // query just rides the same cache key.
  const { data: variantsForLabel } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const variantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of variantsForLabel ?? []) {
      map.set(v._id, v.name);
    }
    return map;
  }, [variantsForLabel]);

  // Variant GROUPS used by the new Variants column — show which sentence-
  // level variant groups a template references in its body. Cheap regex
  // scan of templateHtml for `data-group-slug="X"` is fine; the editor
  // serialises variantBlocks with that attribute.
  const { data: variantGroupsForCol } = useQuery({
    ...convexQuery(api.settings.precedents.variantGroups.listVariantGroups, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const toTitleCase = (s: string) =>
    s
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

  const variantGroupNameBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of variantGroupsForCol ?? []) {
      map.set(g.slug, g.name);
    }
    return map;
  }, [variantGroupsForCol]);

  const extractVariantGroupsFromHtml = useCallback(
    (html?: string | null): Array<{ slug: string; name: string }> => {
      if (!html) return [];
      const slugs = new Set<string>();
      const re = /data-group-slug="([^"]+)"/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(html)) !== null) slugs.add(m[1]);
      return [...slugs].map((slug) => ({
        slug,
        name: toTitleCase(variantGroupNameBySlug.get(slug) ?? slug),
      }));
    },
    [variantGroupNameBySlug],
  );

  const firmDefaultBundleName =
    brandingProfile?.firmDefaultLetterheadBundleName ?? null;

  const { mutateAsync: deleteTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.deleteTemplate,
    ),
  });

  const duplicateTemplatesAction = useConvexAction(
    api.settings.precedents.duplicate.duplicateTemplates,
  );
  const { mutateAsync: duplicateOne } = useMutation({
    mutationFn: duplicateTemplatesAction,
  });
  const [duplicatingTemplateId, setDuplicatingTemplateId] =
    useState<Id<"precedentTemplates"> | null>(null);

  const handleDuplicateRow = useCallback(
    async (templateId: Id<"precedentTemplates">, name: string) => {
      setDuplicatingTemplateId(templateId);
      try {
        await duplicateOne({ templateIds: [templateId] });
        toast.success(`Duplicated "${name}"`);
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to duplicate template"));
      } finally {
        setDuplicatingTemplateId(null);
      }
    },
    [duplicateOne],
  );

  const [deleteTarget, setDeleteTarget] = useState<{
    id: Id<"precedentTemplates">;
    name: string;
  } | null>(null);

  const handleDelete = useCallback(
    (templateId: Id<"precedentTemplates">, name: string) => {
      setDeleteTarget({ id: templateId, name });
    },
    [],
  );

  // Gallagher templates (Web Portal, hardcoded fields) cannot be duplicated
  // or deleted — clicking the row opens the same edit dialog the gallery
  // view exposes when you click a card.
  const [previewTemplateId, setPreviewTemplateId] =
    useState<Id<"precedentTemplates"> | null>(null);
  const previewTemplate = useMemo(
    () =>
      previewTemplateId
        ? (templates.find((t) => t._id === previewTemplateId) ?? null)
        : null,
    [previewTemplateId, templates],
  );

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const { id, name } = deleteTarget;
    try {
      await deleteTemplate({ templateId: id });
      toast.success(`"${name}" deleted`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to delete template"));
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteTemplate]);

  // ---------------------------------------------------------------------------
  // Column visibility (localStorage-persisted)
  // ---------------------------------------------------------------------------
  // Each column has a stable key + a default visibility flag. The user's
  // overrides are persisted under `precedent-table-cols`.
  const COLUMN_DEFS = useMemo(
    () =>
      [
        { key: "name", label: "Name", defaultVisible: true, sortable: true },
        {
          key: "category",
          label: "Category",
          defaultVisible: true,
          sortable: true,
        },
        {
          key: "jobType",
          label: "Job Type",
          defaultVisible: false,
          sortable: false,
        },
        {
          key: "jobStatus",
          label: "Job Status",
          defaultVisible: false,
          sortable: false,
        },
        { key: "tags", label: "Tags", defaultVisible: true, sortable: false },
        {
          key: "contacts",
          label: "Contacts",
          defaultVisible: false,
          sortable: false,
        },
        {
          key: "variants",
          label: "Variants",
          defaultVisible: true,
          sortable: false,
        },
        {
          key: "letterhead",
          label: "Letterhead",
          defaultVisible: true,
          sortable: false,
        },
        {
          key: "status",
          label: "Status",
          defaultVisible: true,
          sortable: true,
        },
        {
          key: "dateAdded",
          label: "Date added",
          defaultVisible: true,
          sortable: true,
        },
      ] as const,
    [],
  );
  type ColKey = (typeof COLUMN_DEFS)[number]["key"];

  const [hiddenCols, setHiddenCols] = useState<Set<ColKey>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem("precedent-table-hidden-cols");
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return new Set(arr as ColKey[]);
      }
    } catch {
      /* ignore */
    }
    return new Set(
      COLUMN_DEFS.filter((c) => !c.defaultVisible).map((c) => c.key),
    );
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "precedent-table-hidden-cols",
        JSON.stringify([...hiddenCols]),
      );
    } catch {
      /* ignore */
    }
  }, [hiddenCols]);

  // Resizable Name column — long template names are otherwise clipped. Width
  // (px) is persisted so the user's preferred width survives reloads.
  const NAME_COL_MIN = 120;
  const NAME_COL_MAX = 520;
  const NAME_COL_DEFAULT = 180;
  const [nameColWidth, setNameColWidth] = useState<number>(() => {
    if (typeof window === "undefined") return NAME_COL_DEFAULT;
    const raw = window.localStorage.getItem("precedent-name-col-width");
    const parsed = raw ? Number.parseInt(raw, 10) : NaN;
    if (Number.isFinite(parsed)) {
      return Math.min(NAME_COL_MAX, Math.max(NAME_COL_MIN, parsed));
    }
    return NAME_COL_DEFAULT;
  });
  const handleNameColResize = useCallback((width: number) => {
    const clamped = Math.min(NAME_COL_MAX, Math.max(NAME_COL_MIN, width));
    setNameColWidth(clamped);
    try {
      window.localStorage.setItem("precedent-name-col-width", String(clamped));
    } catch {
      /* ignore */
    }
  }, []);

  const toggleCol = useCallback((key: ColKey) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Sort state (localStorage-persisted)
  // ---------------------------------------------------------------------------
  type SortDir = "asc" | "desc";
  type SortState = { key: ColKey; dir: SortDir } | null;
  const [sort, setSort] = useState<SortState>(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem("precedent-table-sort");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && parsed.key && parsed.dir) {
          return parsed as SortState;
        }
      }
    } catch {
      /* ignore */
    }
    return { key: "dateAdded", dir: "desc" };
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("precedent-table-sort", JSON.stringify(sort));
    } catch {
      /* ignore */
    }
  }, [sort]);
  const onSortClick = useCallback(
    (key: ColKey) => {
      const def = COLUMN_DEFS.find((c) => c.key === key);
      if (!def?.sortable) return;
      setSort((prev) => {
        if (!prev || prev.key !== key) return { key, dir: "asc" };
        if (prev.dir === "asc") return { key, dir: "desc" };
        return null;
      });
    },
    [COLUMN_DEFS],
  );

  const sortedTemplates = useMemo(() => {
    if (!sort) return templates;
    const dir = sort.dir === "asc" ? 1 : -1;
    const getValue = (t: PrecedentTemplateRow): string | number => {
      switch (sort.key) {
        case "name":
          return (t.displayName ?? t.name ?? t.templateKey ?? "")
            .toString()
            .toLowerCase();
        case "category": {
          const cn = t.categoryId
            ? (categoryMap.get(t.categoryId) ?? "Uncategorised")
            : "Uncategorised";
          return cn.toLowerCase();
        }
        case "status":
          return (t.extractionStatus ?? "").toString();
        case "dateAdded":
          return t._creationTime ?? 0;
        default:
          return 0;
      }
    };
    return [...templates].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [templates, sort, categoryMap]);

  const [expandedRows, setExpandedRows] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleRowExpanded = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const { mutateAsync: renameTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.renameTemplate,
    ),
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const startEditing = useCallback((id: string, currentName: string) => {
    setEditingId(id);
    setEditValue(currentName);
  }, []);

  const commitRename = useCallback(
    async (templateId: Id<"precedentTemplates">, originalName: string) => {
      setEditingId(null);
      const trimmed = editValue.trim();
      if (!trimmed || trimmed === originalName) return;
      try {
        await renameTemplate({ templateId, name: trimmed });
        toast.success("Template renamed");
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to rename template"));
      }
    },
    [editValue, renameTemplate],
  );

  const formatDate = (ts: number) => {
    const d = new Date(ts);
    return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(-2)}`;
  };

  // Compact pill for the Status column. Mirrors the colour used elsewhere
  // for extraction status (extracted = green, pending = amber, failed =
  // red, otherwise muted "no file").
  const getStatusBadge = (status: string | undefined) => {
    if (status === "completed")
      return (
        <Badge
          variant="secondary"
          className="bg-emerald-100 text-[10px] font-normal text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
        >
          Extracted
        </Badge>
      );
    if (status === "extracting" || status === "pending")
      return (
        <Badge
          variant="secondary"
          className="bg-amber-100 text-[10px] font-normal text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
        >
          {status === "extracting" ? "Extracting" : "Pending"}
        </Badge>
      );
    if (status === "failed")
      return (
        <Badge
          variant="secondary"
          className="bg-red-100 text-[10px] font-normal text-red-700 dark:bg-red-950/40 dark:text-red-400"
        >
          Failed
        </Badge>
      );
    return (
      <Badge
        variant="secondary"
        className="text-[10px] font-normal text-primary/50"
      >
        No file
      </Badge>
    );
  };

  // Org-defined custom firm-contact categories (MOR-914) so `fcc:<id>`
  // tags render with the category's live name.
  const { data: customFcCategories } = useQuery({
    ...convexQuery(api.contacts.customCategories.listFirmContactCategories, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const customFcCategoryById = useMemo(() => {
    const map = new Map<string, { name: string; icon?: string }>();
    for (const c of customFcCategories ?? []) {
      map.set(String(c._id), { name: c.name, icon: c.icon });
    }
    return map;
  }, [customFcCategories]);

  // Org-defined custom job-contact roles so `jcr:<id>` tags render with the
  // role's live name/colour/icon in the library's Contacts column.
  const { data: customJobRoles } = useQuery({
    ...convexQuery(api.contacts.customRoles.listJobContactRoles, { orgId }),
    enabled: !!orgId,
  });
  const customJobRoleById = useMemo(() => {
    const map = new Map<
      string,
      { name: string; color: BadgeColor; icon?: WorkflowRoleIcon }
    >();
    for (const r of customJobRoles ?? []) {
      map.set(String(r._id), { name: r.name, color: r.color, icon: r.icon });
    }
    return map;
  }, [customJobRoles]);

  // Lookup map for resolving `tg:slug:value` custom-tag tags to their
  // human-readable group + option labels. Keyed by `slug:value` for O(1)
  // resolution per tag.
  const customTagLookup = useMemo(() => {
    const map = new Map<string, { groupLabel: string; optionLabel: string }>();
    for (const group of tagGroups) {
      for (const opt of group.options ?? []) {
        map.set(`${group.slug}:${opt.value}`, {
          groupLabel: group.name,
          optionLabel: opt.label,
        });
      }
    }
    return map;
  }, [tagGroups]);

  // Splits a template's `tags` array into the four dimensions surfaced as
  // separate columns in the library table. Tags that don't resolve to any
  // known dimension (e.g. orphaned `tg:` tags whose group was deleted) are
  // silently dropped — they're still preserved on the document and round-trip
  // through the bulk-edit dialog.
  type TagBuckets = {
    jobType: string[];
    jobStatus: string[];
    customTags: { key: string; groupLabel: string; optionLabel: string }[];
    contacts: {
      key: string;
      label: string;
      color: BadgeColor;
      icon: LucideIcon;
      domain: "job" | "firm";
    }[];
  };

  const getTagBuckets = (t: PrecedentTemplateRow): TagBuckets => {
    const tags: string[] = t.tags ?? [];
    const buckets: TagBuckets = {
      jobType: [],
      jobStatus: [],
      customTags: [],
      contacts: [],
    };
    for (const tag of tags) {
      if (tag === TAG_ALL) {
        buckets.jobType.push("All");
        continue;
      }
      const apt = APPOINTMENT_TYPE_TAGS.find((a) => a.value === tag);
      if (apt) {
        buckets.jobType.push(apt.label);
        continue;
      }
      const jobTag = JOB_STATUS_TAGS.find((j) => j.value === tag);
      if (jobTag) {
        buckets.jobStatus.push(jobTag.label);
        continue;
      }
      if (tag.startsWith(TAG_GROUP_PREFIX)) {
        const stripped = tag.slice(TAG_GROUP_PREFIX.length);
        const resolved = customTagLookup.get(stripped);
        if (resolved) {
          buckets.customTags.push({ key: tag, ...resolved });
        }
        continue;
      }
      const role = parseContactRoleTag(tag);
      if (role) {
        buckets.contacts.push({
          key: `cr:${role}`,
          label: ROLE_META[role].label,
          color: ROLE_META[role].badgeColor,
          icon: ROLE_META[role].icon,
          domain: "job",
        });
        continue;
      }
      const customRoleId = parseJobContactCustomRoleTag(tag);
      if (customRoleId) {
        const custom = customJobRoleById.get(customRoleId);
        // Archived / unknown roles degrade to nothing rather than a
        // dangling chip.
        if (custom) {
          buckets.contacts.push({
            key: tag,
            label: custom.name,
            color: custom.color,
            icon: roleIconComponent(custom.icon) ?? TagIcon,
            domain: "job",
          });
        }
        continue;
      }
      const firmCategory = parseFirmContactTag(tag);
      if (firmCategory) {
        const meta = FIRM_CATEGORY_META[firmCategory];
        buckets.contacts.push({
          key: `fc:${firmCategory}`,
          // Use the singular form so a chip reads "Regulator", "Firm Member".
          label: meta.shortLabel,
          // Firm contacts share one colour so they read as a distinct group
          // from the per-role job-contact palette.
          color: "orange",
          icon: meta.icon,
          domain: "firm",
        });
        continue;
      }
      const customCategoryId = parseFirmContactCustomCategoryTag(tag);
      if (customCategoryId) {
        const custom = customFcCategoryById.get(customCategoryId);
        if (custom) {
          buckets.contacts.push({
            key: tag,
            label: custom.name,
            color: "orange",
            icon:
              roleIconComponent(custom.icon as WorkflowRoleIcon | undefined) ??
              TagIcon,
            domain: "firm",
          });
        }
        continue;
      }
      if (isFirmContactJobStateTag(tag)) {
        buckets.contacts.push({
          key: tag,
          label: "Firm · Job's state",
          color: "orange",
          icon: MapPinned,
          domain: "firm",
        });
        continue;
      }
      const firmState = parseFirmContactStateTag(tag);
      if (firmState) {
        buckets.contacts.push({
          key: tag,
          label: `Firm · ${firmState}`,
          color: "orange",
          icon: MapPin,
          domain: "firm",
        });
        continue;
      }
      if (isJobContactExternalAdminTag(tag)) {
        buckets.contacts.push({
          key: tag,
          label: "EXAD",
          color: "orange",
          icon: Landmark,
          domain: "job",
        });
      }
    }
    return buckets;
  };

  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="size-8 text-primary/15" />
        <p className="mt-2 text-sm text-primary/50">
          No templates match your filters
        </p>
      </div>
    );
  }

  // Per-column widths used in both <th> and <td> so the table doesn't
  // re-flow when a column is toggled.
  const COL_WIDTHS: Record<ColKey, string> = {
    name: "w-[180px]",
    category: "w-[110px]",
    jobType: "w-[90px]",
    jobStatus: "w-[110px]",
    tags: "w-[130px]",
    contacts: "w-[110px]",
    variants: "w-[120px]",
    letterhead: "w-[110px]",
    status: "w-[80px]",
    dateAdded: "w-[100px]",
  };
  const isVisible = (k: ColKey) => !hiddenCols.has(k);

  return (
    <div>
      {/* Table toolbar — Columns picker */}
      <div className="mb-2 flex items-center justify-end gap-2 px-1">
        <ColumnVisibilityMenu
          columns={[...COLUMN_DEFS]}
          hidden={hiddenCols}
          onToggle={toggleCol}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border/60 text-[11px] font-medium uppercase tracking-wide text-primary/50">
              <th className="w-10 py-2 pl-3 pr-0" />
              {COLUMN_DEFS.filter((c) => isVisible(c.key)).map((c) => (
                <SortHeader
                  key={c.key}
                  label={c.label}
                  sortable={c.sortable}
                  width={COL_WIDTHS[c.key]}
                  active={sort?.key === c.key}
                  dir={sort?.key === c.key ? sort.dir : null}
                  onClick={() => onSortClick(c.key)}
                  resizable={c.key === "name"}
                  pxWidth={c.key === "name" ? nameColWidth : undefined}
                  onResize={c.key === "name" ? handleNameColResize : undefined}
                />
              ))}
              {!selectionMode && (
                <th className="w-24 whitespace-nowrap py-2 pl-2 pr-3 text-right">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedTemplates.map((t, idx) => {
              const tplId = t._id as Id<"precedentTemplates"> | undefined;
              const isSelected = !!tplId && selectedTemplateIds.has(tplId);
              const categoryName = t.categoryId
                ? (categoryMap.get(t.categoryId) ?? "Uncategorised")
                : "Uncategorised";
              const tagBuckets = getTagBuckets(t);
              const isExpanded = expandedRows.has(t._id);
              const variantGroupsUsed = extractVariantGroupsFromHtml(
                t.templateHtml,
              );
              const isGallagher = t.templateKey === "form_gallagher";
              // A template is previewable when it carries either an inline
              // body, a source document, or is the Gallagher third-party form.
              // Clicking anywhere on such a row opens the preview dialog so
              // the user doesn't have to hunt for the eye icon.
              const canPreview =
                !!tplId &&
                (isGallagher ||
                  !!t.sourceDocumentStorageId ||
                  !!t.templateHtml);

              return (
                <tr
                  key={t._id ?? idx}
                  className={cn(
                    "border-b border-border/30",
                    (selectionMode || canPreview) && "cursor-pointer",
                    isSelected && "bg-primary/5",
                  )}
                  onClick={
                    selectionMode && tplId
                      ? () => onToggleSelect(tplId)
                      : canPreview && tplId
                        ? () => setPreviewTemplateId(tplId)
                        : undefined
                  }
                >
                  <td className="py-2.5 pl-3 pr-0">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => tplId && onToggleSelect(tplId)}
                      onClick={(e) => e.stopPropagation()}
                      className="size-4 border-primary/30"
                    />
                  </td>
                  {isVisible("name") && (
                    <td className="px-2 py-2.5" style={{ width: nameColWidth }}>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const name = t.displayName ?? t.name ?? t.templateKey;
                          const isEditing = editingId === t._id;

                          if (isEditing && canEdit && tplId) {
                            return (
                              <input
                                type="text"
                                className="flex-1 rounded border border-border bg-background px-1.5 py-0.5 text-sm font-medium text-primary outline-none focus:ring-1 focus:ring-primary/30"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => commitRename(tplId, name)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    commitRename(tplId, name);
                                  if (e.key === "Escape") setEditingId(null);
                                }}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                            );
                          }

                          const isTruncated = name.length > 22;
                          const nameSpan = (
                            <span
                              className={cn(
                                "block truncate font-medium text-primary",
                                canEdit &&
                                  "cursor-text hover:underline hover:decoration-primary/30 hover:underline-offset-2",
                              )}
                              style={{ maxWidth: nameColWidth - 24 }}
                              onClick={
                                canEdit && tplId
                                  ? (e) => {
                                      e.stopPropagation();
                                      startEditing(t._id, name);
                                    }
                                  : undefined
                              }
                            >
                              {name}
                            </span>
                          );

                          if (!isTruncated) return nameSpan;

                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {nameSpan}
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-[400px]"
                              >
                                {name}
                              </TooltipContent>
                            </Tooltip>
                          );
                        })()}
                        {t.masterLink?.isArita && (
                          <Badge
                            variant="secondary"
                            className="bg-blue-100 text-[9px] text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                            title={
                              t.masterLink.modifiedLocally
                                ? "Firm edited from the ARITA master library"
                                : "Added from the ARITA master library"
                            }
                          >
                            {t.masterLink.modifiedLocally
                              ? "ARITA · Firm Edited"
                              : "ARITA"}
                          </Badge>
                        )}
                        {t.masterLink?.updateAvailable && canEdit && tplId && (
                          <AritaSyncButton
                            templateId={tplId}
                            templateName={
                              t.displayName ?? t.name ?? t.templateKey
                            }
                            modifiedLocally={!!t.masterLink.modifiedLocally}
                            orgId={orgId}
                          />
                        )}
                      </div>
                    </td>
                  )}
                  {isVisible("category") && (
                    <td className="px-2 py-2.5 text-xs text-primary/60">
                      {categoryName}
                    </td>
                  )}
                  {isVisible("jobType") && (
                    <td className="px-2 py-2.5 align-top">
                      <TagBucketCell
                        expanded={isExpanded}
                        onToggle={() => t._id && toggleRowExpanded(t._id)}
                        items={tagBuckets.jobType.map((label) => ({
                          key: label,
                          label,
                          color: "gray",
                        }))}
                      />
                    </td>
                  )}
                  {isVisible("jobStatus") && (
                    <td className="px-2 py-2.5 align-top">
                      <TagBucketCell
                        expanded={isExpanded}
                        onToggle={() => t._id && toggleRowExpanded(t._id)}
                        items={tagBuckets.jobStatus.map((label) => ({
                          key: label,
                          label,
                          color: "blue",
                        }))}
                      />
                    </td>
                  )}
                  {isVisible("tags") && (
                    <td className="px-2 py-2.5 align-top">
                      <TagBucketCell
                        expanded={isExpanded}
                        onToggle={() => t._id && toggleRowExpanded(t._id)}
                        items={tagBuckets.customTags.map((tag) => ({
                          key: tag.key,
                          label: `${tag.groupLabel}: ${tag.optionLabel}`,
                          color: "purple",
                        }))}
                      />
                    </td>
                  )}
                  {isVisible("contacts") && (
                    <td className="px-2 py-2.5 align-top">
                      <TagBucketCell
                        expanded={isExpanded}
                        onToggle={() => t._id && toggleRowExpanded(t._id)}
                        items={tagBuckets.contacts.map((c) => ({
                          key: c.key,
                          label: c.label,
                          color: c.color,
                          icon: c.icon,
                        }))}
                      />
                    </td>
                  )}
                  {isVisible("variants") && (
                    <td className="px-2 py-2.5 align-top">
                      <TagBucketCell
                        expanded={isExpanded}
                        onToggle={() => t._id && toggleRowExpanded(t._id)}
                        items={variantGroupsUsed.map((g) => ({
                          key: g.slug,
                          label: g.name,
                          color: "indigo",
                        }))}
                      />
                    </td>
                  )}
                  {isVisible("letterhead") && (
                    <td className="px-2 py-2.5">
                      <LetterheadCellLabel
                        override={t.letterheadOverride ?? null}
                        firmDefaultBundleName={firmDefaultBundleName}
                        variantName={
                          t.letterheadOverride?.kind === "variant"
                            ? (variantNameById.get(
                                t.letterheadOverride.variantId,
                              ) ?? null)
                            : null
                        }
                        isThirdPartyForm={t.isThirdPartyForm}
                      />
                    </td>
                  )}
                  {isVisible("status") && (
                    <td className="px-2 py-2.5">
                      {getStatusBadge(t.extractionStatus)}
                    </td>
                  )}
                  {isVisible("dateAdded") && (
                    <td className="whitespace-nowrap px-2 py-2.5 text-xs tabular-nums text-primary/50">
                      {t._creationTime ? formatDate(t._creationTime) : "—"}
                    </td>
                  )}
                  {!selectionMode && (
                    <td className="py-2.5 pl-2 pr-3">
                      <div className="flex items-center justify-end gap-1">
                        {canPreview && tplId && (
                          <button
                            type="button"
                            className="rounded p-1 text-primary/40 transition hover:bg-primary/5 hover:text-primary"
                            aria-label="Preview template"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewTemplateId(tplId);
                            }}
                          >
                            <Eye className="size-3.5" />
                          </button>
                        )}
                        {canEdit && tplId && !isGallagher && (
                          <button
                            type="button"
                            className="rounded p-1 text-primary/40 transition hover:bg-primary/5 hover:text-primary disabled:opacity-50"
                            aria-label="Duplicate template"
                            disabled={duplicatingTemplateId === tplId}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDuplicateRow(
                                tplId,
                                t.displayName ?? t.name ?? t.templateKey,
                              );
                            }}
                          >
                            {duplicatingTemplateId === tplId ? (
                              <Loader2 className="size-3.5 animate-spin" />
                            ) : (
                              <Copy className="size-3.5" />
                            )}
                          </button>
                        )}
                        {canEdit && tplId && !isGallagher && (
                          <button
                            type="button"
                            className="rounded p-1 text-primary/40 transition hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/30"
                            aria-label="Delete template"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(
                                tplId,
                                t.displayName ?? t.name ?? t.templateKey,
                              );
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this precedent template. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {previewTemplate && (
        <PreviewDialog
          name={
            previewTemplate.displayName ??
            previewTemplate.name ??
            previewTemplate.templateKey
          }
          templateId={previewTemplate._id}
          templateKey={previewTemplate.templateKey}
          orgId={orgId}
          bodyHtml={previewTemplate.templateHtml}
          firmLetterheadFragments={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.firmLetterheadFragments
          }
          headerImageUrl={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.headerImageUrl
          }
          footerImageUrl={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.footerImageUrl
          }
          leftSideImageUrl={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.leftSideImageUrl
          }
          rightSideImageUrl={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.rightSideImageUrl
          }
          headerHtml={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.headerHtml
          }
          footerHtml={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.footerHtml
          }
          headerPageRule={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.headerPageRule
          }
          footerPageRule={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.footerPageRule
          }
          leftSidePageRule={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.leftSidePageRule
          }
          rightSidePageRule={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.rightSidePageRule
          }
          leftSideFit={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.leftSideFit
          }
          rightSideFit={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.rightSideFit
          }
          leftSideTopBlockHeightPct={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.leftSideTopBlockHeightPct
          }
          rightSideTopBlockHeightPct={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.rightSideTopBlockHeightPct
          }
          leftSideCrop={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.leftSideCrop
          }
          rightSideCrop={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.rightSideCrop
          }
          isThirdPartyForm={previewTemplate.isThirdPartyForm}
          pdfUrl={previewTemplate.sourceDocumentUrl}
          sourceDocumentName={previewTemplate.sourceDocumentName}
          fields={
            previewTemplate.extractedFields ??
            (previewTemplate.templateKey === "form_gallagher"
              ? GALLAGHER_INSURANCE_FIELDS
              : undefined)
          }
          pdfFieldMappings={previewTemplate.pdfFieldMappings}
          fieldPositions={
            previewTemplate.fieldPositions as FieldPosition[] | undefined
          }
          dateCalcs={
            previewTemplate.dateCalcs as Record<string, DateCalc> | undefined
          }
          brandingPadding={
            previewTemplate.isThirdPartyForm ? undefined : brandingProfile
          }
          templatePadding={
            previewTemplate.isThirdPartyForm ? undefined : previewTemplate
          }
          presetColors={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.editorPresetColors
          }
          defaultFontFamily={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.editorFontFamily
          }
          defaultLineHeight={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.editorLineHeight
          }
          bodyTextColor={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.bodyTextColor
          }
          defaultFontSize={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.editorFontSize
          }
          defaultParagraphSpacingBefore={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.editorParagraphSpacingBefore
          }
          defaultParagraphSpacingAfter={
            previewTemplate.isThirdPartyForm
              ? undefined
              : brandingProfile?.editorParagraphSpacingAfter
          }
          pageNumbering={
            previewTemplate.isThirdPartyForm
              ? null
              : resolvePageNumbering(brandingProfile, previewTemplate)
          }
          suppressPageNumbering={previewTemplate.suppressPageNumbering}
          currentTags={previewTemplate.tags ?? []}
          letterheadOverride={previewTemplate.letterheadOverride ?? null}
          attachments={
            (previewTemplate.attachments ?? []) as AttachmentRequirement[]
          }
          fieldConfig={
            previewTemplate.fieldConfig as GallagherFieldConfig[] | undefined
          }
          categoryId={previewTemplate.categoryId}
          initialPreviewVariantSelections={
            previewTemplate.previewVariantSelections
          }
          initialPreviewSnippetSelections={
            previewTemplate.previewSnippetSelections
          }
          canEdit={canEdit}
          open={true}
          onOpenChange={(open) => {
            if (!open) setPreviewTemplateId(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SortHeader — table column header with sort indicator + click handler
// ---------------------------------------------------------------------------

function SortHeader({
  label,
  sortable,
  width,
  active,
  dir,
  onClick,
  resizable,
  pxWidth,
  onResize,
}: {
  label: string;
  sortable: boolean;
  width: string;
  active: boolean;
  dir: "asc" | "desc" | null;
  onClick: () => void;
  resizable?: boolean;
  pxWidth?: number;
  onResize?: (width: number) => void;
}) {
  // Column resize: track the drag from the header's right edge and report
  // the new width to the parent, which clamps + persists it.
  const handleResizeStart = (e: ReactMouseEvent) => {
    if (!onResize || pxWidth === undefined) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = pxWidth;
    const onMove = (moveEvent: MouseEvent) => {
      onResize(startWidth + (moveEvent.clientX - startX));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const resizeHandle =
    resizable && onResize ? (
      <span
        role="separator"
        aria-orientation="vertical"
        onMouseDown={handleResizeStart}
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none hover:bg-primary/20"
      />
    ) : null;

  const thClassName = cn(
    "relative px-2 py-2",
    resizable && pxWidth !== undefined ? undefined : width,
  );
  const thStyle =
    resizable && pxWidth !== undefined ? { width: pxWidth } : undefined;

  if (!sortable) {
    return (
      <th className={thClassName} style={thStyle}>
        {label}
        {resizeHandle}
      </th>
    );
  }
  return (
    <th className={thClassName} style={thStyle}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "inline-flex items-center gap-1 text-left uppercase transition hover:text-primary/80",
          active ? "text-primary/80" : "text-primary/50",
        )}
      >
        {label}
        <span className="text-[9px] leading-none">
          {active && dir === "asc" ? "▲" : active && dir === "desc" ? "▼" : "▾"}
        </span>
      </button>
      {resizeHandle}
    </th>
  );
}

// ---------------------------------------------------------------------------
// ColumnVisibilityMenu — toggle which columns appear, persisted in localStorage
// ---------------------------------------------------------------------------

function ColumnVisibilityMenu<K extends string>({
  columns,
  hidden,
  onToggle,
}: {
  columns: Array<{ key: K; label: string }>;
  hidden: Set<K>;
  onToggle: (key: K) => void;
}) {
  const [open, setOpen] = useState(false);
  const visibleCount = columns.length - hidden.size;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1 text-xs text-primary/70 transition hover:bg-secondary"
        >
          <Columns3 className="size-3.5" />
          Columns
          <Badge variant="secondary" className="ml-0.5 text-[10px]">
            {visibleCount}/{columns.length}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        {columns.map((c) => {
          const visible = !hidden.has(c.key);
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => onToggle(c.key)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition hover:bg-secondary"
            >
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded border",
                  visible
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background",
                )}
              >
                {visible && <Check className="size-3" />}
              </span>
              <span className="text-primary/80">{c.label}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Category Section — a single user-defined category with its templates
// ---------------------------------------------------------------------------

function CategorySection({
  category,
  templates,
  orgId,
  brandingProfile,
  collapsed = false,
  onToggleCollapsed,
  canEdit,
  selectionMode,
  selectedTemplateIds,
  onToggleSelect,
}: {
  /** DB category, or the synthetic "Uncategorised" bucket for legacy rows. */
  category: Pick<TemplateCategoryRow, "name" | "isSystem"> & {
    _id: Id<"templateCategories"> | "__uncategorized";
  };
  templates: PrecedentTemplateRow[];
  orgId: Id<"organizations">;
  brandingProfile: PreviewBrandingProfile | null | undefined;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  canEdit: boolean;
  selectionMode: boolean;
  selectedTemplateIds: Set<Id<"precedentTemplates">>;
  onToggleSelect: (id: Id<"precedentTemplates">) => void;
}) {
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState(category.name);

  const { mutateAsync: updateCategory } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.categories.updateCategory,
    ),
  });

  const { mutateAsync: deleteCategoryMut } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.categories.deleteCategory,
    ),
  });

  const handleRename = useCallback(async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === category.name) {
      setEditingName(false);
      return;
    }
    try {
      await updateCategory({
        categoryId: category._id as Id<"templateCategories">,
        name: trimmed,
      });
      toast.success("Category renamed");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to rename"));
      setEditName(category.name);
    }
    setEditingName(false);
  }, [editName, category, updateCategory]);

  const handleDeleteCategory = useCallback(async () => {
    try {
      await deleteCategoryMut({
        categoryId: category._id as Id<"templateCategories">,
      });
      toast.success(`Category "${category.name}" deleted`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Cannot delete category"));
    }
  }, [category, deleteCategoryMut]);

  const templateTypes = useMemo(
    () =>
      templates.map((t) => ({
        key: t.templateKey,
        name: t.displayName ?? t.name ?? t.templateKey,
        shortName: t.displayName ?? t.name ?? t.templateKey,
        description: t.description ?? "",
        category: t.category,
        icon:
          t.templateType === "insurance"
            ? FileText
            : t.isThirdPartyForm
              ? FileSpreadsheet
              : FileText,
        thirdPartyForm: t.isThirdPartyForm,
        hardcodedFields:
          t.templateKey === "form_gallagher"
            ? GALLAGHER_INSURANCE_FIELDS
            : undefined,
      })),
    [templates],
  );

  const isUncategorized = category._id === "__uncategorized";

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `category-drop-${category._id}`,
    data: { categoryId: category._id },
    disabled: isUncategorized,
  });

  return (
    <div
      ref={isUncategorized ? undefined : setDropRef}
      className={cn(
        GROUP_CARD_CLASS,
        isOver && "ring-2 ring-primary ring-offset-2",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between px-5 py-3",
          !collapsed && "border-b border-border/50",
        )}
      >
        <div className="flex items-center gap-2">
          {onToggleCollapsed && (
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="-ml-1 flex size-6 shrink-0 items-center justify-center rounded text-primary/40 transition hover:bg-secondary/60 hover:text-primary/70"
              aria-label={collapsed ? "Expand category" : "Collapse category"}
              aria-expanded={!collapsed}
            >
              <ChevronDown
                className={cn(
                  "size-4 transition-transform duration-200",
                  collapsed && "-rotate-90",
                )}
              />
            </button>
          )}
          {editingName && !isUncategorized && canEdit ? (
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleRename}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRename();
                if (e.key === "Escape") {
                  setEditName(category.name);
                  setEditingName(false);
                }
              }}
              className="h-7 w-48 text-sm"
              autoFocus
            />
          ) : canEdit && !isUncategorized ? (
            <button
              type="button"
              className="text-sm font-medium text-primary/70 transition hover:text-primary/90"
              onClick={() => {
                setEditingName(true);
                setEditName(category.name);
              }}
              title="Click to rename"
            >
              {category.name}
            </button>
          ) : (
            <span className="text-sm font-medium text-primary/70">
              {category.name}
            </span>
          )}
          {category.isSystem && (
            <Badge variant="outline" className="px-1.5 py-0 text-[9px]">
              System
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-primary/40">
            {templates.length} template{templates.length !== 1 ? "s" : ""}
          </span>
          {!category.isSystem && !isUncategorized && canEdit && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 text-xs text-red-500 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="size-3" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete "{category.name}"?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {templates.length > 0
                      ? `This category contains ${templates.length} template${templates.length !== 1 ? "s" : ""}. They will be moved to "Uncategorised".`
                      : "This category is empty and will be permanently deleted."}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteCategory}
                    className={buttonVariants({ variant: "destructive" })}
                  >
                    Delete Category
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      {!collapsed && (
        <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {templateTypes.map((type, idx) => {
            const tpl = templates[idx];
            const tplId = tpl?._id as Id<"precedentTemplates"> | undefined;
            return (
              <TemplateCard
                key={type.key}
                type={type}
                template={tpl}
                orgId={orgId}
                brandingProfile={brandingProfile}
                canEdit={canEdit}
                selectionMode={selectionMode}
                isSelected={!!tplId && selectedTemplateIds.has(tplId)}
                onToggleSelect={tplId ? () => onToggleSelect(tplId) : undefined}
              />
            );
          })}
          {templates.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-6 text-center">
              <FileText className="size-8 text-primary/15" />
              <p className="mt-2 text-xs text-primary/40">
                No templates in this category
              </p>
              {canEdit && (
                <p className="text-[10px] text-primary/30">
                  Use bulk upload to add templates
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template Name Editor — settings-tab inline rename
// ---------------------------------------------------------------------------

function TemplateNameEditor({
  templateId,
  name,
}: {
  templateId: Id<"precedentTemplates">;
  name: string;
}) {
  const [value, setValue] = useState(name);
  const [saving, setSaving] = useState(false);
  const { mutateAsync: renameTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.renameTemplate,
    ),
  });

  useEffect(() => {
    setValue(name);
  }, [name]);

  const trimmed = value.trim();
  const isDirty = trimmed.length > 0 && trimmed !== name;

  const handleSave = useCallback(async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      await renameTemplate({ templateId, name: trimmed });
      toast.success("Template renamed");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to rename template"));
      setValue(name);
    } finally {
      setSaving(false);
    }
  }, [isDirty, renameTemplate, templateId, trimmed, name]);

  return (
    <div>
      <h4 className="text-sm font-medium text-primary/80">Template Name</h4>
      <p className="mt-1 text-xs text-primary/40">
        You can update the name of this template. Name changes will not affect
        references or linked precedents.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSave();
            }
          }}
          placeholder="Template name"
          className="h-9"
        />
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="h-9 gap-1"
        >
          {saving && <Loader2 className="size-3.5 animate-spin" />}
          Save
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Job Status Tag Editor — inline checkboxes for status-driven auto-selection
// ---------------------------------------------------------------------------

function JobStatusTagEditor({
  currentTags,
  onTagsChange,
}: {
  currentTags: string[];
  onTagsChange: (update: string[] | ((prev: string[]) => string[])) => void;
}) {
  const toggle = useCallback(
    (tagValue: string) => {
      onTagsChange((prev) =>
        prev.includes(tagValue)
          ? prev.filter((t) => t !== tagValue)
          : [...prev, tagValue],
      );
    },
    [onTagsChange],
  );

  return (
    <div className="rounded-lg border border-border">
      <div className="grid grid-cols-2 gap-1 p-2">
        {JOB_STATUS_TAGS.map((opt) => {
          const checked = currentTags.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              className={cn(
                "flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition",
                checked ? "bg-primary/5" : "hover:bg-secondary/80",
              )}
              onClick={() => toggle(opt.value)}
            >
              <div
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-sm border transition",
                  checked
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border",
                )}
              >
                {checked && <Check className="size-3" />}
              </div>
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact Role Tag Editor — pre-select templates per contact role
//
// Templates tagged with a contact role (e.g. `cr:liability` for creditors)
// "fan out" at generation time: the wizard creates one document per
// matching `jobContacts` entry instead of a single row. The editor
// lives in the Settings tab of the template preview dialog alongside
// `JobStatusTagEditor`.
// ---------------------------------------------------------------------------

function ContactRoleTagEditor({
  currentTags,
  onTagsChange,
  orgId,
}: {
  currentTags: string[];
  onTagsChange: (update: string[] | ((prev: string[]) => string[])) => void;
  orgId: Id<"organizations">;
}) {
  // Org-defined custom job-contact roles — selectable alongside the built-ins
  // so a template can fan out over e.g. "Tax Advisor" as well as "Director".
  const { data: customRoles } = useQuery({
    ...convexQuery(api.contacts.customRoles.listJobContactRoles, { orgId }),
  });

  const toggle = useCallback(
    (tagValue: string) => {
      onTagsChange((prev) =>
        prev.includes(tagValue)
          ? prev.filter((t) => t !== tagValue)
          : [...prev, tagValue],
      );
    },
    [onTagsChange],
  );

  const renderButton = (
    key: string,
    tagValue: string,
    label: string,
    Icon: LucideIcon,
  ) => {
    const checked = currentTags.includes(tagValue);
    return (
      <button
        key={key}
        type="button"
        className={cn(
          "flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition",
          checked ? "bg-primary/5" : "hover:bg-secondary/80",
        )}
        onClick={() => toggle(tagValue)}
      >
        <div
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-sm border transition",
            checked
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border",
          )}
        >
          {checked && <Check className="size-3" />}
        </div>
        <Icon className="size-3.5 text-primary/55" />
        <span>{label}</span>
      </button>
    );
  };

  return (
    <div className="rounded-lg border border-border">
      <div className="grid grid-cols-2 gap-1 p-2">
        {ROLE_ORDER.map((kind) => {
          const meta = ROLE_META[kind];
          return renderButton(
            kind,
            contactRoleTag(kind),
            meta.label,
            meta.icon,
          );
        })}
        {(customRoles ?? []).map((role) =>
          renderButton(
            role._id,
            jobContactCustomRoleTag(role._id),
            role.name,
            roleIconComponent(role.icon) ?? TagIcon,
          ),
        )}
      </div>
      {/* External administration narrowing. Restricts the roles ticked above
          to companies in external administration (e.g. Related Entity +
          this = one letter per related company under administration,
          addressed to its appointee via the External Administration fields).
          On its own it selects every such company. */}
      <div className="border-t border-border p-2">
        <p className="mb-1.5 px-1 text-[10px] font-medium uppercase tracking-wide text-primary/40">
          Restrict to external administration
        </p>
        <div className="grid grid-cols-1 gap-1">
          {renderButton(
            "__external_admin__",
            JOB_CONTACT_EXTERNAL_ADMIN_TAG,
            "Only companies in external administration",
            Landmark,
          )}
        </div>
        <p className="mt-1.5 px-1 text-[11px] leading-snug text-primary/40">
          Use with the &ldquo;External Administration&rdquo; contact fields to
          address the letter to the company&rsquo;s appointee and firm.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Firm Contact Category Editor — pre-select templates per firm-contact
// category. Mirrors `ContactRoleTagEditor` but writes `fc:*` tags and
// fans out over the org-wide `firmContacts` directory instead of the
// per-job `jobContacts`.
// ---------------------------------------------------------------------------

function FirmContactTagEditor({
  currentTags,
  onTagsChange,
  orgId,
}: {
  currentTags: string[];
  onTagsChange: (update: string[] | ((prev: string[]) => string[])) => void;
  orgId: Id<"organizations">;
}) {
  // Org-defined custom categories (MOR-914) — selectable alongside the
  // built-ins so day ones can fan out over them too.
  const { data: customCategories } = useQuery({
    ...convexQuery(api.contacts.customCategories.listFirmContactCategories, {
      orgId,
    }),
  });

  const toggleTag = useCallback(
    (tagValue: string) => {
      onTagsChange((prev) =>
        prev.includes(tagValue)
          ? prev.filter((t) => t !== tagValue)
          : [...prev, tagValue],
      );
    },
    [onTagsChange],
  );

  const renderCheckboxButton = (
    key: string,
    tagValue: string | null,
    label: string,
    Icon: LucideIcon | null,
    checkedOverride?: boolean,
    onClickOverride?: () => void,
  ) => {
    const checked =
      checkedOverride ?? (tagValue !== null && currentTags.includes(tagValue));
    return (
      <button
        key={key}
        type="button"
        className={cn(
          "flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition",
          checked ? "bg-primary/5" : "hover:bg-secondary/80",
        )}
        onClick={() =>
          onClickOverride ? onClickOverride() : tagValue && toggleTag(tagValue)
        }
      >
        <div
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-sm border transition",
            checked
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border",
          )}
        >
          {checked && <Check className="size-3" />}
        </div>
        {Icon && <Icon className="size-3.5 text-primary/55" />}
        <span>{label}</span>
      </button>
    );
  };

  const selectedStateCodes = currentTags
    .map((t) => parseFirmContactStateTag(t))
    .filter((s): s is string => s !== null);
  const anyStateSelected = selectedStateCodes.length > 0;
  const matchJobState = currentTags.some(isFirmContactJobStateTag);

  // The three state modes are mutually exclusive so the restriction reads
  // one way: all states, the job's own state, or a fixed set of states.
  const isStateTag = (t: string) =>
    parseFirmContactStateTag(t) !== null || isFirmContactJobStateTag(t);
  const clearStateTags = () =>
    onTagsChange((prev) => prev.filter((t) => !isStateTag(t)));
  const selectJobState = () =>
    onTagsChange((prev) =>
      matchJobState
        ? prev.filter((t) => !isFirmContactJobStateTag(t))
        : [...prev.filter((t) => !isStateTag(t)), FIRM_CONTACT_JOB_STATE_TAG],
    );
  const toggleFixedState = (tagValue: string) =>
    onTagsChange((prev) => {
      const withoutJob = prev.filter((t) => !isFirmContactJobStateTag(t));
      return withoutJob.includes(tagValue)
        ? withoutJob.filter((t) => t !== tagValue)
        : [...withoutJob, tagValue];
    });

  const stateSummary = matchJobState
    ? "Job's state"
    : anyStateSelected
      ? `${selectedStateCodes.length} of ${AU_STATES.length} states`
      : "All states";
  const stateHelp = matchJobState
    ? "Only contacts whose address state matches the job's registered office (or principal place of business) are included. A job with no state recorded yet includes every state."
    : anyStateSelected
      ? `Only contacts with a ${selectedStateCodes.join(" / ")} address are included. Untick every state to cover all of them again.`
      : "No state ticked, so contacts in every state are included. Tick states only to narrow the categories above, or match the job's state so one template serves every jurisdiction.";

  return (
    <div className="rounded-lg border border-border">
      <div className="grid grid-cols-2 gap-1 p-2">
        {FIRM_CATEGORY_ORDER.map((category) => {
          const meta = FIRM_CATEGORY_META[category];
          return renderCheckboxButton(
            category,
            firmContactTag(category),
            meta.label,
            meta.icon,
          );
        })}
        {(customCategories ?? []).map((c) =>
          renderCheckboxButton(
            c._id,
            firmContactCustomCategoryTag(c._id),
            c.name,
            roleIconComponent(c.icon) ?? TagIcon,
          ),
        )}
      </div>
      {/* Address states (MOR-913). States NARROW the categories ticked above
          (e.g. Banks + NSW = one letter per NSW bank). Leaving every state
          unticked means all states, which the header states explicitly so the
          default isn't mistaken for "none selected = nothing matches". */}
      <div className="border-t border-border p-2">
        <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 px-1">
          <p className="text-[10px] font-medium uppercase tracking-wide text-primary/40">
            Restrict to address states
          </p>
          <span
            className={cn(
              "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
              anyStateSelected || matchJobState
                ? "bg-primary/10 text-primary/70"
                : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
            )}
          >
            {stateSummary}
          </span>
        </div>
        <p className="mb-2 px-1 text-[11px] leading-snug text-primary/40">
          {stateHelp}
        </p>
        <div className="grid grid-cols-4 gap-1">
          {/* "All states" clears the restriction — the same thing unticking
              every box does, surfaced as a one-click affordance. */}
          {renderCheckboxButton(
            "__all_states__",
            null,
            "All states",
            Globe,
            !anyStateSelected && !matchJobState,
            clearStateTags,
          )}
          {/* Dynamic option: resolve the state from the job at generate
              time, so a single template serves every jurisdiction. */}
          {renderCheckboxButton(
            "__job_state__",
            null,
            "Match job",
            MapPinned,
            matchJobState,
            selectJobState,
          )}
          {AU_STATES.map((s) => {
            const tagValue = firmContactStateTag(s.value);
            return renderCheckboxButton(
              s.value,
              tagValue,
              s.label,
              null,
              currentTags.includes(tagValue),
              () => toggleFixedState(tagValue),
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bulk Edit Dialog — edit category + tags for many templates at once
// ---------------------------------------------------------------------------

/**
 * Shape of a tag group as returned by `listTagGroups`. Kept local to the
 * docs settings page since the Convex query returns `v.any()` and the value
 * is only consumed here and in the BulkEditDialog.
 */
type TagGroup = {
  _id: Id<"templateTagGroups">;
  name: string;
  slug: string;
  options: Array<{ value: string; label: string }>;
};

type BulkChangeMode = "replace" | "add" | "remove";

const BULK_MODE_LABELS: Record<BulkChangeMode, string> = {
  replace: "Replace existing",
  add: "Add to existing",
  remove: "Remove from existing",
};

const BULK_MODE_TOOLTIPS: Record<BulkChangeMode, string> = {
  add: "Adds selected tag(s) to existing tags",
  remove: "Removes selected tag(s) from existing tags",
  replace: "Replaces existing tag(s) with selected tag(s)",
};

/**
 * Compact mode picker used inside each section of the bulk-edit dialog.
 * Three pill-style buttons that swap the change mode in place.
 */
function BulkModePicker({
  value,
  onChange,
  allowRemove = true,
}: {
  value: BulkChangeMode;
  onChange: (mode: BulkChangeMode) => void;
  allowRemove?: boolean;
}) {
  const modes: ReadonlyArray<BulkChangeMode> = allowRemove
    ? ["add", "remove", "replace"]
    : ["add", "replace"];
  return (
    <div className="inline-flex items-center rounded-md border border-border bg-secondary/30 p-0.5 text-[11px]">
      {modes.map((mode) => (
        <button
          key={mode}
          type="button"
          onClick={() => onChange(mode)}
          className={cn(
            "inline-flex items-center gap-1 rounded px-2 py-1 font-medium transition",
            value === mode
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-primary/55 hover:text-primary/80",
          )}
        >
          {BULK_MODE_LABELS[mode]}
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex"
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="size-3 opacity-40 transition-opacity hover:opacity-70" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {BULK_MODE_TOOLTIPS[mode]}
            </TooltipContent>
          </Tooltip>
        </button>
      ))}
    </div>
  );
}

/**
 * Renders a row of multi-select chips. Used by every tag section in the
 * bulk-edit dialog so the look stays consistent.
 */
function BulkTagChipPicker<T extends string>({
  options,
  selected,
  onToggle,
}: {
  options: ReadonlyArray<{ value: T; label: string }>;
  selected: ReadonlySet<T>;
  onToggle: (value: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = selected.has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(opt.value)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs transition",
              active
                ? "border-primary/40 bg-primary/10 text-primary"
                : "border-border text-primary/60 hover:border-primary/30 hover:bg-secondary/40",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Bulk-edit Letterhead picker — wraps the shared LetterheadOverridePicker
 * with its own data fetches (assets + variants + branding profile) so
 * the BulkEditDialog doesn't need to thread them in.
 */
function BulkLetterheadPicker({
  orgId,
  value,
  onChange,
}: {
  orgId: Id<"organizations">;
  value:
    | { kind: "none" }
    | { kind: "letterhead"; bundleName: string }
    | { kind: "variant"; variantId: Id<"letterheadVariants"> }
    | null;
  onChange: (
    next:
      | { kind: "none" }
      | { kind: "letterhead"; bundleName: string }
      | { kind: "variant"; variantId: Id<"letterheadVariants"> }
      | null,
  ) => void;
}) {
  const { data: brandingProfile } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, { orgId }),
    enabled: !!orgId,
  });
  const { data: variants } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const { data: assets } = useQuery({
    ...convexQuery(api.settings.branding.assetQueries.listAssetsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const bundleNames = useMemo<string[]>(() => {
    if (!assets) return [];
    const seen = new Set<string>();
    for (const a of assets) {
      const name = a.bundleName ?? a.sourceDocumentName ?? null;
      if (name) seen.add(name);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  return (
    <div className="space-y-2">
      <LetterheadOverridePicker
        currentOverride={value}
        firmDefaultBundleName={
          brandingProfile?.firmDefaultLetterheadBundleName ?? null
        }
        favouriteBundleNames={
          brandingProfile?.favouriteLetterheadBundleNames ?? []
        }
        allBundles={bundleNames}
        allVariants={variants ?? []}
        onChange={onChange}
        clearLabel="Use firm default (no override)"
        triggerTitle="Pick the letterhead to apply"
      />
      <p className="text-[11px] text-primary/40">
        {value
          ? "Saved override will replace any existing override on the selected templates."
          : "Picks the letterhead applied to every selected template. Choose 'Use firm default (no override)' to clear existing overrides."}
      </p>
    </div>
  );
}

function BulkEditDialog({
  open,
  onOpenChange,
  orgId,
  templateIds,
  categories,
  tagGroups,
  onApplied,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: Id<"organizations">;
  templateIds: ReadonlyArray<Id<"precedentTemplates">>;
  categories: ReadonlyArray<{
    _id: Id<"templateCategories">;
    name: string;
    isSystem?: boolean;
  }>;
  tagGroups: ReadonlyArray<TagGroup>;
  onApplied: () => void;
}) {
  // Each section is gated by an "update this dimension" toggle so the user
  // can apply changes to only the dimensions they care about.
  const [updateCategory, setUpdateCategory] = useState(false);
  const [categoryId, setCategoryId] = useState<Id<"templateCategories"> | "">(
    "",
  );

  const [updateAppointment, setUpdateAppointment] = useState(false);
  const [appointmentMode, setAppointmentMode] = useState<BulkChangeMode>("add");
  const [appointmentValues, setAppointmentValues] = useState<Set<string>>(
    () => new Set(),
  );

  const [updateStatus, setUpdateStatus] = useState(false);
  const [statusMode, setStatusMode] = useState<BulkChangeMode>("add");
  const [statusValues, setStatusValues] = useState<Set<JobStatus>>(
    () => new Set(),
  );

  const [updateContactRole, setUpdateContactRole] = useState(false);
  const [contactRoleMode, setContactRoleMode] = useState<BulkChangeMode>("add");
  const [contactRoleValues, setContactRoleValues] = useState<Set<ContactRole>>(
    () => new Set(),
  );

  const [updateCustom, setUpdateCustom] = useState(false);
  const [customMode, setCustomMode] = useState<BulkChangeMode>("add");
  // Selected custom-tag values are stored as fully-qualified `tg:slug:value`
  // strings so they're already in the format the backend expects.
  const [customValues, setCustomValues] = useState<Set<string>>(
    () => new Set(),
  );

  // Letterhead section state. The override is a tagged union; we store it
  // as a single state slot so the picker can swap it atomically.
  type BulkLetterheadOverride =
    | { kind: "none" }
    | { kind: "letterhead"; bundleName: string }
    | { kind: "variant"; variantId: Id<"letterheadVariants"> }
    | null;
  const [updateLetterhead, setUpdateLetterhead] = useState(false);
  const [letterheadValue, setLetterheadValue] =
    useState<BulkLetterheadOverride>(null);

  // Reset all editor state whenever the dialog closes so the next open
  // starts clean. Memoised by the boolean so it only fires on transitions.
  useEffect(() => {
    if (!open) {
      setUpdateCategory(false);
      setCategoryId("");
      setUpdateAppointment(false);
      setAppointmentMode("add");
      setAppointmentValues(new Set());
      setUpdateStatus(false);
      setStatusMode("add");
      setStatusValues(new Set());
      setUpdateContactRole(false);
      setContactRoleMode("add");
      setContactRoleValues(new Set());
      setUpdateCustom(false);
      setCustomMode("add");
      setCustomValues(new Set());
      setUpdateLetterhead(false);
      setLetterheadValue(null);
    }
  }, [open]);

  const { mutateAsync: bulkUpdate, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.bulkUpdateTemplates,
    ),
  });

  const toggleAppointment = useCallback((value: string) => {
    setAppointmentValues((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const toggleStatus = useCallback((value: JobStatus) => {
    setStatusValues((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const toggleContactRole = useCallback((value: ContactRole) => {
    setContactRoleValues((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const toggleCustom = useCallback((value: string) => {
    setCustomValues((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const hasAnyChange =
    (updateCategory && !!categoryId) ||
    updateAppointment ||
    updateStatus ||
    updateContactRole ||
    updateCustom ||
    updateLetterhead;

  const handleApply = useCallback(async () => {
    if (templateIds.length === 0 || !hasAnyChange) return;
    const total = templateIds.length;
    try {
      const { updated } = await bulkUpdate({
        templateIds: [...templateIds],
        categoryId:
          updateCategory && categoryId
            ? (categoryId as Id<"templateCategories">)
            : undefined,
        appointmentTags: updateAppointment
          ? { mode: appointmentMode, values: [...appointmentValues] }
          : undefined,
        statusTags: updateStatus
          ? {
              mode: statusMode,
              values: [...statusValues].map(jobStatusTag),
            }
          : undefined,
        contactRoleTags: updateContactRole
          ? {
              mode: contactRoleMode,
              values: [...contactRoleValues].map(contactRoleTag),
            }
          : undefined,
        customTags: updateCustom
          ? { mode: customMode, values: [...customValues] }
          : undefined,
        letterheadOverride: updateLetterhead ? letterheadValue : undefined,
      });
      if (updated === 0) {
        toast.info(
          `All ${total} selected template${total === 1 ? " was" : "s were"} already up to date`,
        );
      } else if (updated < total) {
        toast.success(
          `Updated ${updated} of ${total} templates (the rest were already up to date)`,
        );
      } else {
        toast.success(`Updated ${updated} template${updated === 1 ? "" : "s"}`);
      }
      onApplied();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to apply changes"));
    }
  }, [
    templateIds,
    hasAnyChange,
    bulkUpdate,
    updateCategory,
    categoryId,
    updateAppointment,
    appointmentMode,
    appointmentValues,
    updateStatus,
    statusMode,
    statusValues,
    updateContactRole,
    contactRoleMode,
    contactRoleValues,
    updateCustom,
    customMode,
    customValues,
    updateLetterhead,
    letterheadValue,
    onApplied,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Edit {templateIds.length} template
            {templateIds.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>Select what element to update.</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          {/* Category */}
          <BulkSection
            icon={<FolderInput className="size-3.5" />}
            title="Category"
            enabled={updateCategory}
            onEnabledChange={setUpdateCategory}
          >
            <Select
              value={categoryId === "" ? undefined : (categoryId as string)}
              onValueChange={(v) =>
                setCategoryId(v as Id<"templateCategories">)
              }
              disabled={!updateCategory}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue placeholder="Choose a category…" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c._id} value={c._id as string}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </BulkSection>

          {/* Appointment Type tags */}
          <BulkSection
            icon={<Tags className="size-3.5" />}
            title="Appointment Types"
            enabled={updateAppointment}
            onEnabledChange={setUpdateAppointment}
            mode={appointmentMode}
            onModeChange={setAppointmentMode}
          >
            <BulkTagChipPicker
              options={[
                { value: TAG_ALL, label: "All Jobs" },
                ...APPOINTMENT_TYPE_TAGS,
              ]}
              selected={appointmentValues}
              onToggle={toggleAppointment}
            />
          </BulkSection>

          {/* Job status tags */}
          <BulkSection
            icon={<Tags className="size-3.5" />}
            title="Job Statuses"
            enabled={updateStatus}
            onEnabledChange={setUpdateStatus}
            mode={statusMode}
            onModeChange={setStatusMode}
          >
            <BulkTagChipPicker<JobStatus>
              options={JOB_STATUS_TAGS.map((t) => ({
                value: t.status,
                label: t.label,
              }))}
              selected={statusValues}
              onToggle={toggleStatus}
            />
          </BulkSection>

          {/* Contact role tags — fan-out per matching contact */}
          <BulkSection
            icon={<Tags className="size-3.5" />}
            title="Contact Roles"
            enabled={updateContactRole}
            onEnabledChange={setUpdateContactRole}
            mode={contactRoleMode}
            onModeChange={setContactRoleMode}
          >
            <BulkTagChipPicker<ContactRole>
              options={ROLE_ORDER.map((kind) => ({
                value: kind,
                label: ROLE_META[kind].label,
              }))}
              selected={contactRoleValues}
              onToggle={toggleContactRole}
            />
          </BulkSection>

          {/* Custom tag groups */}
          <BulkSection
            icon={<Tags className="size-3.5" />}
            title="Custom Tags"
            enabled={updateCustom}
            onEnabledChange={setUpdateCustom}
            mode={customMode}
            onModeChange={setCustomMode}
          >
            {tagGroups.length === 0 ? (
              <p className="text-xs text-primary/40">
                No custom tag groups yet — create one in any template's Settings
                tab first.
              </p>
            ) : (
              <div className="space-y-3">
                {tagGroups.map((group) => (
                  <div key={group._id}>
                    <div className="mb-1.5 text-[11px] font-medium text-primary/55">
                      {group.name}
                    </div>
                    <BulkTagChipPicker
                      options={group.options.map((opt) => ({
                        value: `${TAG_GROUP_PREFIX}${group.slug}:${opt.value}`,
                        label: opt.label,
                      }))}
                      selected={customValues}
                      onToggle={toggleCustom}
                    />
                  </div>
                ))}
              </div>
            )}
          </BulkSection>

          {/* Letterhead — applies the same override to every selected
           *  template. Reuses the same picker as the per-template
           *  settings tab and the per-document picker. */}
          <BulkSection
            icon={<FileText className="size-3.5" />}
            title="Letterhead"
            enabled={updateLetterhead}
            onEnabledChange={setUpdateLetterhead}
          >
            <BulkLetterheadPicker
              orgId={orgId}
              value={letterheadValue}
              onChange={setLetterheadValue}
            />
          </BulkSection>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!hasAnyChange || isPending || templateIds.length === 0}
            className="gap-1.5"
          >
            {isPending && <Loader2 className="size-3.5 animate-spin" />}
            Apply to {templateIds.length} template
            {templateIds.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reusable card-like wrapper for each section in the bulk-edit dialog.
 * Provides a header with the enable-toggle and (optional) mode picker, and
 * dims its body when the section is disabled.
 */
function BulkSection({
  icon,
  title,
  enabled,
  onEnabledChange,
  mode,
  onModeChange,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  mode?: BulkChangeMode;
  onModeChange?: (mode: BulkChangeMode) => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border border-border/60 px-4 py-3",
        !enabled && "bg-secondary/20",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <label className="flex cursor-pointer items-center gap-2">
          <Checkbox
            checked={enabled}
            onCheckedChange={(v) => onEnabledChange(v === true)}
          />
          <span className="flex items-center gap-1.5 text-sm font-medium text-primary/80">
            {icon}
            {title}
          </span>
        </label>
        {mode !== undefined && onModeChange && enabled && (
          <BulkModePicker value={mode} onChange={onModeChange} />
        )}
      </div>
      {enabled && <div className="mt-3">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings Tag Editor — inline checkboxes for generation-inclusion tags
// ---------------------------------------------------------------------------

function SettingsTagEditor({
  currentTags,
  onTagsChange,
}: {
  currentTags: string[];
  onTagsChange: (update: string[] | ((prev: string[]) => string[])) => void;
}) {
  const toggleTag = useCallback(
    (tagValue: string) => {
      onTagsChange((prev) => {
        // Only ever touch this editor's own namespace (TAG_ALL + the
        // appointment-type tags). Job status, contact and custom-group tags
        // live in the same flat array and must survive untouched (MOR-921).
        const owned = new Set<string>([
          TAG_ALL,
          ...APPOINTMENT_TYPE_TAGS.map((a) => a.value),
        ]);
        const foreign = prev.filter((t) => !owned.has(t));
        const mine = prev.filter((t) => owned.has(t));

        // "All" is exclusive within this namespace.
        const nextMine: string[] =
          tagValue === TAG_ALL
            ? mine.includes(TAG_ALL)
              ? []
              : [TAG_ALL]
            : (() => {
                const without = mine.filter(
                  (t) => t !== TAG_ALL && t !== tagValue,
                );
                return mine.includes(tagValue)
                  ? without
                  : [...without, tagValue];
              })();
        return [...foreign, ...nextMine];
      });
    },
    [onTagsChange],
  );

  const hasAll = currentTags.includes(TAG_ALL);

  return (
    <div className="space-y-3">
      {/* "All Jobs" toggle — prominent at the top */}
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-3 rounded-lg border p-3 text-left transition",
          hasAll
            ? "border-primary/30 bg-primary/5"
            : "border-border hover:border-border/80",
        )}
        onClick={() => toggleTag(TAG_ALL)}
      >
        <div
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-sm border transition",
            hasAll
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border",
          )}
        >
          {hasAll && <Check className="size-3" />}
        </div>
        <div>
          <div className="text-sm font-medium text-primary/80">All Jobs</div>
          <div className="text-xs text-primary/40">
            Include this template in every document generation
          </div>
        </div>
      </button>

      {/* Individual appointment types */}
      <div className="rounded-lg border border-border">
        <div className="border-b border-border/50 px-3 py-2">
          <span className="text-xs font-medium text-primary/50">
            Or select specific job types:
          </span>
        </div>
        <div className="grid grid-cols-2 gap-1 p-2">
          {ALL_TAG_OPTIONS.filter((o) => o.value !== TAG_ALL).map((opt) => {
            const checked = currentTags.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                disabled={hasAll}
                className={cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition",
                  hasAll
                    ? "cursor-not-allowed opacity-40"
                    : checked
                      ? "bg-primary/5"
                      : "hover:bg-secondary/80",
                )}
                onClick={() => toggleTag(opt.value)}
              >
                <div
                  className={cn(
                    "flex size-4 shrink-0 items-center justify-center rounded-sm border transition",
                    checked || hasAll
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border",
                  )}
                >
                  {(checked || hasAll) && <Check className="size-3" />}
                </div>
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category Reassign — dropdown to move a template to a different category
// ---------------------------------------------------------------------------

function CategoryReassign({
  templateId,
  categoryId,
  orgId,
}: {
  templateId: Id<"precedentTemplates">;
  categoryId?: Id<"templateCategories">;
  orgId: Id<"organizations">;
}) {
  const { data: categories } = useQuery({
    ...convexQuery(api.settings.precedents.categories.listCategories, {
      orgId,
    }),
  });
  const { mutateAsync: updateCategory } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.updateTemplateCategory,
    ),
  });

  const handleChange = useCallback(
    async (newCategoryId: string) => {
      try {
        await updateCategory({
          templateId,
          categoryId: newCategoryId as Id<"templateCategories">,
        });
        toast.success("Template moved to new category");
      } catch {
        toast.error("Failed to move template");
      }
    },
    [templateId, updateCategory],
  );

  if (!categories || categories.length === 0) return null;

  return (
    <div>
      <h4 className="text-sm font-medium text-primary/80">Category</h4>
      <p className="mt-1 text-xs text-primary/40">
        Move this template to a different category.
      </p>
      <div className="mt-3">
        <Select value={categoryId ?? ""} onValueChange={handleChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {(categories ?? []).map((cat) => (
              <SelectItem key={cat._id} value={cat._id}>
                {cat.name}
                {cat.isSystem && " (System)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TemplateLetterheadOverrideEditor — per-template letterhead picker.
// Consulted at render time between the per-document override and the
// firm default. Reuses the same dropdown component as the day-one page.
// ---------------------------------------------------------------------------

function TemplateLetterheadOverrideEditor({
  templateId,
  orgId,
  currentOverride,
}: {
  templateId: Id<"precedentTemplates">;
  orgId: Id<"organizations">;
  currentOverride:
    | { kind: "none" }
    | { kind: "letterhead"; bundleName: string }
    | { kind: "variant"; variantId: Id<"letterheadVariants"> }
    | null;
}) {
  const { data: brandingProfile } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, { orgId }),
    enabled: !!orgId,
  });
  const { data: variants } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const { data: assets } = useQuery({
    ...convexQuery(api.settings.branding.assetQueries.listAssetsForOrg, {
      orgId,
    }),
    enabled: !!orgId,
  });

  const bundleNames = useMemo<string[]>(() => {
    if (!assets) return [];
    const seen = new Set<string>();
    for (const a of assets) {
      const name = a.bundleName ?? a.sourceDocumentName ?? null;
      if (name) seen.add(name);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  const { mutateAsync: setTemplateLetterheadOverride } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.setTemplateLetterheadOverride,
    ),
  });

  return (
    <div>
      <h4 className="text-sm font-medium text-primary/80">Letterhead</h4>
      <p className="mt-1 text-xs text-primary/40">
        Optional: select a letterhead to apply by default to the precedent.
        Letterheads can be updated during document generation within a job
        workspace. When unselected, your default letterhead will apply.
      </p>
      <div className="mt-3">
        <LetterheadOverridePicker
          currentOverride={currentOverride}
          firmDefaultBundleName={
            brandingProfile?.firmDefaultLetterheadBundleName ?? null
          }
          favouriteBundleNames={
            brandingProfile?.favouriteLetterheadBundleNames ?? []
          }
          allBundles={bundleNames}
          allVariants={variants ?? []}
          onChange={async (override) => {
            try {
              await setTemplateLetterheadOverride({
                templateId,
                override,
              });
            } catch (err) {
              toast.error(
                extractErrorMessage(err, "Failed to set template letterhead"),
              );
            }
          }}
        />
      </div>
    </div>
  );
}

/**
 * Per-template opt-out from the firm's footer page numbering.
 *
 * Purpose: numbering is a firm-wide branding setting, but some documents
 * shouldn't carry it (single-page notices, forms whose own footer already
 * prints a number). Mirrors the letterhead override in living under the
 * template's General settings.
 *
 * Only an opt-out — the format, alignment and pages stay firm-wide, so
 * documents from one firm number consistently. When the firm hasn't enabled
 * numbering the toggle is disabled, since there'd be nothing to turn off.
 */
function TemplatePageNumberingEditor({
  templateId,
  orgId,
  suppressed,
}: {
  templateId: Id<"precedentTemplates">;
  orgId: Id<"organizations">;
  suppressed: boolean;
}) {
  const { data: brandingProfile } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, { orgId }),
    enabled: !!orgId,
  });

  const { mutateAsync: setTemplatePageNumbering } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.setTemplatePageNumbering,
    ),
  });

  // Read the firm setting directly rather than via resolvePageNumbering, which
  // folds in this template's suppression and so can't tell the two apart.
  const firmEnabled = !!brandingProfile?.pageNumbering?.enabled;

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-medium text-primary/80">Page numbers</h4>
          <p className="mt-1 text-xs text-primary/40">
            {firmEnabled
              ? "Your firm adds page numbers to the footer of generated documents. Turn this off to leave them off this precedent."
              : "Your firm doesn't add page numbers to generated documents. Turn them on in Branding settings to use them."}
          </p>
        </div>
        <Switch
          checked={firmEnabled && !suppressed}
          disabled={!firmEnabled}
          onCheckedChange={async (checked) => {
            try {
              await setTemplatePageNumbering({
                templateId,
                suppress: !checked,
              });
              toast.success(
                checked
                  ? "Page numbers on for this precedent."
                  : "Page numbers off for this precedent.",
              );
            } catch (err) {
              toast.error(
                extractErrorMessage(err, "Failed to update page numbers"),
              );
            }
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom Tag Group Editor — manage and tag templates with custom tag groups
// ---------------------------------------------------------------------------

function CustomTagGroupEditor({
  currentTags,
  onTagsChange,
  orgId,
}: {
  currentTags: string[];
  onTagsChange: (update: string[] | ((prev: string[]) => string[])) => void;
  orgId: Id<"organizations">;
}) {
  const { data: tagGroups } = useQuery({
    ...convexQuery(api.settings.precedents.tagGroups.listTagGroups, { orgId }),
  });

  const { mutateAsync: createTagGroup } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.tagGroups.createTagGroup,
    ),
  });

  const { mutateAsync: updateTagGroup } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.tagGroups.updateTagGroup,
    ),
  });

  const { mutateAsync: deleteTagGroup } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.tagGroups.deleteTagGroup,
    ),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newOptions, setNewOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [newOptionLabel, setNewOptionLabel] = useState("");

  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editOptionLabel, setEditOptionLabel] = useState("");

  const toggleCustomTag = useCallback(
    (groupSlug: string, optionValue: string) => {
      const tagValue = `${TAG_GROUP_PREFIX}${groupSlug}:${optionValue}`;
      onTagsChange((prev) =>
        prev.includes(tagValue)
          ? prev.filter((t) => t !== tagValue)
          : [...prev, tagValue],
      );
    },
    [onTagsChange],
  );

  const handleCreateGroup = useCallback(async () => {
    if (!newGroupName.trim() || newOptions.length === 0) return;
    try {
      await createTagGroup({
        orgId,
        name: newGroupName.trim(),
        options: newOptions,
      });
      setNewGroupName("");
      setNewOptions([]);
      setShowCreate(false);
      toast.success("Tag group created");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to create tag group"));
    }
  }, [newGroupName, newOptions, orgId, createTagGroup]);

  const handleAddOption = useCallback(() => {
    const label = newOptionLabel.trim();
    if (!label) return;
    const value = label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    if (newOptions.some((o) => o.value === value)) {
      toast.error("Option already exists");
      return;
    }
    setNewOptions((prev) => [...prev, { value, label }]);
    setNewOptionLabel("");
  }, [newOptionLabel, newOptions]);

  const handleAddOptionToExisting = useCallback(
    async (
      groupId: string,
      groupOptions: Array<{ value: string; label: string }>,
    ) => {
      const label = editOptionLabel.trim();
      if (!label) return;
      const value = label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");
      if (groupOptions.some((o) => o.value === value)) {
        toast.error("Option already exists");
        return;
      }
      try {
        await updateTagGroup({
          groupId: groupId as Id<"templateTagGroups">,
          options: [...groupOptions, { value, label }],
        });
        setEditOptionLabel("");
        toast.success("Option added");
      } catch {
        toast.error("Failed to add option");
      }
    },
    [editOptionLabel, updateTagGroup],
  );

  const handleRemoveOptionFromGroup = useCallback(
    async (
      groupId: string,
      groupSlug: string,
      groupOptions: Array<{ value: string; label: string }>,
      removeValue: string,
    ) => {
      try {
        await updateTagGroup({
          groupId: groupId as Id<"templateTagGroups">,
          options: groupOptions.filter((o) => o.value !== removeValue),
        });
        const tagToRemove = `${TAG_GROUP_PREFIX}${groupSlug}:${removeValue}`;
        onTagsChange((prev) =>
          prev.includes(tagToRemove)
            ? prev.filter((t) => t !== tagToRemove)
            : prev,
        );
      } catch {
        toast.error("Failed to remove option");
      }
    },
    [updateTagGroup, onTagsChange],
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-primary/80">
            Custom Tag Groups
          </h4>
          <p className="mt-1 text-xs text-primary/40">
            Apply custom tags to your template. If custom tags are selected, the
            template will be auto-suggested when a job has matching custom tags.
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {/* Existing tag groups */}
        {tagGroups?.map((group: TagGroup) => {
          const isEditing = editingGroupId === group._id;
          return (
            <div
              key={group._id}
              className="rounded-lg border border-border p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <h5 className="text-sm font-medium text-primary/70">
                  {group.name}
                </h5>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() =>
                      setEditingGroupId(isEditing ? null : group._id)
                    }
                  >
                    <Pencil className="mr-1 size-3" />
                    {isEditing ? "Done" : "Edit"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-red-500 hover:text-red-600"
                    onClick={async () => {
                      try {
                        await deleteTagGroup({ groupId: group._id });
                        toast.success("Tag group deleted");
                      } catch (err) {
                        toast.error(
                          extractErrorMessage(err, "Failed to delete"),
                        );
                      }
                    }}
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </div>
              </div>

              {/* Options as checkboxes */}
              <div className="mt-2 flex flex-wrap gap-2">
                {group.options.map((opt: { value: string; label: string }) => {
                  const tagValue = `${TAG_GROUP_PREFIX}${group.slug}:${opt.value}`;
                  const checked = currentTags.includes(tagValue);
                  return (
                    <div key={opt.value} className="flex items-center gap-1.5">
                      <button
                        type="button"
                        className={cn(
                          "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition",
                          checked
                            ? "border-primary/30 bg-primary/5 text-primary/80"
                            : "border-border text-primary/50 hover:border-border/80",
                        )}
                        onClick={() => toggleCustomTag(group.slug, opt.value)}
                      >
                        <div
                          className={cn(
                            "flex size-3.5 shrink-0 items-center justify-center rounded-sm border transition",
                            checked
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border",
                          )}
                        >
                          {checked && <Check className="size-2.5" />}
                        </div>
                        {opt.label}
                      </button>
                      {isEditing && (
                        <button
                          type="button"
                          className="text-red-400 transition hover:text-red-600"
                          onClick={() =>
                            handleRemoveOptionFromGroup(
                              group._id,
                              group.slug,
                              group.options,
                              opt.value,
                            )
                          }
                        >
                          <Trash2 className="size-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Add option (when editing) */}
              {isEditing && (
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    value={editOptionLabel}
                    onChange={(e) => setEditOptionLabel(e.target.value)}
                    placeholder="New option label..."
                    className="h-7 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddOptionToExisting(group._id, group.options);
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() =>
                      handleAddOptionToExisting(group._id, group.options)
                    }
                    disabled={!editOptionLabel.trim()}
                  >
                    <Plus className="mr-1 size-3" />
                    Add
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        {/* Create new tag group */}
        {showCreate ? (
          <div className="space-y-3 rounded-lg border border-dashed border-primary/30 bg-primary/[0.02] p-4">
            <div>
              <Label className="text-xs">Group Name</Label>
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="e.g. Company Type"
                className="mt-1 h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Options</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {newOptions.map((opt) => (
                  <Badge
                    key={opt.value}
                    variant="secondary"
                    className="gap-1 text-xs"
                  >
                    {opt.label}
                    <button
                      type="button"
                      onClick={() =>
                        setNewOptions((prev) =>
                          prev.filter((o) => o.value !== opt.value),
                        )
                      }
                      className="ml-0.5 text-primary/40 hover:text-primary/60"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <Input
                  value={newOptionLabel}
                  onChange={(e) => setNewOptionLabel(e.target.value)}
                  placeholder="Option label..."
                  className="h-7 text-xs"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleAddOption}
                  disabled={!newOptionLabel.trim()}
                >
                  <Plus className="mr-1 size-3" />
                  Add
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="h-7 text-xs"
                onClick={handleCreateGroup}
                disabled={!newGroupName.trim() || newOptions.length === 0}
              >
                Create Group
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setShowCreate(false);
                  setNewGroupName("");
                  setNewOptions([]);
                  setNewOptionLabel("");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => setShowCreate(true)}
          >
            <Plus className="size-3.5" />
            New Tag Group
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template Card
// ---------------------------------------------------------------------------

function TemplateCard({
  type,
  template,
  orgId,
  brandingProfile,
  canEdit,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
}: {
  type: TemplateTypeMeta;
  template: PrecedentTemplateRow | undefined;
  orgId: Id<"organizations">;
  brandingProfile: PreviewBrandingProfile | null | undefined;
  canEdit: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState<string>(
    type.shortName ?? type.name ?? "",
  );
  const posthog = usePostHog();

  // Selection mode disables drag-and-drop entirely so the user can freely
  // click on cards to toggle selection without accidentally starting a drag.
  const isDraggable = !!template?._id && canEdit;
  const dragEnabled = isDraggable && !selectionMode;
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: template?._id ?? `card-${type.key}`,
    data: {
      templateId: template?._id,
      templateName: type.shortName ?? type.name,
      categoryId: template?.categoryId,
    },
    disabled: !dragEnabled,
  });

  const generateUploadUrl = useConvexMutation(
    api.settings.precedents.mutations.generateUploadUrl,
  );
  const { mutateAsync: uploadTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.uploadTemplate,
    ),
  });
  const { mutateAsync: deleteTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.deleteTemplate,
    ),
  });
  const { mutateAsync: retryExtraction } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.retryExtraction,
    ),
  });
  const { mutateAsync: renameTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.renameTemplate,
    ),
  });
  const { mutateAsync: syncFromMaster, isPending: syncing } = useMutation({
    mutationFn: useConvexAction(
      api.settings.precedents.masterActions.syncFromMaster,
    ),
  });

  // ARITA master-library link status (only present on firm copies).
  const masterLink = template?.masterLink ?? {
    isArita: false,
    modifiedLocally: false,
    updateAvailable: false,
  };
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);

  const handleSync = useCallback(async () => {
    if (!template?._id) return;
    try {
      await syncFromMaster({ orgId, templateIds: [template._id] });
      toast.success("Synced latest from ARITA");
      setSyncConfirmOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to sync from ARITA"));
    }
  }, [template?._id, syncFromMaster, orgId]);

  const displayName = type.shortName ?? type.name ?? "";

  // Keep the inline rename buffer in sync if the underlying template name
  // changes (e.g. updated from another tab via the Settings dialog).
  useEffect(() => {
    if (!editingName) setEditName(displayName);
  }, [displayName, editingName]);

  const handleCommitRename = useCallback(async () => {
    setEditingName(false);
    const trimmed = editName.trim();
    if (!template?._id || !trimmed || trimmed === displayName) {
      setEditName(displayName);
      return;
    }
    try {
      await renameTemplate({ templateId: template._id, name: trimmed });
      toast.success("Template renamed");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to rename template"));
      setEditName(displayName);
    }
  }, [editName, template?._id, displayName, renameTemplate]);

  const handleConfirmDelete = useCallback(async () => {
    if (!template?._id) return;
    try {
      await deleteTemplate({ templateId: template._id });
      toast.success(`"${displayName}" deleted`);
      setDeleteDialogOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to delete template"));
    }
  }, [template?._id, deleteTemplate, displayName]);

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadBegin: () => setUploading(true),
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) fileInputRef.current.value = "";
      const storageId = (uploaded[0].response as { storageId: Id<"_storage"> })
        .storageId;
      await uploadTemplate({
        orgId,
        templateKey: type.key,
        category: template?.category ?? type.category ?? "user",
        name: type.name,
        storageId,
        fileName: uploaded[0].name ?? "document",
        isThirdPartyForm: type.thirdPartyForm,
      });
      setUploading(false);
    },
    onUploadError: () => setUploading(false),
  });

  const hasTemplate = !!template?.sourceDocumentStorageId;
  const status = template?.extractionStatus;
  const fieldCount = template?.extractedFields?.length ?? 0;
  const hasPdfMappings = !!template?.pdfFieldMappings?.length;
  const isCompleted = status === "completed";
  const isExtracting = status === "extracting" || status === "pending";
  const Icon = type.icon ?? FileText;
  const extractionError: string | undefined = template?.extractionError;
  const sourceFileName: string = template?.sourceDocumentName ?? type.name;

  // Watch for the extraction status flipping to "failed" so we can fire a
  // toast on both the initial upload path and any background re-extraction.
  // We track the previous status so a card mounting in a "failed" state
  // doesn't fire a stale toast on every page load.
  const prevStatusRef = useRef<typeof status>(undefined);
  useEffect(() => {
    const prev = prevStatusRef.current;
    if (prev && prev !== "failed" && status === "failed" && template?._id) {
      const friendlyMessage =
        extractionError ??
        "We couldn't read this template. Try re-saving it from Word, or upload a PDF version.";
      toast.error(friendlyMessage, {
        duration: 10000,
        action: {
          label: "View details",
          onClick: () => setErrorDialogOpen(true),
        },
      });
      posthog?.capture("template_extraction_failed", {
        templateId: template._id,
        templateKey: type.key,
        fileName: sourceFileName,
        error: extractionError,
        isThirdPartyForm: type.thirdPartyForm ?? false,
      });
    }
    prevStatusRef.current = status;
  }, [
    status,
    template?._id,
    extractionError,
    posthog,
    sourceFileName,
    type.key,
    type.thirdPartyForm,
  ]);

  const selectionEligible =
    selectionMode && !!onToggleSelect && !!template?._id;

  return (
    <div
      ref={dragEnabled ? setDragRef : undefined}
      className={cn(
        "group relative flex flex-col rounded-xl border transition",
        hasTemplate || type.hardcodedFields
          ? "cursor-pointer border-border/50 bg-card"
          : "border-dashed border-border/50 bg-secondary/20",
        isDragging && "opacity-40",
        selectionEligible && "cursor-pointer",
        isSelected && "ring-2 ring-primary ring-offset-1",
        selectionMode && "pointer-events-none",
      )}
      onClick={
        (hasTemplate || type.hardcodedFields) && !selectionMode
          ? () => setPreviewOpen(true)
          : undefined
      }
    >
      {/* Selection overlay — in selection mode a click anywhere toggles. */}
      {selectionEligible && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect?.();
          }}
          aria-pressed={isSelected}
          aria-label={isSelected ? "Deselect template" : "Select template"}
          className="pointer-events-auto absolute inset-0 z-30 cursor-pointer rounded-xl"
        />
      )}

      {/* Always-visible checkbox in top-right corner */}
      {!!template?._id && onToggleSelect && (
        <div className="absolute right-2 top-2 z-40">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect?.()}
            onClick={(e) => e.stopPropagation()}
            className="size-4 border-primary/30 bg-background"
          />
        </div>
      )}

      {/* Drag handle */}
      {dragEnabled && (
        <button
          type="button"
          className="absolute left-1.5 top-1.5 z-20 cursor-grab touch-none rounded p-0.5 text-primary/20 opacity-0 transition hover:text-primary/50 active:cursor-grabbing group-hover:opacity-100"
          onClick={(e) => e.stopPropagation()}
          {...listeners}
          {...attributes}
        >
          <GripVertical className="size-3.5" />
        </button>
      )}
      {/* Thumbnail */}
      <div className="relative flex h-28 items-center justify-center rounded-t-xl border-b border-border/50 bg-secondary/50 dark:bg-secondary/20">
        {isExtracting ? (
          <>
            <div className="flex flex-col items-center gap-1.5">
              <Loader2 className="size-6 animate-spin text-primary/30" />
              <span className="text-[10px] text-primary/40">Extracting...</span>
            </div>
            {canEdit && template?._id && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-t-xl bg-black/0 opacity-0 transition group-hover:bg-black/5 group-hover:opacity-100"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  aria-label="Delete template"
                  className="rounded-full bg-white p-2 shadow-md transition hover:bg-red-50"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="size-3.5 text-red-500" />
                </button>
              </div>
            )}
          </>
        ) : hasTemplate || type.hardcodedFields ? (
          <>
            <div className="flex flex-col items-center gap-1">
              <Icon className="size-6 text-primary/40" />
              {status === "failed" ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded text-[10px] font-medium text-red-500 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
                  onClick={(e) => {
                    e.stopPropagation();
                    setErrorDialogOpen(true);
                  }}
                  aria-label="View extraction error details"
                >
                  <AlertCircle className="size-3" />
                  Extraction failed
                </button>
              ) : type.hardcodedFields && !hasTemplate ? (
                <>
                  <span className="text-[10px] font-medium text-emerald-600">
                    Web Portal
                  </span>
                  <span className="text-[9px] text-primary/40">
                    {type.hardcodedFields.length} fields
                  </span>
                </>
              ) : (
                <span className="text-[10px] font-medium text-emerald-600">
                  {hasPdfMappings
                    ? `${template?.pdfFieldMappings?.length} mappings`
                    : `${fieldCount} fields`}
                </span>
              )}
            </div>
            <div
              className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-t-xl bg-black/0 opacity-0 transition group-hover:bg-black/5 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <PreviewDialog
                name={type.name}
                templateId={template?._id}
                templateKey={type.key}
                orgId={orgId}
                bodyHtml={template?.templateHtml}
                firmLetterheadFragments={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.firmLetterheadFragments
                }
                headerImageUrl={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.headerImageUrl
                }
                footerImageUrl={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.footerImageUrl
                }
                leftSideImageUrl={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.leftSideImageUrl
                }
                rightSideImageUrl={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.rightSideImageUrl
                }
                headerHtml={
                  type.thirdPartyForm ? undefined : brandingProfile?.headerHtml
                }
                footerHtml={
                  type.thirdPartyForm ? undefined : brandingProfile?.footerHtml
                }
                headerPageRule={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.headerPageRule
                }
                footerPageRule={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.footerPageRule
                }
                leftSidePageRule={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.leftSidePageRule
                }
                rightSidePageRule={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.rightSidePageRule
                }
                leftSideFit={
                  type.thirdPartyForm ? undefined : brandingProfile?.leftSideFit
                }
                rightSideFit={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.rightSideFit
                }
                leftSideTopBlockHeightPct={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.leftSideTopBlockHeightPct
                }
                rightSideTopBlockHeightPct={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.rightSideTopBlockHeightPct
                }
                leftSideCrop={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.leftSideCrop
                }
                rightSideCrop={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.rightSideCrop
                }
                isThirdPartyForm={type.thirdPartyForm}
                pdfUrl={template?.sourceDocumentUrl}
                sourceDocumentName={template?.sourceDocumentName}
                fields={template?.extractedFields ?? type.hardcodedFields}
                pdfFieldMappings={template?.pdfFieldMappings}
                fieldPositions={
                  template?.fieldPositions as FieldPosition[] | undefined
                }
                dateCalcs={
                  template?.dateCalcs as Record<string, DateCalc> | undefined
                }
                fieldConditions={
                  template?.fieldConditions as FieldConditionMap | undefined
                }
                brandingPadding={
                  type.thirdPartyForm ? undefined : brandingProfile
                }
                templatePadding={type.thirdPartyForm ? undefined : template}
                presetColors={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.editorPresetColors
                }
                defaultFontFamily={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.editorFontFamily
                }
                defaultLineHeight={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.editorLineHeight
                }
                bodyTextColor={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.bodyTextColor
                }
                defaultFontSize={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.editorFontSize
                }
                defaultParagraphSpacingBefore={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.editorParagraphSpacingBefore
                }
                defaultParagraphSpacingAfter={
                  type.thirdPartyForm
                    ? undefined
                    : brandingProfile?.editorParagraphSpacingAfter
                }
                pageNumbering={
                  type.thirdPartyForm
                    ? null
                    : resolvePageNumbering(brandingProfile, template)
                }
                suppressPageNumbering={template?.suppressPageNumbering}
                currentTags={template?.tags ?? []}
                letterheadOverride={template?.letterheadOverride ?? null}
                attachments={
                  (template?.attachments ?? []) as AttachmentRequirement[]
                }
                fieldConfig={
                  template?.fieldConfig as GallagherFieldConfig[] | undefined
                }
                categoryId={template?.categoryId}
                initialPreviewVariantSelections={
                  template?.previewVariantSelections
                }
                initialPreviewSnippetSelections={
                  template?.previewSnippetSelections
                }
                canEdit={canEdit}
              />
              {hasTemplate && canEdit && template?._id && (
                <button
                  type="button"
                  aria-label="Delete template"
                  className="rounded-full bg-white p-2 shadow-md transition hover:bg-red-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="size-3.5 text-red-500" />
                </button>
              )}
            </div>
          </>
        ) : canEdit ? (
          <label
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setIsFileDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsFileDragOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setIsFileDragOver(false);
              if (uploading) return;
              const files = Array.from(e.dataTransfer.files);
              if (files.length > 0) startUpload(files);
            }}
            className={cn(
              "flex h-full w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-t-xl transition hover:bg-primary/[0.03]",
              uploading && "pointer-events-none opacity-50",
              isFileDragOver &&
                "bg-primary/10 ring-2 ring-inset ring-primary/40",
            )}
          >
            {uploading ? (
              <Loader2 className="size-6 animate-spin text-primary/30" />
            ) : (
              <>
                <Upload className="size-5 text-primary/25" />
                <span className="text-[10px] text-primary/40">
                  {isFileDragOver ? "Drop to upload" : "Upload or drop file"}
                </span>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.dotx,.pptx"
              className="sr-only"
              onChange={async (e) => {
                if (!e.target.files?.length) return;
                startUpload(Array.from(e.target.files));
              }}
            />
          </label>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 rounded-t-xl text-primary/20">
            <Lock className="size-4" />
            <span className="text-[10px]">No upload</span>
          </div>
        )}

        {/* Status dot */}
        {(hasTemplate || type.hardcodedFields) && (
          <div
            className={cn(
              "absolute right-2 top-2 size-2 rounded-full",
              (isCompleted || type.hardcodedFields) && "bg-emerald-500",
              isExtracting && "animate-pulse bg-amber-500",
              status === "failed" && "bg-red-500",
            )}
          />
        )}
      </div>

      {/* Label + metadata */}
      <div className="flex flex-col gap-0.5 px-3 py-2">
        {editingName && canEdit && template?._id ? (
          <Input
            value={editName}
            autoFocus
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleCommitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setEditName(displayName);
                setEditingName(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="h-6 w-full px-1 py-0 text-xs"
          />
        ) : canEdit && template?._id ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditingName(true);
            }}
            title="Click to rename"
            className="truncate text-left text-xs font-medium text-primary/80 transition hover:text-primary"
          >
            {displayName}
          </button>
        ) : (
          <span className="truncate text-xs font-medium text-primary/80">
            {displayName}
          </span>
        )}
        <span className="truncate text-[10px] text-primary/40">
          {type.description}
        </span>

        {/* ARITA master-library link status */}
        {masterLink.isArita && (
          <div
            className="mt-1 flex flex-wrap items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Badge
              variant="secondary"
              className="bg-blue-100 text-[10px] text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
            >
              ARITA
            </Badge>
            {masterLink.modifiedLocally && (
              <Badge
                variant="secondary"
                className="bg-purple-100 text-[10px] text-purple-700 dark:bg-purple-950/40 dark:text-purple-300"
              >
                Firm Edited
              </Badge>
            )}
            {masterLink.updateAvailable && canEdit && (
              <button
                type="button"
                disabled={syncing}
                onClick={(e) => {
                  e.stopPropagation();
                  if (masterLink.modifiedLocally) setSyncConfirmOpen(true);
                  else handleSync();
                }}
                className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 transition hover:bg-amber-200 disabled:opacity-50 dark:bg-amber-950/40 dark:text-amber-300"
              >
                {syncing ? (
                  <Loader2 className="size-2.5 animate-spin" />
                ) : (
                  <RefreshCw className="size-2.5" />
                )}
                Update available
              </button>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={syncConfirmOpen} onOpenChange={setSyncConfirmOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Sync &ldquo;{displayName}&rdquo; from ARITA?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This template was edited after it was added from the ARITA
              library. Syncing will overwrite your changes with the latest
              master version. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSync}
              className={buttonVariants({ variant: "destructive" })}
            >
              Overwrite &amp; Sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete &ldquo;{displayName}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the template, its extracted fields, field
              mappings, attachments configuration, and the uploaded source file.
              Documents already generated for jobs are not affected.
              <br />
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className={buttonVariants({ variant: "destructive" })}
            >
              Delete Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ExtractionErrorDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        fileName={sourceFileName}
        errorMessage={extractionError}
        canRetry={!!template?._id && canEdit}
        onRetry={async () => {
          if (!template?._id) return;
          try {
            await retryExtraction({ templateId: template._id });
            toast.info("Retrying extraction…");
            setErrorDialogOpen(false);
          } catch (err) {
            const message =
              err instanceof Error ? err.message : "Failed to start retry";
            toast.error(message);
          }
        }}
        onReplace={
          canEdit
            ? () => {
                setErrorDialogOpen(false);
                fileInputRef.current?.click();
              }
            : undefined
        }
      />

      {/* Controlled preview dialog — opens on card body click */}
      {(hasTemplate || type.hardcodedFields) && (
        <PreviewDialog
          name={type.name}
          templateId={template?._id}
          templateKey={type.key}
          orgId={orgId}
          bodyHtml={template?.templateHtml}
          firmLetterheadFragments={
            type.thirdPartyForm
              ? undefined
              : brandingProfile?.firmLetterheadFragments
          }
          headerImageUrl={
            type.thirdPartyForm ? undefined : brandingProfile?.headerImageUrl
          }
          footerImageUrl={
            type.thirdPartyForm ? undefined : brandingProfile?.footerImageUrl
          }
          leftSideImageUrl={
            type.thirdPartyForm ? undefined : brandingProfile?.leftSideImageUrl
          }
          rightSideImageUrl={
            type.thirdPartyForm ? undefined : brandingProfile?.rightSideImageUrl
          }
          headerHtml={
            type.thirdPartyForm ? undefined : brandingProfile?.headerHtml
          }
          footerHtml={
            type.thirdPartyForm ? undefined : brandingProfile?.footerHtml
          }
          headerPageRule={
            type.thirdPartyForm ? undefined : brandingProfile?.headerPageRule
          }
          footerPageRule={
            type.thirdPartyForm ? undefined : brandingProfile?.footerPageRule
          }
          leftSidePageRule={
            type.thirdPartyForm ? undefined : brandingProfile?.leftSidePageRule
          }
          rightSidePageRule={
            type.thirdPartyForm ? undefined : brandingProfile?.rightSidePageRule
          }
          leftSideFit={
            type.thirdPartyForm ? undefined : brandingProfile?.leftSideFit
          }
          rightSideFit={
            type.thirdPartyForm ? undefined : brandingProfile?.rightSideFit
          }
          leftSideTopBlockHeightPct={
            type.thirdPartyForm
              ? undefined
              : brandingProfile?.leftSideTopBlockHeightPct
          }
          rightSideTopBlockHeightPct={
            type.thirdPartyForm
              ? undefined
              : brandingProfile?.rightSideTopBlockHeightPct
          }
          leftSideCrop={
            type.thirdPartyForm ? undefined : brandingProfile?.leftSideCrop
          }
          rightSideCrop={
            type.thirdPartyForm ? undefined : brandingProfile?.rightSideCrop
          }
          isThirdPartyForm={type.thirdPartyForm}
          pdfUrl={template?.sourceDocumentUrl}
          sourceDocumentName={template?.sourceDocumentName}
          fields={template?.extractedFields ?? type.hardcodedFields}
          pdfFieldMappings={template?.pdfFieldMappings}
          fieldPositions={
            template?.fieldPositions as FieldPosition[] | undefined
          }
          dateCalcs={
            template?.dateCalcs as Record<string, DateCalc> | undefined
          }
          fieldConditions={
            template?.fieldConditions as FieldConditionMap | undefined
          }
          brandingPadding={type.thirdPartyForm ? undefined : brandingProfile}
          templatePadding={type.thirdPartyForm ? undefined : template}
          presetColors={
            type.thirdPartyForm
              ? undefined
              : brandingProfile?.editorPresetColors
          }
          defaultFontFamily={
            type.thirdPartyForm ? undefined : brandingProfile?.editorFontFamily
          }
          defaultLineHeight={
            type.thirdPartyForm ? undefined : brandingProfile?.editorLineHeight
          }
          bodyTextColor={
            type.thirdPartyForm ? undefined : brandingProfile?.bodyTextColor
          }
          defaultFontSize={
            type.thirdPartyForm ? undefined : brandingProfile?.editorFontSize
          }
          defaultParagraphSpacingBefore={
            type.thirdPartyForm
              ? undefined
              : brandingProfile?.editorParagraphSpacingBefore
          }
          defaultParagraphSpacingAfter={
            type.thirdPartyForm
              ? undefined
              : brandingProfile?.editorParagraphSpacingAfter
          }
          pageNumbering={
            type.thirdPartyForm
              ? null
              : resolvePageNumbering(brandingProfile, template)
          }
          suppressPageNumbering={template?.suppressPageNumbering}
          currentTags={template?.tags ?? []}
          attachments={(template?.attachments ?? []) as AttachmentRequirement[]}
          fieldConfig={
            template?.fieldConfig as GallagherFieldConfig[] | undefined
          }
          categoryId={template?.categoryId}
          canEdit={canEdit}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Extraction Error Dialog
// ---------------------------------------------------------------------------

/**
 * Dialog that surfaces the persisted extraction error and gives the user two
 * recovery paths:
 *   1. Retry — re-runs the extraction action against the existing source file.
 *   2. Replace file — re-opens the upload picker so the user can swap in a
 *      cleaner copy (e.g. re-saved from Word, or a PDF version).
 */
function ExtractionErrorDialog({
  open,
  onOpenChange,
  fileName,
  errorMessage,
  canRetry,
  onRetry,
  onReplace,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileName: string;
  errorMessage: string | undefined;
  canRetry: boolean;
  onRetry: () => Promise<void> | void;
  /** Omit to hide the "Replace file" affordance (e.g. for read-only members). */
  onReplace?: () => void;
}) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="size-4 text-red-500" />
            Extraction failed
          </DialogTitle>
          <DialogDescription>
            We couldn't read{" "}
            <span className="font-medium text-primary/80">{fileName}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 text-sm">
          {errorMessage ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
              <span className="font-medium">Error:</span> {errorMessage}
            </div>
          ) : null}
          <div className="text-xs text-primary/60">
            Try one of the following:
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li>
                Open the file in Word and re-save it (this often rewrites
                non-standard structures).
              </li>
              <li>Export the file to PDF and upload that instead.</li>
              <li>
                Click <span className="font-medium">Retry</span> if the issue
                may have been transient.
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {onReplace && (
            <Button variant="outline" onClick={onReplace} type="button">
              <Upload className="mr-1.5 size-3.5" />
              Replace file
            </Button>
          )}
          <Button
            type="button"
            onClick={handleRetry}
            disabled={!canRetry || retrying}
          >
            {retrying ? (
              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-1.5 size-3.5" />
            )}
            Retry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Preview Dialog — PDF embed for standard forms, HTML for AI-extracted
// ---------------------------------------------------------------------------

/**
 * Replace any existing logo element in header HTML with the uploaded logo image.
 * Handles three cases in priority order:
 *  1. Existing <img> tags (from DOCX extraction with {{IMG:…}} placeholders)
 *  2. Text-based logo divs with large font spans (from PDF text approximation)
 *  3. Any large-font div/span (fallback text approximation match)
 * If none match, the logo is prepended.
 */

// Legend strip (`SourceLegend`) + `SOURCE_LEGEND` definitions moved to
// `src/components/precedents/SourceLegend.tsx` so the Map Fields tab in
// `PdfFieldEditor` can render the same legend without duplicating the data
// or the chip styling (MOR-462).

/** Nullable per-edge source-image crop bag for topBlock sides. */
type SideCropBag = {
  top?: number | null;
  bottom?: number | null;
  left?: number | null;
  right?: number | null;
} | null;

function PreviewDialog({
  name,
  templateId,
  templateKey,
  orgId,
  bodyHtml,
  firmLetterheadFragments,
  headerImageUrl,
  footerImageUrl,
  leftSideImageUrl,
  rightSideImageUrl,
  headerHtml,
  footerHtml,
  headerPageRule,
  footerPageRule,
  leftSidePageRule,
  rightSidePageRule,
  leftSideFit,
  rightSideFit,
  leftSideTopBlockHeightPct,
  rightSideTopBlockHeightPct,
  leftSideCrop,
  rightSideCrop,
  isThirdPartyForm,
  pdfUrl,
  sourceDocumentName,
  fields,
  pdfFieldMappings,
  fieldPositions,
  dateCalcs,
  fieldConditions,
  brandingPadding,
  templatePadding,
  presetColors,
  defaultFontFamily,
  defaultLineHeight,
  bodyTextColor,
  defaultFontSize,
  defaultParagraphSpacingBefore,
  defaultParagraphSpacingAfter,
  pageNumbering,
  suppressPageNumbering,
  currentTags,
  attachments,
  fieldConfig,
  categoryId,
  letterheadOverride,
  initialPreviewVariantSelections,
  initialPreviewSnippetSelections,
  canEdit: canEditSettings,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  name: string;
  templateId?: Id<"precedentTemplates">;
  templateKey: string;
  orgId: Id<"organizations">;
  bodyHtml?: string | null;
  /**
   * Full ordered letterhead fragments for the firm default (MOR-953) —
   * one per (zone, image, rule), so a first-page header paired with a
   * subsequent-pages header both render. The flat `headerImageUrl` /
   * `headerPageRule` props below carry only the page-1 image and remain
   * for the crop/scale controls and page-rule badges.
   */
  firmLetterheadFragments?: VariantFragments[] | null;
  headerImageUrl?: string | null;
  footerImageUrl?: string | null;
  /** Full-height left/right letterhead bands from the org branding profile. */
  leftSideImageUrl?: string | null;
  rightSideImageUrl?: string | null;
  headerHtml?: string | null;
  footerHtml?: string | null;
  headerPageRule?: string | null;
  footerPageRule?: string | null;
  leftSidePageRule?: string | null;
  rightSidePageRule?: string | null;
  /** Side fit — "topBlock" = partial-height block with body text wrap. */
  leftSideFit?: "fullHeight" | "topBlock" | null;
  rightSideFit?: "fullHeight" | "topBlock" | null;
  leftSideTopBlockHeightPct?: number | null;
  rightSideTopBlockHeightPct?: number | null;
  /** Source-image crop for topBlock sides (per-edge fractions). */
  leftSideCrop?: {
    top?: number | null;
    bottom?: number | null;
    left?: number | null;
    right?: number | null;
  } | null;
  rightSideCrop?: {
    top?: number | null;
    bottom?: number | null;
    left?: number | null;
    right?: number | null;
  } | null;
  isThirdPartyForm?: boolean;
  pdfUrl?: string | null;
  sourceDocumentName?: string | null;
  fields?: Array<{
    key: string;
    label: string;
    description?: string;
    exampleValue?: string;
    fieldType: string;
    canonicalKey?: string;
  }>;
  pdfFieldMappings?: Array<{
    pdfFieldName: string;
    canonicalKey?: string;
    fieldType: string;
  }>;
  fieldPositions?: FieldPosition[];
  /** Date calcs configured on this template's date fields, keyed by field key. */
  dateCalcs?: Record<string, DateCalc>;
  /** Conditional field visibility, keyed by dependent field key (MOR-885). */
  fieldConditions?: FieldConditionMap;
  brandingPadding?: PaddingSource | null;
  templatePadding?: PaddingSource | null;
  presetColors?: string[] | null;
  defaultFontFamily?: string | null;
  defaultLineHeight?: string | null;
  bodyTextColor?: string | null;
  defaultFontSize?: string | null;
  defaultParagraphSpacingBefore?: string | null;
  defaultParagraphSpacingAfter?: string | null;
  /**
   * Resolved footer page numbering (null when off firm-wide or suppressed
   * for this template).
   */
  pageNumbering?: PageNumberingConfig | null;
  /** This template's opt-out from the firm-wide page numbering. */
  suppressPageNumbering?: boolean;
  currentTags: string[];
  attachments: AttachmentRequirement[];
  fieldConfig?: GallagherFieldConfig[];
  categoryId?: Id<"templateCategories">;
  /** Existing per-template letterhead override (if any). */
  letterheadOverride?:
    | { kind: "none" }
    | { kind: "letterhead"; bundleName: string }
    | { kind: "variant"; variantId: Id<"letterheadVariants"> }
    | null;
  /**
   * Author's persisted preview variant picks for THIS template (MOR-460).
   * Hydrates the dialog's local state on open so a refresh / reopen renders
   * the variant the author last chose, instead of silently defaulting back
   * to the first option of each group. `undefined` ⇒ no picks saved yet.
   */
  initialPreviewVariantSelections?: Record<string, string> | null;
  initialPreviewSnippetSelections?: Record<
    string,
    Id<"variantSnippets">
  > | null;
  /** Whether the current user has owner/admin access to mutate templates. */
  canEdit: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const hasPdfMappings = !!pdfFieldMappings?.length;

  // When the template carries a per-template letterhead override, the
  // preview should reflect that — not the firm default that was overlaid
  // by the parent. We resolve the override here against the asset
  // library and variant store, and substitute the letterhead props on
  // the PaginatedLetterPreview call below.
  //
  // For variant overrides we surface the FIRST layer's image per zone —
  // accurate for the most common single-layer case and a sensible
  // approximation for stacked-layer recipes (the real day-one renderer
  // handles full per-page layer stacking).
  const { data: previewAssetsRaw } = useQuery({
    ...convexQuery(api.settings.branding.assetQueries.listAssetsForOrg, {
      orgId,
    }),
    enabled: !!orgId && !!letterheadOverride,
  });
  const { data: previewVariantsRaw } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId,
    }),
    enabled: !!orgId && letterheadOverride?.kind === "variant",
  });
  const overrideResolvedLetterhead = useMemo(() => {
    if (!letterheadOverride) return null;
    if (letterheadOverride.kind === "none") {
      return {
        headerImageUrl: null,
        footerImageUrl: null,
        leftSideImageUrl: null,
        rightSideImageUrl: null,
        headerPageRule: "none" as PageRule,
        footerPageRule: "none" as PageRule,
        leftSidePageRule: "none" as PageRule,
        rightSidePageRule: "none" as PageRule,
        leftSideFit: null as "fullHeight" | "topBlock" | null,
        rightSideFit: null as "fullHeight" | "topBlock" | null,
        leftSideTopBlockHeightPct: null as number | null,
        rightSideTopBlockHeightPct: null as number | null,
        leftSideCrop: null as SideCropBag,
        rightSideCrop: null as SideCropBag,
      };
    }
    if (letterheadOverride.kind === "letterhead") {
      const assets = previewAssetsRaw ?? [];
      const bundleAssets = assets.filter(
        (a) =>
          (a.bundleName ?? a.sourceDocumentName ?? null) ===
          letterheadOverride.bundleName,
      );
      const findZone = (zone: string) =>
        bundleAssets.find((a) => a.zoneKind === zone);
      const header = findZone("header");
      const footer = findZone("footer");
      const left = findZone("leftSide");
      const right = findZone("rightSide");
      return {
        headerImageUrl: header?.imageUrl ?? null,
        footerImageUrl: footer?.imageUrl ?? null,
        leftSideImageUrl: left?.imageUrl ?? null,
        rightSideImageUrl: right?.imageUrl ?? null,
        headerPageRule: (header?.defaultPageRule ?? "all") as PageRule,
        footerPageRule: (footer?.defaultPageRule ?? "all") as PageRule,
        leftSidePageRule: (left?.defaultPageRule ?? "all") as PageRule,
        rightSidePageRule: (right?.defaultPageRule ?? "all") as PageRule,
        leftSideFit: (left?.sideFit ?? null) as
          "fullHeight" | "topBlock" | null,
        rightSideFit: (right?.sideFit ?? null) as
          "fullHeight" | "topBlock" | null,
        leftSideTopBlockHeightPct: (left?.topBlockHeightPct ?? null) as
          number | null,
        rightSideTopBlockHeightPct: (right?.topBlockHeightPct ?? null) as
          number | null,
        leftSideCrop: (left
          ? {
              top: left.imageCropTop ?? null,
              bottom: left.imageCropBottom ?? null,
              left: left.imageCropLeft ?? null,
              right: left.imageCropRight ?? null,
            }
          : null) as SideCropBag,
        rightSideCrop: (right
          ? {
              top: right.imageCropTop ?? null,
              bottom: right.imageCropBottom ?? null,
              left: right.imageCropLeft ?? null,
              right: right.imageCropRight ?? null,
            }
          : null) as SideCropBag,
      };
    }
    // letterheadOverride.kind === "variant"
    const variants = previewVariantsRaw ?? [];
    const v = variants.find((x) => x._id === letterheadOverride.variantId);
    if (!v) return null;
    const firstLayer = (layers: ResolvedLayer[] | null | undefined) =>
      layers && layers.length > 0 ? layers[0] : null;
    const h = firstLayer(v.resolvedHeaderLayers);
    const f = firstLayer(v.resolvedFooterLayers);
    const l = firstLayer(v.resolvedLeftSideLayers);
    const r = firstLayer(v.resolvedRightSideLayers);
    return {
      headerImageUrl: h?.imageUrl ?? v.headerImageUrl ?? null,
      footerImageUrl: f?.imageUrl ?? v.footerImageUrl ?? null,
      leftSideImageUrl: l?.imageUrl ?? v.leftSideImageUrl ?? null,
      rightSideImageUrl: r?.imageUrl ?? v.rightSideImageUrl ?? null,
      headerPageRule: (h?.pageRule ?? "all") as PageRule,
      footerPageRule: (f?.pageRule ?? "all") as PageRule,
      leftSidePageRule: (l?.pageRule ?? "all") as PageRule,
      rightSidePageRule: (r?.pageRule ?? "all") as PageRule,
      leftSideFit: (l?.sideFit ?? v.leftSideFit ?? null) as
        "fullHeight" | "topBlock" | null,
      rightSideFit: (r?.sideFit ?? v.rightSideFit ?? null) as
        "fullHeight" | "topBlock" | null,
      leftSideTopBlockHeightPct: (l?.topBlockHeightPct ??
        v.leftSideTopBlockHeightPct ??
        null) as number | null,
      rightSideTopBlockHeightPct: (r?.topBlockHeightPct ??
        v.rightSideTopBlockHeightPct ??
        null) as number | null,
      leftSideCrop: ((l
        ? {
            top: l.imageCropTop ?? null,
            bottom: l.imageCropBottom ?? null,
            left: l.imageCropLeft ?? null,
            right: l.imageCropRight ?? null,
          }
        : null) ??
        v.leftSideCrop ??
        null) as SideCropBag,
      rightSideCrop: ((r
        ? {
            top: r.imageCropTop ?? null,
            bottom: r.imageCropBottom ?? null,
            left: r.imageCropLeft ?? null,
            right: r.imageCropRight ?? null,
          }
        : null) ??
        v.rightSideCrop ??
        null) as SideCropBag,
    };
  }, [letterheadOverride, previewAssetsRaw, previewVariantsRaw]);

  // MOR-953: the full ordered fragment list for a per-template letterhead
  // override — one per (zone, image, rule). `overrideResolvedLetterhead`
  // above flattens each zone to its page-1 image for the crop controls and
  // page-rule badges; that shape cannot express a first-page header paired
  // with a subsequent-pages header, so the renderer uses these instead.
  //
  // `null` means "no override" (fall back to the firm default); an empty
  // array means "this template explicitly carries no letterhead".
  const overrideLetterheadFragments = useMemo<VariantFragments[] | null>(() => {
    if (!letterheadOverride) return null;
    if (letterheadOverride.kind === "none") return [];
    if (letterheadOverride.kind === "letterhead") {
      return buildBundleFragments(
        letterheadOverride.bundleName,
        (previewAssetsRaw ?? []) as LetterheadAssetLike[],
      );
    }
    const variant = ((previewVariantsRaw ?? []) as LayeredVariantLike[]).find(
      (x) => x._id === letterheadOverride.variantId,
    );
    // Override points at a deleted variant → fall through to the firm default.
    return variant ? buildVariantFragments(variant) : null;
  }, [letterheadOverride, previewAssetsRaw, previewVariantsRaw]);

  // Effective values used by the preview render below. When an override
  // is set we use those; otherwise we use the prop values which already
  // carry the firm-default-bundle resolution from the parent.
  const effectiveHeaderImageUrl =
    overrideResolvedLetterhead?.headerImageUrl ?? headerImageUrl;
  const effectiveFooterImageUrl =
    overrideResolvedLetterhead?.footerImageUrl ?? footerImageUrl;
  const effectiveLeftSideImageUrl =
    overrideResolvedLetterhead?.leftSideImageUrl ?? leftSideImageUrl;
  const effectiveRightSideImageUrl =
    overrideResolvedLetterhead?.rightSideImageUrl ?? rightSideImageUrl;
  const effectiveHeaderPageRule =
    overrideResolvedLetterhead?.headerPageRule ??
    (headerPageRule as PageRule | null | undefined);
  const effectiveFooterPageRule =
    overrideResolvedLetterhead?.footerPageRule ??
    (footerPageRule as PageRule | null | undefined);
  const effectiveLeftSidePageRule =
    overrideResolvedLetterhead?.leftSidePageRule ??
    (leftSidePageRule as PageRule | null | undefined);
  const effectiveRightSidePageRule =
    overrideResolvedLetterhead?.rightSidePageRule ??
    (rightSidePageRule as PageRule | null | undefined);
  const effectiveLeftSideFit = overrideResolvedLetterhead
    ? overrideResolvedLetterhead.leftSideFit
    : leftSideFit;
  const effectiveRightSideFit = overrideResolvedLetterhead
    ? overrideResolvedLetterhead.rightSideFit
    : rightSideFit;
  const effectiveLeftSideTopBlockHeightPct = overrideResolvedLetterhead
    ? overrideResolvedLetterhead.leftSideTopBlockHeightPct
    : leftSideTopBlockHeightPct;
  const effectiveRightSideTopBlockHeightPct = overrideResolvedLetterhead
    ? overrideResolvedLetterhead.rightSideTopBlockHeightPct
    : rightSideTopBlockHeightPct;
  const effectiveLeftSideCrop = overrideResolvedLetterhead
    ? overrideResolvedLetterhead.leftSideCrop
    : leftSideCrop;
  const effectiveRightSideCrop = overrideResolvedLetterhead
    ? overrideResolvedLetterhead.rightSideCrop
    : rightSideCrop;
  // Override blanks out legacy HTML — the asset library is image-only.
  const effectiveHeaderHtml = overrideResolvedLetterhead ? null : headerHtml;
  const effectiveFooterHtml = overrideResolvedLetterhead ? null : footerHtml;

  // LetterParts for the preview's pagination engine (MOR-521). Wrap the
  // effective letterhead values into a single variant and carry the
  // font/spacing defaults so the preview paginates with the SAME inputs the
  // PDF export would. Note `defaultParagraphSpacingBefore/After` are now fed
  // through (previously dropped on this surface, causing preview drift).
  const previewParts = useMemo<LetterParts>(() => {
    // Precedence: explicit per-template override (including an explicit
    // "no letterhead") → firm default → the legacy single-image props.
    // The first two carry every layer; the last is the pre-MOR-953 shape
    // kept as a fallback for anything the fragment paths don't cover.
    const layered =
      overrideLetterheadFragments ??
      (firmLetterheadFragments && firmLetterheadFragments.length > 0
        ? firmLetterheadFragments
        : null);
    if (!isThirdPartyForm && layered) {
      return {
        bodyHtml: "",
        variants: layered,
        brandingPadding,
        templatePadding,
        templatePageOverrides:
          (
            templatePadding as
              { pageMarginOverrides?: PageOverrideSource[] } | null | undefined
          )?.pageMarginOverrides ?? null,
        defaultFontFamily: defaultFontFamily ?? undefined,
        defaultLineHeight: defaultLineHeight ?? undefined,
        bodyTextColor: bodyTextColor ?? undefined,
        defaultFontSize: defaultFontSize ?? undefined,
        defaultParagraphSpacingBefore:
          defaultParagraphSpacingBefore ?? undefined,
        defaultParagraphSpacingAfter: defaultParagraphSpacingAfter ?? undefined,
      };
    }
    const variants = isThirdPartyForm
      ? []
      : [
          resolveVariantFragments({
            headerImageUrl: effectiveHeaderImageUrl ?? undefined,
            footerImageUrl: effectiveFooterImageUrl ?? undefined,
            leftSideImageUrl: effectiveLeftSideImageUrl ?? undefined,
            rightSideImageUrl: effectiveRightSideImageUrl ?? undefined,
            headerHtml: effectiveHeaderHtml ?? undefined,
            footerHtml: effectiveFooterHtml ?? undefined,
            headerPageRule: effectiveHeaderPageRule ?? undefined,
            footerPageRule: effectiveFooterPageRule ?? undefined,
            leftSidePageRule: effectiveLeftSidePageRule ?? undefined,
            rightSidePageRule: effectiveRightSidePageRule ?? undefined,
            leftSideFit: effectiveLeftSideFit ?? undefined,
            rightSideFit: effectiveRightSideFit ?? undefined,
            leftSideTopBlockHeightPct:
              effectiveLeftSideTopBlockHeightPct ?? undefined,
            rightSideTopBlockHeightPct:
              effectiveRightSideTopBlockHeightPct ?? undefined,
            leftSideCrop: effectiveLeftSideCrop ?? undefined,
            rightSideCrop: effectiveRightSideCrop ?? undefined,
          }),
        ];
    return {
      bodyHtml: "",
      variants,
      brandingPadding: isThirdPartyForm ? undefined : brandingPadding,
      templatePadding: isThirdPartyForm ? undefined : templatePadding,
      templatePageOverrides: isThirdPartyForm
        ? null
        : ((
            templatePadding as
              { pageMarginOverrides?: PageOverrideSource[] } | null | undefined
          )?.pageMarginOverrides ?? null),
      defaultFontFamily: isThirdPartyForm
        ? undefined
        : (defaultFontFamily ?? undefined),
      defaultLineHeight: isThirdPartyForm
        ? undefined
        : (defaultLineHeight ?? undefined),
      bodyTextColor: isThirdPartyForm
        ? undefined
        : (bodyTextColor ?? undefined),
      defaultFontSize: isThirdPartyForm
        ? undefined
        : (defaultFontSize ?? undefined),
      defaultParagraphSpacingBefore: isThirdPartyForm
        ? undefined
        : (defaultParagraphSpacingBefore ?? undefined),
      defaultParagraphSpacingAfter: isThirdPartyForm
        ? undefined
        : (defaultParagraphSpacingAfter ?? undefined),
      pageNumbering: isThirdPartyForm ? null : (pageNumbering ?? null),
    };
  }, [
    isThirdPartyForm,
    overrideLetterheadFragments,
    firmLetterheadFragments,
    effectiveHeaderImageUrl,
    effectiveFooterImageUrl,
    effectiveLeftSideImageUrl,
    effectiveRightSideImageUrl,
    effectiveHeaderHtml,
    effectiveFooterHtml,
    effectiveHeaderPageRule,
    effectiveFooterPageRule,
    effectiveLeftSidePageRule,
    effectiveRightSidePageRule,
    effectiveLeftSideFit,
    effectiveRightSideFit,
    effectiveLeftSideTopBlockHeightPct,
    effectiveRightSideTopBlockHeightPct,
    effectiveLeftSideCrop,
    effectiveRightSideCrop,
    brandingPadding,
    templatePadding,
    defaultFontFamily,
    defaultLineHeight,
    bodyTextColor,
    defaultFontSize,
    defaultParagraphSpacingBefore,
    defaultParagraphSpacingAfter,
    pageNumbering,
  ]);
  // Resolve margins with the SAME variant tier the paginator uses — variant
  // padding overrides (Beacon) would otherwise be missing from the preview.
  const docPadding = resolveDocumentPadding(
    brandingPadding,
    templatePadding,
    undefined,
    variantPaddingSources(previewParts.variants ?? []),
  );

  // The visual PDF field editor (Map Fields tab) and Mappings tab both mutate
  // template data, so they require admin/owner access in addition to having a
  // PDF source. The Edit tab is similarly admin/owner-only.
  const showFieldEditor =
    !!isThirdPartyForm && !!pdfUrl && !!templateId && canEditSettings;

  const canEdit =
    !!bodyHtml && !isThirdPartyForm && !!templateId && canEditSettings;

  // Settings tab mutates tags/category and the inline custom-field affordance,
  // so hide it from members entirely.
  const showSettingsTab = canEditSettings;

  const showGallagherConfig =
    templateKey === "form_gallagher" && !!templateId && canEditSettings;

  const [activeTab, setActiveTab] = useState<
    | "form"
    | "original"
    | "mappings"
    | "edit"
    | "mapfields"
    | "attachments"
    | "settings"
    | "gallagher"
  >(
    bodyHtml
      ? "form"
      : showFieldEditor
        ? "mapfields"
        : pdfUrl
          ? "original"
          : hasPdfMappings && canEditSettings
            ? "mappings"
            : "form",
  );

  // Right-hand notes sidebar — persists open/closed across dialog opens.
  const [notesOpen, setNotesOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("precedent-notes-sidebar-open") === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      "precedent-notes-sidebar-open",
      notesOpen ? "1" : "0",
    );
  }, [notesOpen]);

  const { data: currentUser } = useQuery({
    ...convexQuery(api.users.queries.getCurrentUser, {}),
  });
  const currentUserId = currentUser?._id as Id<"users"> | undefined;

  const { data: notesForCount } = useQuery({
    ...convexQuery(api.settings.precedents.notes.listTemplateNotes, {
      templateId: templateId as Id<"precedentTemplates">,
    }),
    enabled: !!templateId,
  });
  const notesCount = notesForCount?.length ?? 0;

  // Author's preview variant / snippet picks for THIS template. Seeded
  // from the template doc so a refresh keeps the selection (MOR-460), and
  // any change is written back via `updateTemplatePreviewSelections` so
  // every reviewer sees the same authoritative preview.
  const [previewVariantSelections, setPreviewVariantSelections] = useState<
    Record<string, string>
  >(initialPreviewVariantSelections ?? {});
  const [previewSnippetSelections, setPreviewSnippetSelections] = useState<
    Record<string, string>
  >(initialPreviewSnippetSelections ?? {});

  // Re-hydrate when the dialog is reopened against a different template
  // (or the template doc reloads with fresh persisted picks). Compares by
  // templateId so swapping templates resets cleanly without clobbering
  // in-progress edits on the same template.
  const hydratedForTemplateRef = useRef<Id<"precedentTemplates"> | undefined>(
    undefined,
  );
  useEffect(() => {
    if (!templateId) return;
    if (hydratedForTemplateRef.current === templateId) return;
    hydratedForTemplateRef.current = templateId;
    setPreviewVariantSelections(initialPreviewVariantSelections ?? {});
    setPreviewSnippetSelections(initialPreviewSnippetSelections ?? {});
  }, [
    templateId,
    initialPreviewVariantSelections,
    initialPreviewSnippetSelections,
  ]);

  const { mutateAsync: persistPreviewSelections } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.updateTemplatePreviewSelections,
    ),
  });

  // Debounce the write so a rapid click-through doesn't spam Convex —
  // 400ms after the last pick is plenty fast for the author's experience
  // and avoids piling mutations onto a single navigation away from the
  // dialog. Cleared on unmount so the final pending write still lands.
  const previewPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  useEffect(() => {
    return () => {
      if (previewPersistTimerRef.current) {
        clearTimeout(previewPersistTimerRef.current);
      }
    };
  }, []);
  const schedulePersistPreviewSelections = useCallback(
    (next: {
      variantSelections?: Record<string, string>;
      snippetSelections?: Record<string, string>;
    }) => {
      if (!templateId || !canEditSettings) return;
      if (previewPersistTimerRef.current) {
        clearTimeout(previewPersistTimerRef.current);
      }
      previewPersistTimerRef.current = setTimeout(() => {
        // Snippet IDs are branded `Id<"variantSnippets">` server-side but
        // are stored locally as plain strings (matches the
        // `VariantPicker` `snippetSelections` shape). Cast at the boundary.
        const snippetCast = next.snippetSelections as
          Record<string, Id<"variantSnippets">> | undefined;
        void persistPreviewSelections({
          templateId,
          previewVariantSelections: next.variantSelections,
          previewSnippetSelections: snippetCast,
        });
      }, 400);
    },
    [templateId, canEditSettings, persistPreviewSelections],
  );

  // Which variant groups this template references — drives the picker and
  // default selection.
  const referencedSlugs = useMemo(
    () => extractVariantGroupSlugs(bodyHtml),
    [bodyHtml],
  );

  const { mutateAsync: setTemplatePageMarginOverride } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.setTemplatePageMarginOverride,
    ),
  });
  const { mutateAsync: updateTemplateHtml } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.updateTemplateHtml,
    ),
  });

  const { mutateAsync: saveFieldPositions } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.saveFieldPositions,
    ),
  });

  const { mutateAsync: setTemplateDateCalc } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.setTemplateDateCalc,
    ),
  });

  const { mutateAsync: setTemplateFieldCondition } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.setTemplateFieldCondition,
    ),
  });

  /**
   * Persist a field visibility condition from the PDF field editor
   * (MOR-885). Keyed by the dependent field; `undefined` clears it.
   */
  const handleFieldConditionChange = useCallback(
    async (dependentKey: string, condition: FieldCondition | undefined) => {
      if (!templateId) return;
      try {
        await setTemplateFieldCondition({
          templateId,
          fieldKey: dependentKey,
          condition,
        });
      } catch (err) {
        toast.error("Couldn't save the field condition. Please try again.");
        // Rethrow so the editor rolls its optimistic state back rather than
        // showing a condition the server never accepted.
        throw err;
      }
    },
    [templateId, setTemplateFieldCondition],
  );

  /**
   * Persist a date calc from the letter editor's field bubble menu. Keyed by
   * field so every mention of that date in the letter derives one value.
   */
  const handleDateCalcChange = useCallback(
    async (fieldKey: string, dateCalc: DateCalc | undefined) => {
      if (!templateId) return;
      try {
        await setTemplateDateCalc({ templateId, fieldKey, dateCalc });
      } catch {
        toast.error("Couldn't save the date calculation. Please try again.");
      }
    },
    [templateId, setTemplateDateCalc],
  );

  // Reusable org-wide fields plus any fields scoped to this template.
  const { data: customFieldDefs } = useQuery({
    ...convexQuery(api.settings.precedents.customFields.listForTemplate, {
      orgId,
      templateId: templateId as Id<"precedentTemplates">,
    }),
    enabled: !!templateId,
  });

  const { data: variantGroupsData } = useQuery({
    ...convexQuery(api.settings.precedents.variantGroups.listVariantGroups, {
      orgId,
    }),
  });
  const variantGroupsForEditor = useMemo(
    () =>
      (variantGroupsData ?? []).map((g) => ({
        slug: g.slug,
        name: g.name,
        options: g.options,
      })),
    [variantGroupsData],
  );

  const { data: variantSnippetsData } = useQuery({
    ...convexQuery(api.settings.precedents.variantSnippets.listSnippets, {
      orgId,
    }),
  });
  const variantSnippetsForEditor = useMemo(
    () =>
      (variantSnippetsData ?? []).map((s) => ({
        _id: String(s._id),
        name: s.name,
        contentHtml: s.contentHtml,
        groupSlug: s.groupSlug,
        optionValue: s.optionValue,
        slotLabel: s.slotLabel,
      })),
    [variantSnippetsData],
  );

  // Org-wide field presets, used by the preview to colour-code chips by source.
  const { data: orgPresets } = useQuery({
    ...convexQuery(api.settings.precedents.presets.listByOrg, { orgId }),
  });

  const { mutateAsync: createCustomField } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.customFields.createField,
    ),
  });
  // Used by the editor's "set a value" step right after creating a
  // reusable custom field — wires the new field into a preset without
  // making the user open Settings → Fields.
  const { mutateAsync: createPresetMutation } = useMutation({
    mutationFn: useConvexMutation(api.settings.precedents.presets.createPreset),
  });
  const { mutateAsync: updatePresetMutation } = useMutation({
    mutationFn: useConvexMutation(api.settings.precedents.presets.updatePreset),
  });

  const handleSavePreset = useCallback(
    async (input: {
      categoryId: string;
      categoryLabel: string;
      values: Record<string, string>;
      presetId?: string;
      newPresetName?: string;
    }) => {
      try {
        if (input.newPresetName) {
          await createPresetMutation({
            orgId,
            categoryId: input.categoryId,
            name: input.newPresetName,
            values: input.values,
            isDefault: false,
          });
          toast.success(`Saved "${input.newPresetName}"`);
          return;
        }
        if (!input.presetId) return;
        // Merge with the existing preset's values so siblings outside this
        // batch aren't wiped (covers the case where the popup only edited
        // a subset of the preset's fields).
        const existing = (orgPresets ?? []).find(
          (p) => p._id === input.presetId,
        );
        const nextValues = {
          ...(existing?.values ?? {}),
          ...input.values,
        };
        await updatePresetMutation({
          presetId: input.presetId as Id<"fieldPresets">,
          values: nextValues,
        });
        toast.success(`Updated "${existing?.name ?? "preset"}"`);
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to save preset"));
      }
    },
    [orgId, orgPresets, createPresetMutation, updatePresetMutation],
  );

  const orgPresetsForEditor = useMemo(
    () =>
      (orgPresets ?? []).map((p) => ({
        _id: String(p._id),
        categoryId: p.categoryId,
        name: p.name,
        values: p.values ?? {},
      })),
    [orgPresets],
  );

  // Optimistic tags - update the UI instantly on checkbox toggle, then persist
  // to Convex in the background. All five tag editors (appointment type, job
  // status, job contacts, firm contacts, custom groups) share this state so
  // rapid clicks - within or across sections - never overwrite each other.
  //
  // MOR-921: two ticks landing inside one render window used to derive their
  // next array from the same stale value, so the second write dropped the
  // first. Three things prevent that now:
  //   1. Editors pass an UPDATER, applied against `pendingTagsRef`, which is
  //      mutated synchronously so a second click sees the first's result.
  //   2. Only the (add, remove) delta is sent; the server applies it to the
  //      stored array, so concurrent writes commute instead of clobbering.
  //   3. The server->client resync is skipped while writes are in flight, so
  //      the response for tick 1 cannot visually undo tick 2.
  const [optimisticTags, setOptimisticTags] = useState(currentTags);
  const pendingTagsRef = useRef<string[]>(currentTags);
  const inFlightTagWrites = useRef(0);

  // Resync from the server only when nothing is in flight. `currentTags` is a
  // fresh `template.tags ?? []` array each parent render, so the effect keys
  // on content rather than array identity.
  const currentTagsKey = currentTags.join("\u0000");
  useEffect(() => {
    if (inFlightTagWrites.current > 0) return;
    const next = currentTagsKey === "" ? [] : currentTagsKey.split("\u0000");
    pendingTagsRef.current = next;
    setOptimisticTags(next);
  }, [currentTagsKey]);

  const { mutateAsync: updateTagsDelta } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.updateTemplateTagsDelta,
    ),
  });

  const handleTagsChange = useCallback(
    async (update: string[] | ((prev: string[]) => string[])) => {
      const prev = pendingTagsRef.current;
      const next = typeof update === "function" ? update(prev) : update;

      // Synchronous, so a second click in the same render window composes on
      // top of this one instead of restarting from the server value.
      pendingTagsRef.current = next;
      setOptimisticTags(next);

      const prevSet = new Set(prev);
      const nextSet = new Set(next);
      const add = next.filter((t) => !prevSet.has(t));
      const remove = prev.filter((t) => !nextSet.has(t));
      if (add.length === 0 && remove.length === 0) return;

      inFlightTagWrites.current++;
      try {
        const serverTags = await updateTagsDelta({
          orgId,
          templateKey,
          add,
          remove,
        });
        // Adopt the server array once the LAST in-flight write settles, so a
        // tag changed elsewhere is picked up without clobbering a tick that is
        // still on the wire here.
        if (inFlightTagWrites.current === 1) {
          pendingTagsRef.current = serverTags;
          setOptimisticTags(serverTags);
        }
      } catch {
        // Back out just this delta, leaving later ticks that already composed
        // on top of it intact.
        const rolledBack = pendingTagsRef.current.filter(
          (t) => !add.includes(t),
        );
        for (const t of remove) {
          if (!rolledBack.includes(t)) rolledBack.push(t);
        }
        pendingTagsRef.current = rolledBack;
        setOptimisticTags(rolledBack);
        toast.error("Failed to update tags");
      } finally {
        inFlightTagWrites.current--;
      }
    },
    [orgId, templateKey, updateTagsDelta],
  );

  const handleCreateCustomField = useCallback(
    async (field: {
      key: string;
      label: string;
      fieldType: FieldType;
      categoryId: string;
      categoryLabel: string;
    }): Promise<{ key: string; label: string } | null> => {
      try {
        await createCustomField({
          orgId,
          key: field.key,
          label: field.label,
          fieldType: field.fieldType,
          categoryId: field.categoryId,
          categoryLabel: field.categoryLabel,
        });
        toast.success(`Custom field "${field.label}" created`);
        // Return the canonicalised shape so callers (e.g. the docs editor's
        // Insert Field popover) can immediately insert the new field. The
        // backend lower-cases + snake_cases the key, so mirror that here.
        return {
          key: field.key.trim().toLowerCase().replace(/\s+/g, "_"),
          label: field.label.trim(),
        };
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to create custom field"));
        return null;
      }
    },
    [orgId, createCustomField],
  );

  const customFieldsForPicker: CustomFieldDef[] = useMemo(
    () =>
      (customFieldDefs ?? []).map((d) => ({
        _id: d._id,
        categoryId: d.categoryId,
        categoryLabel: d.categoryLabel,
        key: d.key,
        label: d.label,
        fieldType: d.fieldType,
        templateId: d.templateId,
      })),
    [customFieldDefs],
  );

  // Org-defined firm-contact custom fields — surfaced in the field picker /
  // Insert Field menu under "Firm Contact" for the tagged categories.
  const { data: firmContactFieldDefs } = useQuery(
    convexQueryOrIdle(
      api.contacts.fieldDefinitions.listFirmContactFieldDefinitions,
      orgId ? { orgId } : undefined,
    ),
  );
  const firmContactCustomFieldOptions = useMemo<FirmContactCustomFieldOption[]>(
    () =>
      (firmContactFieldDefs ?? []).map((d) => ({
        category: d.category,
        key: d.key,
        label: d.label,
      })),
    [firmContactFieldDefs],
  );

  // Org-defined custom job roles — resolve `jcr:<id>` tags to their live names
  // so the editor's Job Contact chip prefix reads e.g. "Tax Advisor" instead
  // of the ambiguous "Job Contact".
  const { data: editorCustomJobRoles } = useQuery(
    convexQueryOrIdle(
      api.contacts.customRoles.listJobContactRoles,
      orgId ? { orgId } : undefined,
    ),
  );
  const customJobRoleNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of editorCustomJobRoles ?? []) map.set(String(r._id), r.name);
    return map;
  }, [editorCustomJobRoles]);

  // ---------------------------------------------------------------------------
  // Field source classification (preview colour coding)
  // ---------------------------------------------------------------------------
  // Build sets that the classifier consumes. Memoised separately from the
  // classifier itself so each set only invalidates when its source query
  // (custom fields / presets / template extraction) actually changes.
  const customFieldKeys = useMemo(
    () => new Set<string>((customFieldDefs ?? []).map((d) => d.key)),
    [customFieldDefs],
  );
  const orgPresetKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const preset of orgPresets ?? []) {
      for (const k of Object.keys(preset.values ?? {})) keys.add(k);
    }
    return keys;
  }, [orgPresets]);
  const templateFieldsWithExample = useMemo(() => {
    const set = new Set<string>();
    for (const f of fields ?? []) {
      if (!f.exampleValue?.trim()) continue;
      set.add(f.key);
      if (f.canonicalKey) set.add(f.canonicalKey);
    }
    return set;
  }, [fields]);

  const classifySource = useCallback(
    (key: string): FieldSource =>
      classifyFieldSource({
        key,
        customFieldKeys,
        orgPresetKeys,
        templateFieldsWithExample,
      }),
    [customFieldKeys, orgPresetKeys, templateFieldsWithExample],
  );

  // Contact roles / firm categories this template fans out over — drives
  // the Insert Field "Job Contact" / "Firm Contact" groups in the editor.
  // Reads `optimisticTags` so newly-ticked roles appear without a reload.
  const editorContactTags = useMemo(
    () => ({
      jobRoles: optimisticTags
        .map((t) => parseContactRoleTag(t))
        .filter((k): k is ContactRole => k !== null),
      firmCategories: optimisticTags
        .map((t) => parseFirmContactTag(t))
        .filter((c): c is FirmContactCategory => c !== null),
      // Custom job roles (`jcr:*`) unlock the generic Job Contact fields too,
      // even though they carry no built-in source-kind mapping. Their names
      // feed the chip prefix; unknown / archived ids resolve to nothing.
      // The external-administration narrowing tag (`jcf:external_admin`)
      // selects on its own when no role is ticked, so it must unlock the Job
      // Contact fields too. It's surfaced as a pseudo role name ("EXAD") so
      // the chip prefix reads "EXAD: Company Full Name" / "EXAD: Appointee" —
      // the prefix also switches `jobContactPartLabel` into entity-aware mode.
      customJobRoleNames: [
        ...optimisticTags
          .map((t) => parseJobContactCustomRoleTag(t))
          .filter((id): id is string => id !== null)
          .map((id) => customJobRoleNameById.get(id))
          .filter((name): name is string => name !== undefined),
        ...(optimisticTags.some((t) => isJobContactExternalAdminTag(t)) &&
        !optimisticTags.some((t) => parseContactRoleTag(t) !== null)
          ? [EXTERNAL_ADMIN_ROLE_LABEL]
          : []),
      ],
    }),
    [optimisticTags, customJobRoleNameById],
  );

  // Convert {{key}} mustache placeholders to <span data-field-key="key"> chips
  // so both the preview and the annotator can operate on real DOM elements.
  const fieldsMap = useMemo(() => buildFieldsLabelMap(fields), [fields]);

  // Build effective variant selections: default to the first option of each
  // referenced group, then overlay any explicit user picks. Without defaults
  // the new resolveVariantBlocks behaviour strips blocks with no selection.
  const defaultVariantSelections = useMemo(() => {
    const defaults: Record<string, string> = {};
    for (const slug of referencedSlugs) {
      const group = variantGroupsForEditor.find((g) => g.slug === slug);
      if (group && group.options.length > 0) {
        defaults[slug] = group.options[0].value;
      }
    }
    return defaults;
  }, [referencedSlugs, variantGroupsForEditor]);

  const effectiveVariantSelections = useMemo(
    () => ({ ...defaultVariantSelections, ...previewVariantSelections }),
    [defaultVariantSelections, previewVariantSelections],
  );

  // Match the same `Job Contact` → role-label mapping used by the editor
  // so the Preview tab shows `Director: Name` (or slash-joined roles for
  // multi-tagged templates) instead of the generic `Job Contact` prefix.
  const previewJobContactPrefix = useMemo(() => {
    const labels = [
      ...editorContactTags.jobRoles.map(
        (role) => ROLE_META[role]?.label ?? role,
      ),
      ...editorContactTags.customJobRoleNames,
    ];
    if (labels.length === 0) return "Job Contact";
    return labels.join(" / ");
  }, [editorContactTags.jobRoles, editorContactTags.customJobRoleNames]);

  // Prepared HTML with field chips still intact (keeps `data-field-mods` on
  // each chip). Shared by the decorated chip preview and the sample-data
  // preview — the latter needs the raw modifiers to reproduce the generated
  // value, so it must read this rather than the decorated HTML below (which
  // strips `data-field-mods`).
  const preparedHtml = useMemo(() => {
    if (!bodyHtml) return null;
    return prepareHtmlForEditor(
      resolveVariantBlocks(
        fillVariantSnippetPlaceholders(
          bodyHtml,
          variantSnippetsForEditor,
          previewSnippetSelections,
          effectiveVariantSelections,
        ),
        effectiveVariantSelections,
        { highlight: referencedSlugs.size > 0 },
      ),
      fieldsMap,
      { jobContactPrefix: previewJobContactPrefix },
    );
  }, [
    bodyHtml,
    fieldsMap,
    variantSnippetsForEditor,
    effectiveVariantSelections,
    previewSnippetSelections,
    referencedSlugs,
    previewJobContactPrefix,
  ]);

  const previewHtml = useMemo(() => {
    if (!preparedHtml) return null;
    // Bake field-modifier decoration (wrap chars + badges) into the static
    // chips so the precedents-library preview matches the editor NodeView.
    return decorateFieldChipModifiers(preparedHtml);
  }, [preparedHtml]);

  // Sanitise once per render and tag each placeholder span with its source so
  // CSS in `src/index.css` can paint per-source colours.
  const annotatedBodyHtml = useMemo(() => {
    if (!previewHtml) return null;
    const sanitized = DOMPurify.sanitize(previewHtml, {
      // TipTap field chips rely on these attributes; allow-list them so
      // sanitisation does not strip keys before `annotateFieldSources` runs.
      ADD_ATTR: [
        "data-field-key",
        "data-field-label",
        "data-field-mods",
        "data-type",
        "data-field-source",
        "data-variant-highlight",
      ],
    });
    return annotateFieldSources(sanitized, classifySource);
  }, [previewHtml, classifySource]);

  // Sample-data preview (MOR-923) — replace every field chip with a
  // plausible sample value so the letter reads exactly as generated.
  // Off by default; the chips remain the primary preview.
  const [showSampleData, setShowSampleData] = useState(false);
  const sampleResolver = useMemo(
    () =>
      buildSampleValueResolver({
        templateFields: fields ?? [],
        customFields: customFieldsForPicker,
      }),
    [fields, customFieldsForPicker],
  );
  const sampleBodyHtml = useMemo(() => {
    if (!preparedHtml || !showSampleData) return null;
    // Substitute on the undecorated prepared HTML so each chip's
    // `data-field-mods` is still present — `substituteSampleValuesInHtml`
    // applies those modifiers (ABN/ACN prefix, case, wrap, date format) to the
    // resolved sample value. Sanitise first with the same field-chip allow-list
    // as `annotatedBodyHtml`; substitution then only swaps chip elements for
    // text nodes, so the result stays sanitised.
    const sanitized = DOMPurify.sanitize(preparedHtml, {
      ADD_ATTR: [
        "data-field-key",
        "data-field-label",
        "data-field-mods",
        "data-type",
        "data-field-source",
        "data-variant-highlight",
      ],
    });
    return substituteSampleValuesInHtml(sanitized, sampleResolver);
  }, [preparedHtml, showSampleData, sampleResolver]);

  const handleSaveFieldPositions = useCallback(
    async (positions: FieldPosition[]) => {
      if (!templateId) return;
      await saveFieldPositions({ templateId, fieldPositions: positions });
    },
    [templateId, saveFieldPositions],
  );

  const handleSave = useCallback(
    async (html: string, padding?: PaddingSource) => {
      if (!templateId) return;
      await updateTemplateHtml({
        templateId,
        templateHtml: html,
        ...(padding && {
          headerTopPadding: padding.headerTopPadding ?? undefined,
          headerSidePadding: padding.headerSidePadding ?? undefined,
          headerLeftPadding: padding.headerLeftPadding ?? undefined,
          headerRightPadding: padding.headerRightPadding ?? undefined,
          bodyTopPadding: padding.bodyTopPadding ?? undefined,
          bodySidePadding: padding.bodySidePadding ?? undefined,
          bodyLeftPadding: padding.bodyLeftPadding ?? undefined,
          bodyRightPadding: padding.bodyRightPadding ?? undefined,
          bodyBottomPadding: padding.bodyBottomPadding ?? undefined,
          footerBottomPadding: padding.footerBottomPadding ?? undefined,
          footerSidePadding: padding.footerSidePadding ?? undefined,
          footerLeftPadding: padding.footerLeftPadding ?? undefined,
          footerRightPadding: padding.footerRightPadding ?? undefined,
          sidePadding: padding.sidePadding ?? undefined,
        }),
      });
    },
    [templateId, updateTemplateHtml],
  );

  return (
    <Dialog open={controlledOpen} onOpenChange={controlledOnOpenChange}>
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          <button
            type="button"
            aria-label="Preview template"
            className="rounded p-1 text-primary/40 transition hover:bg-primary/5 hover:text-primary"
          >
            <Eye className="size-3.5" />
          </button>
        </DialogTrigger>
      )}
      <DialogContent
        className={cn(
          "flex h-[90vh] flex-col gap-0 p-0",
          activeTab === "edit" || activeTab === "mapfields"
            ? "max-w-7xl"
            : "max-w-6xl",
        )}
      >
        <VisuallyHidden.Root>
          <DialogTitle>{name} Preview</DialogTitle>
        </VisuallyHidden.Root>
        <div className="flex items-center justify-between border-b border-border px-6 py-3 pr-12">
          <div className="flex items-center gap-2">
            {activeTab === "edit" ? (
              <Pencil className="size-4 text-primary/40" />
            ) : (
              <Eye className="size-4 text-primary/40" />
            )}
            <span className="text-sm font-medium text-primary/70">{name}</span>
          </div>
          {/* Tab switcher */}
          <div className="flex items-center gap-1 rounded-md border border-border p-0.5 text-xs">
            {bodyHtml && (
              <button
                type="button"
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap rounded px-2.5 py-1 transition",
                  activeTab === "form"
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                onClick={() => setActiveTab("form")}
              >
                <Eye className="size-3.5" />
                Preview
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap rounded px-2.5 py-1 transition",
                  activeTab === "edit"
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                onClick={() => setActiveTab("edit")}
              >
                <Pencil className="size-3.5" />
                Edit
              </button>
            )}
            {pdfUrl && (
              <button
                type="button"
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap rounded px-2.5 py-1 transition",
                  activeTab === "original"
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                onClick={() => setActiveTab("original")}
              >
                <FileText className="size-3.5" />
                {(() => {
                  const ext = (sourceDocumentName ?? pdfUrl ?? "")
                    .toLowerCase()
                    .split(".")
                    .pop();
                  return ext === "docx" ||
                    ext === "doc" ||
                    ext === "dotx" ||
                    ext === "dot"
                    ? "Original Word"
                    : "Original PDF";
                })()}
              </button>
            )}
            {showFieldEditor && (
              <button
                type="button"
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap rounded px-2.5 py-1 transition",
                  activeTab === "mapfields"
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                onClick={() => setActiveTab("mapfields")}
              >
                <CheckSquare className="size-3.5" />
                Map Fields
                {fieldPositions?.length ? ` (${fieldPositions.length})` : ""}
              </button>
            )}
            {hasPdfMappings && !showFieldEditor && canEditSettings && (
              <button
                type="button"
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap rounded px-2.5 py-1 transition",
                  activeTab === "mappings"
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                onClick={() => setActiveTab("mappings")}
              >
                <List className="size-3.5" />
                Mappings ({pdfFieldMappings?.length ?? 0})
              </button>
            )}
            {showGallagherConfig && (
              <button
                type="button"
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap rounded px-2.5 py-1 transition",
                  activeTab === "gallagher"
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                onClick={() => setActiveTab("gallagher")}
              >
                <LayoutGrid className="size-3.5" />
                Portal Fields
              </button>
            )}
            {showSettingsTab && (
              <button
                type="button"
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap rounded px-2.5 py-1 transition",
                  activeTab === "attachments"
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                onClick={() => setActiveTab("attachments")}
              >
                <Paperclip className="size-3.5" />
                Attachments
                {attachments.length > 0 ? ` (${attachments.length})` : ""}
              </button>
            )}
            {showSettingsTab && (
              <button
                type="button"
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap rounded px-2.5 py-1 transition",
                  activeTab === "settings"
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                onClick={() => setActiveTab("settings")}
              >
                <SettingsIcon className="size-3.5" />
                Settings
              </button>
            )}
            {templateId && (
              <button
                type="button"
                className={cn(
                  "ml-1 flex items-center gap-1 whitespace-nowrap rounded px-2.5 py-1 transition",
                  notesOpen
                    ? "bg-primary/10 font-medium text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                onClick={() => setNotesOpen((v) => !v)}
                aria-pressed={notesOpen}
                title={notesOpen ? "Hide notes" : "Show notes"}
              >
                <StickyNote className="size-3.5" />
                Notes
                {notesCount > 0 && (
                  <span className="ml-0.5 rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold text-primary">
                    {notesCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          <div
            className={cn(
              "min-w-0 flex-1",
              // The PDF field editor + WYSIWYG editor are flex columns that
              // need min-height: 0 to size correctly inside the dialog. The
              // simpler tabs (preview, original, mappings) want overflow-auto
              // for natural scrolling.
              activeTab === "mapfields" || activeTab === "edit"
                ? "min-h-0 overflow-hidden"
                : "overflow-auto",
            )}
          >
            {/* WYSIWYG editor for AI-extracted letter templates */}
            {activeTab === "edit" && canEdit && bodyHtml && (
              <TemplateEditor
                initialHtml={bodyHtml}
                fields={fields}
                customFields={customFieldsForPicker}
                dateCalcs={dateCalcs}
                onDateCalcChange={templateId ? handleDateCalcChange : undefined}
                onCreateCustomField={handleCreateCustomField}
                onSavePreset={handleSavePreset}
                orgPresets={orgPresetsForEditor}
                onAutosave={handleSave}
                onClose={() => setActiveTab("form")}
                templatePadding={templatePadding}
                brandingPadding={brandingPadding}
                presetColors={presetColors}
                defaultFontFamily={defaultFontFamily}
                defaultLineHeight={defaultLineHeight}
                bodyTextColor={bodyTextColor}
                defaultFontSize={defaultFontSize}
                defaultParagraphSpacingBefore={defaultParagraphSpacingBefore}
                defaultParagraphSpacingAfter={defaultParagraphSpacingAfter}
                classifyFieldSource={classifySource}
                contactTags={editorContactTags}
                firmContactCustomFields={firmContactCustomFieldOptions}
                templateId={templateId}
                canUseAi={canEdit}
                variantGroups={variantGroupsForEditor}
                variantSnippets={variantSnippetsForEditor}
                variantSelections={effectiveVariantSelections}
                onVariantChange={(slug, value) =>
                  setPreviewVariantSelections((prev) => {
                    const next = { ...prev, [slug]: value };
                    schedulePersistPreviewSelections({
                      variantSelections: next,
                    });
                    return next;
                  })
                }
              />
            )}

            {/* Original document (PDF or DOCX) */}
            {activeTab === "original" && pdfUrl && (
              <div className="flex h-full flex-col">
                <div className="flex items-center justify-end border-b border-border bg-muted/10 px-4 py-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5 text-xs"
                    onClick={() =>
                      void downloadSourceDoc(pdfUrl, sourceDocumentName)
                    }
                  >
                    <Download className="size-3.5" />
                    Download
                  </Button>
                </div>
                <div className="min-h-0 flex-1 overflow-auto">
                  <SourceDocPreview
                    url={pdfUrl}
                    fileName={sourceDocumentName}
                    title={`${name} Original`}
                  />
                </div>
              </div>
            )}

            {/* Visual PDF field editor */}
            {activeTab === "mapfields" && showFieldEditor && pdfUrl && (
              <div className="flex h-full flex-col">
                {/*
                Same field-source legend the Preview tab shows above the
                rendered letter — repeating it here so the chips in the
                Fields panel and the rectangles drawn on the PDF carry an
                explicit colour key without the user having to flip tabs
                (MOR-462).
              */}
                <div className="shrink-0 border-b border-border bg-secondary/20 px-5 py-3">
                  <SourceLegend />
                </div>
                <PdfFieldEditor
                  pdfUrl={pdfUrl}
                  initialPositions={fieldPositions ?? []}
                  onAutosave={handleSaveFieldPositions}
                  customFields={customFieldsForPicker}
                  onCreateCustomField={handleCreateCustomField}
                  orgPresets={orgPresetsForEditor}
                  onSavePreset={handleSavePreset}
                  contactTags={editorContactTags}
                  firmContactCustomFields={firmContactCustomFieldOptions}
                  classifyFieldSource={classifySource}
                  fieldConditions={fieldConditions}
                  onFieldConditionChange={handleFieldConditionChange}
                  templateFields={fields ?? []}
                  sampleFileName={`${name} (Sample).pdf`}
                  templateId={templateId}
                />
              </div>
            )}

            {/* Legacy PDF field mappings tab */}
            {activeTab === "mappings" && pdfFieldMappings && (
              <PdfFieldMappingsEditor
                templateId={templateId}
                mappings={pdfFieldMappings}
              />
            )}

            {/* HTML preview (for AI-extracted firm letters) */}
            {activeTab === "form" && bodyHtml && (
              <div className="bg-gray-50 p-6 dark:bg-secondary/20">
                <div className="mb-4 rounded-lg border border-border/50 bg-secondary/20 px-5 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <SourceLegend className="min-w-0 flex-1" />
                    {/* Sample-data toggle (MOR-923) — chips stay the default. */}
                    <div className="flex shrink-0 items-center gap-3">
                      <label className="flex cursor-pointer items-center gap-2 text-xs text-primary/60">
                        <Switch
                          checked={showSampleData}
                          onCheckedChange={setShowSampleData}
                        />
                        Preview with sample data
                      </label>
                    </div>
                  </div>
                  {referencedSlugs.size > 0 && (
                    <div className="mt-2.5 border-t border-border/40 pt-2.5">
                      <VariantPicker
                        groups={variantGroupsForEditor}
                        templateHtml={bodyHtml}
                        jobSelections={effectiveVariantSelections}
                        onChange={(groupSlug, optionValue) =>
                          setPreviewVariantSelections((prev) => {
                            const next = { ...prev, [groupSlug]: optionValue };
                            schedulePersistPreviewSelections({
                              variantSelections: next,
                            });
                            return next;
                          })
                        }
                        snippets={variantSnippetsForEditor}
                        snippetSelections={previewSnippetSelections}
                        onSnippetChange={(groupSlug, snippetId) =>
                          setPreviewSnippetSelections((prev) => {
                            const next = { ...prev, [groupSlug]: snippetId };
                            schedulePersistPreviewSelections({
                              snippetSelections: next,
                            });
                            return next;
                          })
                        }
                        dense
                      />
                    </div>
                  )}
                </div>
                <PaginatedLetterPreview
                  pageCardClassName="mx-auto shadow-lg"
                  pageCardStyle={{
                    fontFamily: defaultFontFamily || "Arial, sans-serif",
                    fontSize: defaultFontSize || "11pt",
                    lineHeight: defaultLineHeight || 1.6,
                    color: bodyTextColor || "#000",
                  }}
                  isThirdPartyForm={isThirdPartyForm}
                  parts={previewParts}
                  docPadding={docPadding}
                  PageRuleBadgeComponent={DocPageRuleBadge}
                  renderPageActions={
                    isThirdPartyForm || !templateId
                      ? undefined
                      : (page) => (
                          <PageMarginsPopover
                            pageIndex={page.pageIndex}
                            inherited={resolvePagePadding(
                              {
                                ...pagePaddingInputs(previewParts),
                                templatePageOverrides: (
                                  previewParts.templatePageOverrides ?? []
                                ).filter((e) => e.page !== page.pageIndex),
                              },
                              page.pageIndex,
                            )}
                            current={
                              (previewParts.templatePageOverrides ?? []).find(
                                (e) => e.page === page.pageIndex,
                              )?.padding ?? null
                            }
                            canEdit={canEditSettings}
                            lockedZones={zoneLocksForPage(
                              getVariantsForParts(previewParts),
                              page.pageIndex,
                            )}
                            onSave={(padding) =>
                              setTemplatePageMarginOverride({
                                templateId,
                                page: page.pageIndex,
                                padding,
                              })
                            }
                          />
                        )
                  }
                  bodyHtml={sampleBodyHtml ?? annotatedBodyHtml ?? ""}
                  // `annotatedBodyHtml` has already been DOMPurify-sanitised
                  // (see `annotatedBodyHtml` memo above) — skip the second
                  // sanitise pass so the field-source `data-field-source`
                  // attributes survive. `sampleBodyHtml` derives from it by
                  // replacing chip elements with text, so it stays sanitised.
                  bodyAlreadySanitised
                />
              </div>
            )}

            {/* Source document fallback when no HTML body */}
            {activeTab === "form" && !bodyHtml && pdfUrl && (
              <SourceDocPreview
                url={pdfUrl}
                fileName={sourceDocumentName}
                title={`${name} Preview`}
              />
            )}

            {activeTab === "gallagher" && templateId && (
              <GallagherFieldConfigEditor
                templateId={templateId}
                initialConfig={fieldConfig}
              />
            )}

            {/* Attachment settings */}
            {activeTab === "attachments" && templateId && (
              <AttachmentSettingsEditor
                templateId={templateId}
                orgId={orgId}
                templateKey={templateKey}
                initialAttachments={attachments}
              />
            )}

            {/* Settings tab — name, category, tags, and custom tag groups */}
            {activeTab === "settings" && (
              <SettingsTabContent
                templateId={templateId}
                name={name}
                categoryId={categoryId}
                orgId={orgId}
                isThirdPartyForm={isThirdPartyForm}
                letterheadOverride={letterheadOverride ?? null}
                suppressPageNumbering={suppressPageNumbering ?? false}
                optimisticTags={optimisticTags}
                handleTagsChange={handleTagsChange}
              />
            )}

            {/* No preview fallback */}
            {activeTab === "form" && !bodyHtml && !pdfUrl && (
              <div className="flex items-center justify-center py-20">
                <p className="text-sm text-primary/40">No preview available</p>
              </div>
            )}
          </div>

          {/* Right-hand notes sidebar — toggleable from the tab strip. */}
          {notesOpen && templateId && (
            <aside className="flex w-[380px] shrink-0 flex-col border-l border-border bg-muted/10">
              <TemplateNotesFeed
                templateId={templateId}
                currentUserId={currentUserId}
                onClose={() => setNotesOpen(false)}
              />
            </aside>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Settings Tab Content — filter bar + sectioned settings
// ---------------------------------------------------------------------------

type SettingsSection = "all" | "general" | "tags" | "contacts" | "custom";

const SETTINGS_SECTIONS: Array<{ key: SettingsSection; label: string }> = [
  { key: "all", label: "All" },
  { key: "general", label: "General" },
  { key: "tags", label: "Appointment Type & Status" },
  { key: "contacts", label: "Contacts" },
  { key: "custom", label: "Custom Tags" },
];

function SettingsTabContent({
  templateId,
  name,
  categoryId,
  orgId,
  isThirdPartyForm,
  letterheadOverride,
  suppressPageNumbering,
  optimisticTags,
  handleTagsChange,
}: {
  templateId: Id<"precedentTemplates"> | undefined;
  name: string;
  categoryId?: Id<"templateCategories">;
  orgId: Id<"organizations">;
  isThirdPartyForm?: boolean;
  letterheadOverride:
    | { kind: "none" }
    | { kind: "letterhead"; bundleName: string }
    | { kind: "variant"; variantId: Id<"letterheadVariants"> }
    | null;
  suppressPageNumbering: boolean;
  optimisticTags: string[];
  handleTagsChange: (update: string[] | ((prev: string[]) => string[])) => void;
}) {
  const [section, setSection] = useState<SettingsSection>("all");

  const show = (s: SettingsSection) => section === "all" || section === s;

  return (
    <div className="p-6">
      <div className="mx-auto max-w-xl space-y-6">
        {/* Section filter bar */}
        <div className="flex flex-wrap gap-1.5">
          {SETTINGS_SECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium transition",
                section === s.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-primary/50 hover:bg-muted/80 hover:text-primary/70",
              )}
              onClick={() => setSection(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* General: Name, Category, Letterhead */}
        {show("general") && (
          <>
            {templateId && (
              <TemplateNameEditor templateId={templateId} name={name} />
            )}

            {templateId && (
              <CategoryReassign
                templateId={templateId}
                categoryId={categoryId}
                orgId={orgId}
              />
            )}

            {templateId && !isThirdPartyForm && (
              <TemplateLetterheadOverrideEditor
                templateId={templateId}
                orgId={orgId}
                currentOverride={letterheadOverride}
              />
            )}

            {templateId && !isThirdPartyForm && (
              <TemplatePageNumberingEditor
                templateId={templateId}
                orgId={orgId}
                suppressed={suppressPageNumbering}
              />
            )}
          </>
        )}

        {/* Tags: Appointment Type + Job Status */}
        {show("tags") && (
          <>
            <div>
              <h4 className="text-sm font-medium text-primary/80">
                Appointment Type Tags
              </h4>
              <p className="mt-1 text-xs text-primary/40">
                Control when this template is automatically suggested during
                document generation. Templates tagged with a job type will be
                pre-selected when generating documents for matching jobs.
              </p>
              <div className="mt-4">
                <SettingsTagEditor
                  currentTags={optimisticTags}
                  onTagsChange={handleTagsChange}
                />
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-primary/80">
                Job Status Tags
              </h4>
              <p className="mt-1 text-xs text-primary/40">
                Optional: select a job status to auto-select template when
                generating the document on a job with matching status.
              </p>
              <div className="mt-4">
                <JobStatusTagEditor
                  currentTags={optimisticTags}
                  onTagsChange={handleTagsChange}
                />
              </div>
            </div>
          </>
        )}

        {/* Contacts: Job Contacts + Firm Contacts */}
        {show("contacts") && (
          <div>
            <h4 className="text-sm font-medium text-primary/80">
              Contact Roles
            </h4>
            <p className="mt-1 text-xs text-primary/40">
              Optional. When set, this template generates one document per
              matching contact (e.g. tagging "Directors" produces a personalised
              letter for every director). Each tagged role also unlocks its Job
              Contact / Firm Contact fields in the editor's Insert Field menu.
            </p>

            <div className="mt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-primary/45">
                Job Contacts
              </p>
              <p className="mb-2 text-xs text-primary/40">
                Per-job contacts detected from the investigation (creditors,
                directors, secured parties, etc.).
              </p>
              <ContactRoleTagEditor
                currentTags={optimisticTags}
                onTagsChange={handleTagsChange}
                orgId={orgId}
              />
            </div>

            <div className="mt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-primary/45">
                Firm Contacts
              </p>
              <p className="mb-2 text-xs text-primary/40">
                Org-wide directory contacts (banks, regulators, professionals,
                firm members).
              </p>
              <FirmContactTagEditor
                currentTags={optimisticTags}
                onTagsChange={handleTagsChange}
                orgId={orgId}
              />
            </div>
          </div>
        )}

        {/* Custom Tag Groups */}
        {show("custom") && (
          <CustomTagGroupEditor
            currentTags={optimisticTags}
            onTagsChange={handleTagsChange}
            orgId={orgId}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attachment Settings Editor
// ---------------------------------------------------------------------------

const GALLAGHER_SOURCE_TYPE_OPTIONS: Array<{
  value: GallagherFieldSourceType;
  label: string;
}> = [
  { value: "canonical", label: "Field source" },
  { value: "static", label: "Static text" },
  { value: "blank", label: "Blank" },
];

function buildGallagherConfigRows(
  initialConfig: GallagherFieldConfig[] | undefined,
): GallagherFieldConfig[] {
  const byField = new Map<string, GallagherFieldConfig>();
  const sourceKeys = new Set(
    GALLAGHER_SOURCE_FIELD_OPTIONS.map((option) => option.key),
  );
  for (const entry of DEFAULT_GALLAGHER_FIELD_CONFIG) {
    byField.set(entry.fieldKey, entry);
  }
  for (const saved of initialConfig ?? []) {
    // MOR-2342 — rows still mirroring a pre-fix default source are shown (and
    // re-saved) on the current default, matching what generation resolves.
    const entry = upgradeLegacyGallagherSource(saved);
    if (
      entry.sourceType === "canonical" &&
      (!entry.sourceKey || !sourceKeys.has(entry.sourceKey))
    ) {
      continue;
    }
    byField.set(entry.fieldKey, entry);
  }

  return GALLAGHER_INSURANCE_FIELDS.map((field) => ({
    fieldKey: field.key,
    sourceType: "canonical" as const,
    sourceKey: field.canonicalKey ?? field.key,
    ...byField.get(field.key),
  }));
}

function GallagherFieldConfigEditor({
  templateId,
  initialConfig,
}: {
  templateId: Id<"precedentTemplates">;
  initialConfig?: GallagherFieldConfig[];
}) {
  const [localConfig, setLocalConfig] = useState<GallagherFieldConfig[]>(() =>
    buildGallagherConfigRows(initialConfig),
  );
  const [saving, setSaving] = useState(false);
  const { mutateAsync: updateGallagherFieldConfig } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.updateGallagherFieldConfig,
    ),
  });

  useEffect(() => {
    setLocalConfig(buildGallagherConfigRows(initialConfig));
  }, [initialConfig]);

  const updateRow = useCallback(
    (fieldKey: string, patch: Partial<GallagherFieldConfig>) => {
      setLocalConfig((prev) =>
        prev.map((entry) =>
          entry.fieldKey === fieldKey
            ? { ...entry, ...patch, fieldKey }
            : entry,
        ),
      );
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateGallagherFieldConfig({
        templateId,
        fieldConfig: localConfig.map((entry) => ({
          fieldKey: entry.fieldKey,
          sourceType: entry.sourceType,
          sourceKey: entry.sourceKey,
          defaultValue: entry.defaultValue,
          hidden: entry.hidden,
        })),
      });
      toast.success("Gallagher portal fields updated");
    } catch (err) {
      toast.error(
        extractErrorMessage(err, "Failed to update Gallagher fields"),
      );
    } finally {
      setSaving(false);
    }
  }, [localConfig, templateId, updateGallagherFieldConfig]);

  return (
    <div className="p-6">
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="rounded-lg border border-border/60 bg-secondary/20 p-4">
          <h4 className="text-sm font-semibold text-primary/80">
            Gallagher Portal Fields
          </h4>
          <p className="mt-1 text-xs leading-relaxed text-primary/50">
            Configure how each Morgan value maps into the AJG Gallagher portal.
            These settings set defaults for newly generated notifications;
            analysts can still edit values during generation.
          </p>
        </div>

        <div className="overflow-hidden rounded-lg border border-border">
          <div className="grid grid-cols-[minmax(180px,1.2fr)_90px_150px_minmax(180px,1fr)_minmax(180px,1fr)] gap-3 border-b border-border bg-muted/30 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-primary/45">
            <span>Gallagher Field</span>
            <span>Visible</span>
            <span>Source</span>
            <span>Source Field</span>
            <span>Static / Default</span>
          </div>

          <div className="divide-y divide-border">
            {GALLAGHER_INSURANCE_FIELDS.map((field) => {
              const row = localConfig.find(
                (entry) => entry.fieldKey === field.key,
              ) ?? {
                fieldKey: field.key,
                sourceType: "canonical" as const,
                sourceKey: field.canonicalKey ?? field.key,
              };
              return (
                <div
                  key={field.key}
                  className={cn(
                    "grid grid-cols-[minmax(180px,1.2fr)_90px_150px_minmax(180px,1fr)_minmax(180px,1fr)] gap-3 px-4 py-3",
                    row.hidden && "bg-muted/20 opacity-60",
                  )}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-primary/75">
                      {field.label}
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-primary/35">
                      {field.key}
                    </div>
                    {field.description && (
                      <div className="mt-1 text-xs text-primary/40">
                        {field.description}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center">
                    <Switch
                      checked={!row.hidden}
                      onCheckedChange={(checked) =>
                        updateRow(field.key, { hidden: !checked })
                      }
                      aria-label={`Toggle ${field.label}`}
                    />
                  </div>

                  <Select
                    value={row.sourceType}
                    onValueChange={(value) =>
                      updateRow(field.key, {
                        sourceType: value as GallagherFieldSourceType,
                        sourceKey:
                          value === "canonical"
                            ? (row.sourceKey ?? field.canonicalKey)
                            : undefined,
                        defaultValue:
                          value === "static" ? row.defaultValue : undefined,
                      })
                    }
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GALLAGHER_SOURCE_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={row.sourceKey ?? "__none"}
                    disabled={row.sourceType !== "canonical"}
                    onValueChange={(value) =>
                      updateRow(field.key, {
                        sourceKey: value === "__none" ? undefined : value,
                      })
                    }
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Choose field" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">No source</SelectItem>
                      {GALLAGHER_SOURCE_FIELD_OPTIONS.map((option) => (
                        <SelectItem key={option.key} value={option.key}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    value={row.defaultValue ?? ""}
                    disabled={row.sourceType !== "static"}
                    onChange={(event) =>
                      updateRow(field.key, { defaultValue: event.target.value })
                    }
                    placeholder={
                      row.sourceType === "static"
                        ? "Default text"
                        : "Only for static text"
                    }
                    className="h-9 text-xs"
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setLocalConfig(buildGallagherConfigRows(initialConfig))
            }
            disabled={saving}
          >
            Reset
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
            ) : null}
            Save Portal Fields
          </Button>
        </div>
      </div>
    </div>
  );
}

const ATTACHMENT_KEY_OPTIONS: Array<{
  value: PrecedentAttachmentKey;
  label: string;
}> = [
  { value: "notice_of_appointment", label: "Notice of Appointment" },
  { value: "custom", label: "Custom Attachment" },
];

const ATTACHMENT_SOURCE_OPTIONS: Array<{
  value: PrecedentAttachmentSourceKind;
  label: string;
  description: string;
}> = [
  {
    value: "job_document",
    label: "Resolve from job documents",
    description:
      "Pick an uploaded job file at generation time (e.g. the NOA, court order, or a named form such as Form 505).",
  },
  {
    value: "template_default",
    label: "Template default file",
    description:
      "Use the same reusable file every time this template is generated.",
  },
  {
    value: "upload",
    label: "Ask user to upload during generation",
    description:
      "The practitioner must provide or replace the file in the generation wizard.",
  },
  {
    value: "generated_precedent",
    label: "Generated precedent",
    description:
      "Use another document generated for this job as the attachment.",
  },
  {
    value: "none",
    label: "No default",
    description:
      "Do not preselect a file; leave the attachment unresolved until generation.",
  },
];

type AttachmentTemplateOptionSource = {
  templateKey: string;
  displayName?: string;
  name?: string;
};

/**
 * Searchable combobox for picking a generated precedent template to attach.
 * Uses Popover + Command pattern (same as CanonicalKeyPicker) so users can
 * quickly filter through hundreds of templates by typing.
 */
function GeneratedPrecedentPicker({
  value,
  options,
  selectedLabel,
  onChange,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  selectedLabel?: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const dialogContainer =
    triggerRef.current?.closest<HTMLElement>("[role='dialog']");

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="mt-2 w-full min-w-0 justify-between overflow-hidden text-sm font-normal"
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {selectedLabel ?? "Select generated document"}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
        container={dialogContainer}
      >
        <Command onKeyDown={(e) => e.stopPropagation()}>
          <CommandInput placeholder="Search templates..." />
          <CommandList className="max-h-64 overscroll-contain">
            <CommandEmpty>No matching template.</CommandEmpty>
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={option.label}
                onSelect={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-3 w-3 shrink-0",
                    value === option.value ? "opacity-100" : "opacity-0",
                  )}
                />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Name picker for `custom` (reusable) attachments.
 *
 * Custom attachments are shared across templates by their name/slug, so a typo
 * silently mints a new, unlinked reference. This combobox lets the user pick an
 * existing reference (used elsewhere in the org) or deliberately create a new
 * one from the typed text.
 */
function CustomAttachmentNamePicker({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<{ customRef: string; name: string; usedByCount: number }>;
  onChange: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogContainer =
    triggerRef.current?.closest<HTMLElement>("[role='dialog']");

  const trimmed = search.trim();
  const exactMatch = options.some(
    (o) => o.name.toLowerCase() === trimmed.toLowerCase(),
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
      modal
    >
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full min-w-0 justify-between overflow-hidden text-sm font-normal"
        >
          {/* `min-w-0 flex-1`: without it the label's `whitespace-nowrap`
              min-content keeps the flex item at full width, so the name runs
              under (and past) the chevron instead of ellipsising. */}
          <span className="min-w-0 flex-1 truncate text-left">
            {value || "Select or create a reference"}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
        container={dialogContainer}
      >
        <Command onKeyDown={(e) => e.stopPropagation()}>
          <CommandInput
            placeholder="Search or type a new name..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-64 overscroll-contain">
            {options.length === 0 && !trimmed && (
              <CommandEmpty>Type a name to create a reference.</CommandEmpty>
            )}
            {trimmed && !exactMatch && (
              <CommandItem
                value={`__create__${trimmed}`}
                onSelect={() => {
                  onChange(trimmed);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <Plus className="mr-2 h-3 w-3 shrink-0" />
                <span className="truncate">Create &quot;{trimmed}&quot;</span>
              </CommandItem>
            )}
            {options.map((option) => (
              <CommandItem
                key={option.customRef}
                value={option.name}
                onSelect={() => {
                  onChange(option.name);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-3 w-3 shrink-0",
                    value.trim().toLowerCase() === option.name.toLowerCase()
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <span className="min-w-0 flex-1 truncate">{option.name}</span>
                <span className="shrink-0 pl-2 text-[10px] text-primary/40">
                  {option.usedByCount}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Text input that buffers keystrokes locally and only commits on blur.
 *
 * The attachment editor autosaves on every change to `localAttachments`,
 * which round-trips through a reactive Convex query and resyncs local
 * state. Committing per keystroke races that resync — characters typed
 * while a save is in-flight get clobbered by the stale server snapshot.
 * Committing on blur keeps the write a discrete event (matching the
 * autosave design) and the focused draft ignores incoming prop updates.
 */
function CommitOnBlurInput({
  value,
  placeholder,
  onCommit,
}: {
  value: string;
  placeholder?: string;
  onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(value);
  }, [value]);

  return (
    <Input
      value={draft}
      placeholder={placeholder}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        focusedRef.current = false;
        if (draft !== value) onCommit(draft);
      }}
    />
  );
}

function AttachmentSettingsEditor({
  templateId,
  initialAttachments,
  orgId,
  templateKey,
}: {
  templateId: Id<"precedentTemplates">;
  initialAttachments: AttachmentRequirement[];
  orgId: Id<"organizations">;
  templateKey: string;
}) {
  const [localAttachments, setLocalAttachments] =
    useState<AttachmentRequirement[]>(initialAttachments);
  const [uploadingDefaultIndex, setUploadingDefaultIndex] = useState<
    number | null
  >(null);
  const [previewingDefaultIndex, setPreviewingDefaultIndex] = useState<
    number | null
  >(null);
  const convex = useConvex();

  // Open the currently configured default file in a new tab so the user can
  // check they picked the right one before (or instead of) replacing it.
  const previewDefaultFile = useCallback(
    async (index: number, storageId: string) => {
      setPreviewingDefaultIndex(index);
      try {
        const url = await convex.query(
          api.settings.precedents.queries.getAttachmentDefaultFileUrl,
          {
            orgId,
            templateId,
            storageId: storageId as Id<"_storage">,
          },
        );
        if (!url) {
          toast.error("Default file not found in storage");
          return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to open default file"));
      } finally {
        setPreviewingDefaultIndex(null);
      }
    },
    [convex, orgId, templateId],
  );
  const { mutateAsync: updateAttachments } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.updateAttachments,
    ),
  });
  const generateUploadUrl = useConvexMutation(
    api.settings.precedents.mutations.generateUploadUrl,
  );
  const { data: allTemplates } = useQuery({
    ...convexQuery(api.settings.precedents.queries.listByCategory, { orgId }),
    enabled: !!orgId,
  });
  const { data: customReferenceOptions } = useQuery({
    ...convexQuery(
      api.settings.precedents.queries.listCustomAttachmentReferences,
      { orgId },
    ),
    enabled: !!orgId,
  });
  const pendingDefaultUploadIndexRef = useRef<number | null>(null);

  const attachmentsRef = useRef(localAttachments);
  useEffect(() => {
    attachmentsRef.current = localAttachments;
  }, [localAttachments]);

  const autosave = useAutosave<AttachmentRequirement[]>({
    getValue: () => attachmentsRef.current,
    initialValue: initialAttachments,
    save: async (next) => {
      // Incomplete rows (still on their empty-string select placeholders)
      // stay local — the mutation's validators reject them (MOR-553).
      await updateAttachments({
        templateId,
        attachments: prepareAttachmentRequirementsForSave(next).map(
          (attachment) => ({
            name: attachment.name,
            description: attachment.description,
            required: attachment.required,
            attachmentKey: attachment.attachmentKey,
            customRef: attachment.customRef,
            sourceKind: attachment.sourceKind,
            documentCategory: attachment.documentCategory as
              DocumentCategory | undefined,
            acceptedFileTypes: attachment.acceptedFileTypes,
            storageId: attachment.storageId as Id<"_storage"> | undefined,
            fileName: attachment.fileName,
            fileType: attachment.fileType,
            fileSize: attachment.fileSize,
            generatedTemplateKey: attachment.generatedTemplateKey,
          }),
        ),
      });
    },
  });

  // Mirror the same commit pattern as PdfFieldEditor: every mutation to
  // localAttachments comes from a discrete event (input blur, select
  // change, drag stop), so reacting to the array reference is the right
  // place to flush. We omit `autosave` from deps since the hook reads
  // values via getValue and a ref keeps the latest flush available.
  const autosaveFlushRef = useRef(autosave.flush);
  useEffect(() => {
    autosaveFlushRef.current = autosave.flush;
  }, [autosave.flush]);

  useEffect(() => {
    void autosaveFlushRef.current();
  }, [localAttachments]);

  useEffect(() => {
    return () => {
      void autosaveFlushRef.current();
    };
  }, []);

  const { startUpload: startDefaultAttachmentUpload } = useUploadFiles(
    generateUploadUrl,
    {
      onUploadBegin: () => {
        setUploadingDefaultIndex(pendingDefaultUploadIndexRef.current);
      },
      onUploadComplete: async (uploaded) => {
        const index = pendingDefaultUploadIndexRef.current;
        const file = uploaded[0];
        if (index === null || !file) return;
        const storageId = (file.response as { storageId: Id<"_storage"> })
          .storageId;
        updateRow(index, {
          sourceKind: "template_default",
          storageId,
          fileName: file.name,
          fileType: file.type || "application/octet-stream",
          fileSize: file.size,
        });
        pendingDefaultUploadIndexRef.current = null;
        setUploadingDefaultIndex(null);
        toast.success(`${file.name} uploaded`);
      },
      onUploadError: () => {
        pendingDefaultUploadIndexRef.current = null;
        setUploadingDefaultIndex(null);
        toast.error("Failed to upload default attachment");
      },
    },
  );

  const generatedTemplateOptions = useMemo(
    () =>
      (allTemplates ?? [])
        .filter(
          (template: AttachmentTemplateOptionSource) =>
            template.templateKey !== templateKey,
        )
        .map((template: AttachmentTemplateOptionSource) => ({
          value: template.templateKey,
          label: template.displayName ?? template.name ?? template.templateKey,
        })),
    [allTemplates, templateKey],
  );

  useEffect(() => {
    setLocalAttachments(initialAttachments);
  }, [initialAttachments]);

  const updateRow = (index: number, next: Partial<AttachmentRequirement>) => {
    setLocalAttachments((prev) =>
      prev.map((attachment, i) => {
        if (i !== index) return attachment;
        const merged = { ...attachment, ...next };
        if (next.attachmentKey && !merged.name) {
          const typeLabel = ATTACHMENT_KEY_OPTIONS.find(
            (o) => o.value === next.attachmentKey,
          )?.label;
          if (typeLabel && next.attachmentKey !== "custom") {
            merged.name = typeLabel;
          }
        }
        if (next.name || next.attachmentKey) {
          const normalised = normaliseAttachmentRequirement({
            name: merged.name,
            description: merged.description,
            required: merged.required,
          });
          // `||` rather than `??`: freshly added rows hold empty-string
          // placeholders, which must not shadow the normalised inference.
          const attachmentKey =
            next.attachmentKey ||
            merged.attachmentKey ||
            normalised.attachmentKey;
          return {
            ...normalised,
            ...merged,
            attachmentKey,
            customRef:
              attachmentKey === "custom"
                ? slugifyAttachmentName(merged.name)
                : undefined,
            sourceKind:
              next.sourceKind || merged.sourceKind || normalised.sourceKind,
            documentCategory:
              merged.documentCategory ?? normalised.documentCategory,
            acceptedFileTypes:
              merged.acceptedFileTypes ?? normalised.acceptedFileTypes,
          };
        }
        return merged;
      }),
    );
  };

  const handleAdd = () => {
    setLocalAttachments((prev) => [
      ...prev,
      {
        name: "",
        required: false,
        attachmentKey: "" as PrecedentAttachmentKey,
        sourceKind: "" as PrecedentAttachmentSourceKind,
      },
    ]);
  };

  // Reorder: the template's attachment order IS the order the slots appear
  // in the wizard and the order the files merge into every generated
  // document, so setting it here applies to all future documents at once
  // (rather than reordering each generated document individually).
  const moveAttachment = (index: number, delta: -1 | 1) => {
    setLocalAttachments((prev) => {
      const to = index + delta;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  return (
    <div className="p-6">
      <div className="mx-auto max-w-2xl space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="flex items-center gap-2 text-sm font-semibold text-primary">
              <Paperclip className="size-4 text-primary/40" />
              Attachment Settings
            </h4>
            <p className="mt-1 text-xs text-primary/50">
              Manage attachments that apply to this precedent. Morgan
              automatically identifies attachments from the content of the
              letter and suggests attachment settings. Use the arrows to set the
              order attachments appear in generated documents. Changes are saved
              automatically.
            </p>
          </div>
          <AutosaveStatus
            status={autosave.status}
            lastSavedAt={autosave.lastSavedAt}
            onRetry={autosave.retry}
            className="mt-1 shrink-0"
          />
        </div>

        {localAttachments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-primary/50">
            No attachments configured for this precedent.
          </div>
        ) : (
          <div className="space-y-3">
            {localAttachments.map((attachment, index) => {
              const selectedSource = ATTACHMENT_SOURCE_OPTIONS.find(
                (option) => option.value === attachment.sourceKind,
              );
              const selectedType = ATTACHMENT_KEY_OPTIONS.find(
                (option) => option.value === attachment.attachmentKey,
              );
              const selectedGeneratedTemplate = generatedTemplateOptions.find(
                (option) => option.value === attachment.generatedTemplateKey,
              );

              return (
                <ReorderableAttachmentSettingsRow
                  key={`${attachment.attachmentKey}-${index}`}
                  onMoveUp={
                    index > 0 ? () => moveAttachment(index, -1) : undefined
                  }
                  onMoveDown={
                    index < localAttachments.length - 1
                      ? () => moveAttachment(index, 1)
                      : undefined
                  }
                >
                  <div className="space-y-1.5">
                    <Label>Attachment Source</Label>
                    <Select
                      value={attachment.sourceKind || undefined}
                      onValueChange={(value) =>
                        updateRow(index, {
                          sourceKind: value as PrecedentAttachmentSourceKind,
                        })
                      }
                    >
                      <SelectTrigger>
                        <span
                          className={cn(
                            "truncate text-left",
                            !selectedSource && "text-muted-foreground",
                          )}
                        >
                          {selectedSource?.label ??
                            "Select source of attachment"}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        {ATTACHMENT_SOURCE_OPTIONS.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            textValue={option.label}
                          >
                            <div className="flex flex-col gap-0.5 py-1">
                              <span>{option.label}</span>
                              <span className="text-xs text-primary/45">
                                {option.description}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-primary/50">
                      {selectedSource?.description ??
                        "Select the source to generate the attachment (e.g. resolve from Job documents)."}
                    </p>
                  </div>
                  {attachment.sourceKind === "job_document" && (
                    <>
                      {/* `minmax(0,1fr)` + `min-w-0`: a plain `1fr` track takes
                        its automatic minimum from the content, and the Name
                        combobox's trigger label is `whitespace-nowrap`, so a
                        long custom attachment name grew the column and pushed
                        the control out through the card's right edge. */}
                      <div className="mt-3 grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                        <div className="min-w-0 space-y-1.5">
                          <Label>Type</Label>
                          <Select
                            value={attachment.attachmentKey || undefined}
                            onValueChange={(value) =>
                              updateRow(index, {
                                attachmentKey: value as PrecedentAttachmentKey,
                              })
                            }
                          >
                            <SelectTrigger>
                              <span
                                className={cn(
                                  "truncate text-left",
                                  !selectedType && "text-muted-foreground",
                                )}
                              >
                                {selectedType?.label ?? "Select type…"}
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              {ATTACHMENT_KEY_OPTIONS.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="min-w-0 space-y-1.5">
                          <Label>Name</Label>
                          {attachment.attachmentKey === "custom" ? (
                            <CustomAttachmentNamePicker
                              value={attachment.name}
                              options={customReferenceOptions ?? []}
                              onChange={(value) =>
                                updateRow(index, { name: value })
                              }
                            />
                          ) : (
                            <CommitOnBlurInput
                              value={attachment.name}
                              placeholder="Attachment name"
                              onCommit={(value) =>
                                updateRow(index, { name: value })
                              }
                            />
                          )}
                        </div>
                      </div>
                      <p className="mt-1.5 text-xs text-primary/50">
                        Select the type of attachment. If Custom Attachment is
                        selected, select or add name for Custom Attachment to
                        apply.
                      </p>
                      {attachment.attachmentKey === "custom" && (
                        <div className="mt-3 rounded-md border border-border/60 bg-secondary/25 p-3">
                          <p className="text-xs text-primary/60">
                            The Name of the attachment is a shared reference. If
                            you add the same custom attachment to other
                            precedents, all precedents will use the same
                            uploaded job document. Pick the file once per job
                            and the file will be added to every precedent that
                            uses this named custom attachment.
                          </p>
                          {slugifyAttachmentName(attachment.name) && (
                            <p className="mt-1.5 font-mono text-[11px] text-primary/45">
                              reference: custom:
                              {slugifyAttachmentName(attachment.name)}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {attachment.sourceKind === "template_default" && (
                    <div className="mt-3 rounded-md border border-border/60 bg-secondary/25 p-3">
                      <Label>Default File</Label>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <label className="inline-flex cursor-pointer items-center rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-primary/70 hover:bg-secondary">
                          {uploadingDefaultIndex === index ? (
                            <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                          ) : (
                            <Upload className="mr-1.5 size-3.5" />
                          )}
                          {attachment.storageId
                            ? "Replace default file"
                            : "Upload default file"}
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.dotx"
                            className="sr-only"
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (!file) return;
                              pendingDefaultUploadIndexRef.current = index;
                              startDefaultAttachmentUpload([file]);
                              event.currentTarget.value = "";
                            }}
                          />
                        </label>
                        {attachment.storageId && attachment.fileName ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (attachment.storageId) {
                                void previewDefaultFile(
                                  index,
                                  attachment.storageId,
                                );
                              }
                            }}
                            disabled={previewingDefaultIndex === index}
                            title="Preview default file"
                            className="inline-flex max-w-[280px] items-center gap-1 text-xs text-primary/70 underline decoration-primary/30 underline-offset-2 hover:text-primary disabled:opacity-60"
                          >
                            {previewingDefaultIndex === index ? (
                              <Loader2 className="size-3 shrink-0 animate-spin" />
                            ) : (
                              <Eye className="size-3 shrink-0" />
                            )}
                            <span className="truncate">
                              {attachment.fileName}
                            </span>
                          </button>
                        ) : (
                          <span className="text-xs text-primary/55">
                            {attachment.fileName ??
                              "No default file uploaded yet."}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {attachment.sourceKind === "generated_precedent" && (
                    <div className="mt-3 rounded-md border border-border/60 bg-secondary/25 p-3">
                      <Label>Generated Precedent To Attach</Label>
                      <GeneratedPrecedentPicker
                        value={attachment.generatedTemplateKey ?? ""}
                        options={generatedTemplateOptions}
                        selectedLabel={selectedGeneratedTemplate?.label}
                        onChange={(value) =>
                          updateRow(index, { generatedTemplateKey: value })
                        }
                      />
                      <p className="mt-1 text-xs text-primary/50">
                        The selected document must be generated for the job
                        before it can be exported as an attachment.
                      </p>
                    </div>
                  )}
                  <div className="mt-3 space-y-1.5">
                    <Label>Description</Label>
                    <CommitOnBlurInput
                      value={attachment.description ?? ""}
                      placeholder="Shown to users during generation"
                      onCommit={(value) =>
                        updateRow(index, { description: value })
                      }
                    />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setLocalAttachments((prev) =>
                          prev.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <Trash2 className="mr-1 size-3.5" />
                      Remove
                    </Button>
                  </div>
                </ReorderableAttachmentSettingsRow>
              );
            })}
          </div>
        )}

        <div>
          <Button type="button" variant="outline" onClick={handleAdd}>
            <Plus className="mr-1.5 size-3.5" />
            Add Attachment
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Wrapper for one attachment-settings card with up/down reorder buttons. The
 * arranged order autosaves as the template's attachments array, which drives
 * the slot order in the generate wizard and the merge order of the files in
 * every generated document. A missing handler (first/last row) renders that
 * button disabled.
 */
function ReorderableAttachmentSettingsRow({
  onMoveUp,
  onMoveDown,
  children,
}: {
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  children: ReactNode;
}) {
  const arrowClass = (enabled: boolean) =>
    cn(
      "rounded p-0.5",
      enabled
        ? "text-primary/40 hover:bg-secondary hover:text-primary/70"
        : "cursor-default text-primary/15",
    );
  return (
    <div className="flex items-start gap-2 rounded-lg border border-border bg-card p-4">
      <div className="mt-1 flex shrink-0 flex-col">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!onMoveUp}
          className={arrowClass(!!onMoveUp)}
          aria-label="Move attachment up"
          title="Move up"
        >
          <ChevronUp className="size-4" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!onMoveDown}
          className={arrowClass(!!onMoveDown)}
          aria-label="Move attachment down"
          title="Move down"
        >
          <ChevronDown className="size-4" />
        </button>
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF Field Mappings Editor — review and edit auto-mapped canonical keys
// ---------------------------------------------------------------------------

function PdfFieldMappingsEditor({
  templateId,
  mappings,
}: {
  templateId?: Id<"precedentTemplates">;
  mappings: Array<{
    pdfFieldName: string;
    canonicalKey?: string;
    fieldType: string;
    checkboxValue?: string;
  }>;
}) {
  const [localMappings, setLocalMappings] = useState(mappings);
  const [saving, setSaving] = useState(false);
  const { mutateAsync: updateMappings } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.updatePdfFieldMappings,
    ),
  });

  const handleCanonicalKeyChange = (index: number, value: string) => {
    setLocalMappings((prev) =>
      prev.map((m, i) =>
        i === index ? { ...m, canonicalKey: value || undefined } : m,
      ),
    );
  };

  const handleCheckboxValueChange = (index: number, value: string) => {
    setLocalMappings((prev) =>
      prev.map((m, i) =>
        i === index ? { ...m, checkboxValue: value || undefined } : m,
      ),
    );
  };

  const handleSave = async () => {
    if (!templateId) return;
    setSaving(true);
    try {
      await updateMappings({ templateId, pdfFieldMappings: localMappings });
      toast.success("Field mappings saved");
    } catch {
      toast.error("Failed to save mappings");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(localMappings) !== JSON.stringify(mappings);

  return (
    <div className="p-6">
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-primary/80">
              PDF Field Mappings
            </h3>
            <p className="text-xs text-primary/40">
              Each PDF form field is mapped to a canonical key for auto-filling.
            </p>
          </div>
          {templateId && (
            <button
              type="button"
              disabled={saving || !hasChanges}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-medium transition",
                hasChanges
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "cursor-not-allowed bg-primary/10 text-primary/40",
              )}
              onClick={handleSave}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
        </div>
        <div className="space-y-1.5">
          {localMappings.map((mapping, index) => (
            <div
              key={mapping.pdfFieldName}
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5"
            >
              <div className="min-w-0 flex-1">
                <span className="block truncate text-xs font-medium text-primary/80">
                  {mapping.pdfFieldName}
                </span>
                <span className="text-[10px] text-primary/30">
                  {mapping.fieldType}
                </span>
              </div>
              <span className="text-[10px] text-primary/30">→</span>
              <input
                type="text"
                value={mapping.canonicalKey ?? ""}
                onChange={(e) =>
                  handleCanonicalKeyChange(index, e.target.value)
                }
                placeholder="canonical_key"
                className="w-44 rounded border border-border bg-background px-2 py-1 font-mono text-xs text-primary/70 placeholder:text-primary/20 focus:border-primary/40 focus:outline-none"
              />
              {mapping.fieldType === "checkbox" && (
                <select
                  value={
                    mapping.checkboxValue === "unchecked"
                      ? "unchecked"
                      : "checked"
                  }
                  onChange={(e) =>
                    handleCheckboxValueChange(
                      index,
                      e.target.value === "unchecked" ? "unchecked" : "",
                    )
                  }
                  title="Which state of the linked field this box represents."
                  className="w-32 rounded border border-purple-300 bg-background px-2 py-1 text-xs text-purple-700 focus:border-purple-400 focus:outline-none dark:border-purple-800 dark:text-purple-300"
                >
                  <option value="checked">Tick if checked</option>
                  <option value="unchecked">Tick if unchecked</option>
                </select>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const DOC_PAGE_RULE_LABELS: Record<PageRule, string> = {
  all: "All pages",
  firstOnly: "First page only",
  notFirst: "All except first",
  oddOnly: "Odd pages",
  evenOnly: "Even pages",
  none: "None",
};

function DocPageRuleBadge({ rule }: { rule: PageRule }) {
  return (
    <span className="absolute right-2 top-1 rounded bg-primary/5 px-1.5 py-0.5 text-[9px] font-medium text-primary/40">
      {DOC_PAGE_RULE_LABELS[rule]}
    </span>
  );
}
